import "./style.css";
import "./ui/game-menu.css";
import "./ui/narrative-journal.css";
import { CampaignRepository } from "./core/persistence";
import { describeForwardMotionReason, forwardMotionLabel } from "./core/forward-motion";
import { createWorld } from "./core/simulation";
import type { ChampionInduction, WorldState } from "./core/types";
import { createCampaignLegacyState, legendQualificationLabel } from "./core/legends";
import { detectNarratorCapability } from "./narrator/capability";
import {
  localNarratorHostTokenMeter,
} from "./narrator/local-narrator-host";
import { createLocalNarratorAssetStore } from "./narrator/local-model-assets";
import { localNarratorRuntimeSourceUrls } from "./narrator/local-narrator-runtime-sources";
import { createLocalNarratorWorker } from "./narrator/local-narrator-worker-factory";
import { NarratorClient } from "./narrator/narrator-client";
import { createCreativeWriterClient, hasCachedCreativeWriterModel, removeCachedCreativeWriterModel } from "./narrator/creative-writer-client";
import { projectStoryBeatJobV1 } from "./narrator/story-beat";
import { createCreativeStoryController, type CreativeStorySnapshot } from "./ui/creative-story-controller";
import { projectCreativeStoryViewpoint } from "./ui/creative-story-viewpoint";
import { createCreativeStoryDirector, type HeldNarrative } from "./ui/creative-story-director";
import { createLastPresentedStory } from "./ui/last-presented-story";
import { createNarrativeJournal } from "./ui/narrative-journal";
import { selectNarrativeContinuity } from "./ui/narrative-continuity";
import { createNarrativeJournalView } from "./ui/narrative-journal-view";
import { projectFirstSharedVictoryChroniclePlate, projectTownChroniclePlate } from "./ui/chronicle-plate";
import { createChroniclePlateArchive } from "./ui/chronicle-plate-archive";
import { createChroniclePlateView } from "./ui/chronicle-plate-view";
import { projectDungeonSearchView } from "./ui/dungeon-search-view";
import { projectDungeonFieldMedicineScene } from "./ui/dungeon-field-medicine-view";
import { projectDungeonSecretPassageScene } from "./ui/dungeon-secret-passage-view";
import { projectDungeonGuardianScene } from "./ui/dungeon-guardian-view";
import { projectRoadSupperCombat, projectRoadSupperScene } from "./ui/road-supper-view";
import { projectSpareGearTradeScene } from "./ui/spare-gear-trade-view";
import { projectCombatAftermathScene } from "./ui/combat-aftermath";
import { projectCombatControlScene } from "./ui/combat-control-recap";
import { projectPennywiseGateScene } from "./ui/pennywise-gate-view";
import { projectSmithyJobScene } from "./ui/smithy-job-view";
import { projectInnBluffScene } from "./ui/inn-bluff-view";
import { createStatusHistoryView } from "./ui/status-history-view";
import { projectFarewellRemembrance } from "./ui/farewell-remembrance";
import { projectRecordedFarewell } from "./ui/recorded-farewell";
import { projectFirstSharedVictory } from "./ui/first-shared-victory";
import {
  effectiveStoryFocus, normalizeStorytellingPreferences, readStorytellingPreferences,
  storytellingCadenceMs, writeStorytellingPreferences,
} from "./ui/storytelling-preferences";
import { createNarrativeIntermission } from "./ui/narrative-intermission";
import { readPlayModePreference, writePlayModePreference, type PlayMode } from "./ui/play-mode-preferences";
import { adventureStepIntervalMs, normalizeAdventureSpeed, readAdventureSpeedPreference, writeAdventureSpeedPreference } from "./ui/adventure-speed";
import { normalizeDungeonPerspective, readDungeonPerspectivePreference, writeDungeonPerspectivePreference } from "./ui/dungeon-perspective-preference";
import { createPlayModeStartup } from "./ui/play-mode-startup";
import { projectSceneNarratorJob } from "./narrator/scene-packet";
import {
  projectFactualStoryBeatTransitionV2,
} from "./narrator/story-beat-v2";
import {
  projectFactualStoryBeatOpportunityV1,
  retainFactualStoryBeatOpportunityV1,
  type FactualStoryBeatOpportunityV1,
  type FactualStoryBeatOpportunityViewV1,
} from "./narrator/story-beat-opportunity";
import { companionActionDefinition, counterDuelHabitText, counterDuelPatternBreakText, counterDuelStanceLabel, counterDuelTellText, derivedStats, describeCompletedQuestReward, describeDungeonShrineUse, describeEncounterThreat, dungeonTrapCheckAttribute, dungeonTrapKindLabel, projectCombatRoster, projectCounterDuelHabit, projectDungeonKeyGate, projectDungeonLandmark, projectDungeonMoveKnowledge, projectDungeonTraps, projectDungeonWayfinding, projectLatestShrineUse, projectSuccessorQuestLead, questObjectiveRuleLabel } from "./depth";
import type { CombatRosterProjection, CombatRosterStatus, CombatState } from "./depth";
import { GameRenderer } from "./render/game-renderer";
import { projectGearAppearance, projectHeroIdentityAppearance } from "./render/hero-appearance";
import { projectLatestCombatCue, projectLatestCombatTurn } from "./render/combat-choreography";
import { formatCombatQuickReceipt } from "./render/combat-roster-layout";
import { projectMillraceReversal } from "./render/millrace-reversal";
import { projectCombatFamiliarWeaponForm, projectFamiliarWeaponForm } from "./render/weapon-form";
import type { FarewellCutawayPhase } from "./render/farewell-cutaway";
import type { BattleSpoilsCutawayPhase } from "./render/battle-spoils-cutaway";
import type { TownItineraryCutawayPhase } from "./render/town-itinerary-cutaway";
import type { FieldNoteCutawayPhase } from "./render/field-note-cutaway";
import type { HeroGrowthAllocationCutawayPhase } from "./render/hero-growth-allocation-cutaway";
import type { HeroLevelUpCutawayPhase } from "./render/hero-level-up-cutaway";
import { projectAbilityResonanceSourcePresentation, type AbilityResonanceCutawayPhase } from "./render/ability-resonance-cutaway";
import type { WeaponMemoryCutawayPhase } from "./render/weapon-memory-cutaway";
import {
  cutawayRepetitionFingerprint,
  cutawayRegistry,
  projectCutawayCandidates,
  validateCutawayAdapterManifest,
  type BattleSpoilsCutawayCandidate,
  type AbilityResonanceCutawayCandidate,
  type FarewellCutawayCandidate,
  type ElsewhereLoafCutawayCandidate,
  type FieldNoteResolutionCutawayCandidate,
  type FieldNoteResolutionLiveTellCutawayCandidate,
  type HeroGrowthAllocationCutawayCandidate,
  type HeroLevelUpChampionCutawayCandidate,
  type HeroLevelUpCutawayCandidate,
  type ProductionCutawayCandidate,
  type ProductionCutawayRecipeKey,
  type TrapCutawayCandidate,
  type TownItineraryCutawayCandidate,
  type WeaponMemoryCutawayCandidate,
} from "./render/cutaway-registry";
import {
  activeCutawayMaximumMs,
  cancelCutawayController,
  completeActiveCutaway,
  createCutawayController,
  discardPendingCutawayPresentation,
  isCutawayBusy,
  offerCommittedCutaway,
  type CutawayControllerState,
} from "./render/cutaway-controller";
import {
  createTrapCutawayFatigueMemory,
  selectTrapCutawayStaging,
  trapCutawayOutcome,
  type TrapCutawayFatigueMemory,
  type TrapCutawayPhase,
  type TrapCutawayStaging,
} from "./render/trap-cutaway";
import { describeTravelCorridor, projectTravelCorridor } from "./render/travel-corridor";
import { randomId } from "./random-id";
import { shouldRecoverRuntime } from "./runtime/liveness";
import {
  projectViewHero,
  type HeroInspectionActivity,
  type HeroInspectionView,
} from "./ui/hero-inspection-activity";
import { projectMiniMap, type MiniMapLine } from "./ui/mini-map";
import { projectAtlasPartyGlyphs, projectAtlasPartyMarker, projectAtlasPartySupportLink } from "./ui/atlas-party-marker";
import { isInjuredPartyStatus, projectParty } from "./ui/party-projection";
import { projectWatchParty } from "./ui/watch-party";
import { describeRoadcraftEffectiveness } from "./ui/roadcraft-effectiveness";
import type { CompanionFarewellPacket } from "./ui/companion-farewell";
import { projectCriticalRoadsideRecovery } from "./ui/critical-roadside-recovery";
import type { HeroLevelUpDerivedDelta } from "./ui/hero-level-up";
import type { HeroLevelUpPresentationPacket } from "./ui/hero-level-up-presentation";
import type { HeroGrowthAllocationPacketV1 } from "./ui/hero-growth-allocation";
import type { AbilityResonancePacketV1 } from "./ui/ability-resonance";
import { projectHeroGrowth } from "./ui/hero-growth";
import { projectHeroExperience } from "./ui/hero-progression";
import { projectHallOfChampions } from "./ui/hall-of-champions";
import type { TrapResolutionPacket } from "./ui/trap-resolution";
import type { WeaponMemoryCeremonyPacketV1 } from "./ui/weapon-memory";
import type { BattleSpoilsComparisonPacketV1 } from "./ui/battle-spoils";
import type { TownItineraryPacketV1 } from "./ui/town-itinerary";
import {
  fieldNoteEvidenceRelationship,
  fieldNotePublicTellClarityLabel,
  fieldNotePublicTellText,
  type FieldNoteResolutionPresentationPacket,
} from "./ui/field-note-resolution-presentation";
import { projectPatternBreakObserverReaction } from "./ui/pattern-break-observer-reaction";
import { projectCounterDuelPatternBreakSignature } from "./ui/pattern-break-signature";
import {
  createLocalNarratorUiController,
  type LocalNarratorControllerSnapshot,
} from "./ui/local-narrator-controller";
import {
  inspectionViews,
  projectCounterDuelSummary,
  projectCodexView,
  projectInventoryView,
  projectJournalView,
  projectMapView,
  projectSpellbookView,
  type InspectionView,
} from "./ui/view-projection";
import {
  beginSpectatorAbsence,
  createSpectatorInbox,
  markSpectatorInboxRead,
  observeSpectatorInbox,
} from "./ui/spectator-inbox";
import {
  compactStageFocusQuery,
  resolveStageChromeMode,
  stageFocusPreferenceKey,
  toggledStageChromeMode,
  type StageChromeMode,
} from "./ui/stage-focus";
import {
  copyStoryBeatTrailPlainText,
  createStoryBeatController,
  storyBeatFallbackPresentation,
  storyBeatLensLabel,
  storyBeatSourceIdentity,
  storyBeatWriteLabel,
  type StoryBeatUiSnapshot,
} from "./ui/story-beat-controller";
import {
  writeStoryBeatAndContinueAtStableScene,
  writeStoryBeatAtStableScene,
} from "./ui/story-beat-write";
import {
  AutomaticUpdateMonitor,
  isNewerVersion,
  updateIntervalMs,
} from "./update/automatic-update";
import { SimulationClient } from "./worker/simulation-client";
import { createAtlasGazetteerView } from "./ui/atlas-gazetteer-view";
import { createReparteeView, projectReparteeScene, type ReparteeSceneView } from "./ui/repartee-view";
import { createCompanionReunionRecord } from "./ui/companion-reunion-view";
import { createElsewhereLoafRecord, elsewhereLoafTitle } from "./ui/elsewhere-loaf-view";
import { createWeaponTechniqueRecord, projectWeaponTechniqueScene, weaponTechniqueEffectLabel } from "./ui/weapon-technique-view";
import { selectCompanionReunion } from "./depth/companion-reunion";
import { createCompanionCreditRecord } from "./ui/companion-credit-view";
import { selectCompanionCredit } from "./depth/companion-credit";
import { createBorrowedBellView, projectBorrowedBellScene, type BorrowedBellSceneView } from "./ui/borrowed-bell-view";
import { createReparteeDwell, reparteeDwellPending, updateReparteeDwell } from "./ui/repartee-dwell";

const fastMode = new URLSearchParams(window.location.search).has("fast");
const beatDurationMs = fastMode
  ? 250
  : 4_800;
let adventureSpeed = readAdventureSpeedPreference();
let adventureSpeedSaved = true;
let dungeonPerspective = readDungeonPerspectivePreference();
let dungeonPerspectiveSaved = true;
const checkpointPrefix = "the-grind-2:last-active:";
const updateAttemptKey = "the-grind-2:update-attempt";

interface UpdateAttempt {
  fromVersion: string;
  targetVersion: string;
  attemptedAt: number;
}

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) throw new Error(`Missing required element: ${selector}`);
  return element;
}

const elements = {
  app: requiredElement<HTMLElement>("#app"),
  stage: requiredElement<HTMLDivElement>("#stage"),
  topbar: requiredElement<HTMLElement>("#topbar"),
  topbarControls: requiredElement<HTMLElement>("#topbar-controls"),
  heroName: requiredElement<HTMLSpanElement>("#hero-name"),
  heroLevel: requiredElement<HTMLSpanElement>("#hero-level"),
  campaignSelect: requiredElement<HTMLSelectElement>("#campaign-select"),
  adventureSpeedSelect: requiredElement<HTMLSelectElement>("#adventure-speed-select"),
  adventureSpeedStatus: requiredElement<HTMLElement>("#adventure-speed-status"),
  dungeonPerspectiveSelect: requiredElement<HTMLSelectElement>("#dungeon-perspective-select"),
  dungeonPerspectiveStatus: requiredElement<HTMLElement>("#dungeon-perspective-status"),
  pauseButton: requiredElement<HTMLButtonElement>("#pause-button"),
  newButton: requiredElement<HTMLButtonElement>("#new-button"),
  narratorButton: requiredElement<HTMLButtonElement>("#narrator-button"),
  narratorDialog: requiredElement<HTMLDialogElement>("#narrator-dialog"),
  narratorClose: requiredElement<HTMLButtonElement>("#narrator-close"),
  gameMenu: requiredElement<HTMLDialogElement>("#game-menu"),
  gameMenuButton: requiredElement<HTMLButtonElement>("#game-menu-button"),
  stageMenuButton: requiredElement<HTMLButtonElement>("#stage-menu-button"),
  gameMenuClose: requiredElement<HTMLButtonElement>("#game-menu-close"),
  lastStoryButton: requiredElement<HTMLButtonElement>("#last-story-button"),
  playStartDialog: requiredElement<HTMLDialogElement>("#play-start-dialog"),
  playStartLlm: requiredElement<HTMLButtonElement>("#play-start-llm"),
  playStartDeterministic: requiredElement<HTMLButtonElement>("#play-start-deterministic"),
  playStartCache: requiredElement<HTMLElement>("#play-start-cache"),
  playModeSelect: requiredElement<HTMLSelectElement>("#play-mode-select"),
  playModeRetry: requiredElement<HTMLButtonElement>("#play-mode-retry"),
  playModeStatus: requiredElement<HTMLElement>("#play-mode-status"),
  narratorStatus: requiredElement<HTMLElement>("#narrator-status"),
  narratorDownloadProgress: requiredElement<HTMLProgressElement>("#narrator-download-progress"),
  narratorDownloadDetail: requiredElement<HTMLElement>("#narrator-download-detail"),
  narratorDownload: requiredElement<HTMLButtonElement>("#narrator-download"),
  narratorCancel: requiredElement<HTMLButtonElement>("#narrator-cancel"),
  narratorDisable: requiredElement<HTMLButtonElement>("#narrator-disable"),
  narratorRemove: requiredElement<HTMLButtonElement>("#narrator-remove"),
  creativeStatus: requiredElement<HTMLElement>("#creative-status"),
  creativeLoad: requiredElement<HTMLButtonElement>("#creative-load"),
  creativeStop: requiredElement<HTMLButtonElement>("#creative-stop"),
  creativeRemove: requiredElement<HTMLButtonElement>("#creative-remove"),
  creativeFocus: requiredElement<HTMLSelectElement>("#creative-story-focus"),
  creativeRhythm: requiredElement<HTMLSelectElement>("#creative-story-rhythm"),
  creativeDraftRecovery: requiredElement<HTMLSelectElement>("#creative-story-draft-recovery"),
  creativeFocusAvailability: requiredElement<HTMLElement>("#creative-story-focus-availability"),
  creativeRelationshipFocus: requiredElement<HTMLOptionElement>("#creative-story-focus-relationship"),
  stageFocusButton: requiredElement<HTMLButtonElement>("#stage-focus-button"),
  stageFocusControls: requiredElement<HTMLElement>("#stage-focus-controls"),
  stagePauseButton: requiredElement<HTMLButtonElement>("#stage-pause-button"),
  adventureView: requiredElement<HTMLElement>("#adventure-view"),
  stageFocusRibbon: requiredElement<HTMLElement>("#stage-focus-ribbon"),
  watchCharacterDetails: requiredElement<HTMLButtonElement>("#watch-character-details"),
  watchHeroPortrait: requiredElement<HTMLElement>("#watch-hero-portrait"),
  watchHeroRole: requiredElement<HTMLElement>("#watch-hero-role"),
  watchHeroHealthText: requiredElement<HTMLElement>("#watch-hero-health-text"),
  watchHeroManaText: requiredElement<HTMLElement>("#watch-hero-mana-text"),
  watchHeroHealth: requiredElement<HTMLProgressElement>("#watch-hero-health"),
  watchHeroMana: requiredElement<HTMLProgressElement>("#watch-hero-mana"),
  watchCompanionCard: requiredElement<HTMLElement>("#watch-companion-card"),
  watchCompanionPortrait: requiredElement<HTMLElement>("#watch-companion-portrait"),
  watchCompanionHealthText: requiredElement<HTMLElement>("#watch-companion-health-text"),
  watchCompanionHealth: requiredElement<HTMLProgressElement>("#watch-companion-health"),
  watchCompanionStatus: requiredElement<HTMLElement>("#watch-companion-status"),
  stageFocusHero: requiredElement<HTMLElement>("#stage-focus-hero"),
  stageFocusResources: requiredElement<HTMLElement>("#stage-focus-resources"),
  stageFocusCompanion: requiredElement<HTMLElement>("#stage-focus-companion"),
  stageFocusQuest: requiredElement<HTMLElement>("#stage-focus-quest"),
  stageFocusObjectiveProgress: requiredElement<HTMLElement>("#stage-focus-objective-progress"),
  stageFocusObjective: requiredElement<HTMLElement>("#stage-focus-objective"),
  stageFocusHeadline: requiredElement<HTMLElement>("#stage-focus-headline"),
  stageFocusAction: requiredElement<HTMLElement>("#stage-focus-action"),
  stageFocusNarrator: requiredElement<HTMLElement>("#stage-focus-narrator"),
  location: requiredElement<HTMLSpanElement>("#scene-location"),
  headline: requiredElement<HTMLHeadingElement>("#scene-headline"),
  action: requiredElement<HTMLParagraphElement>("#scene-action"),
  narratorLine: requiredElement<HTMLParagraphElement>("#narrator-line"),
  narratorLineLabel: requiredElement<HTMLElement>("#narrator-line-label"),
  narratorLineText: requiredElement<HTMLElement>("#narrator-line-text"),
  storyBeatControl: requiredElement<HTMLElement>("#story-beat-control"),
  storyBeatKicker: requiredElement<HTMLElement>("#story-beat-kicker"),
  storyBeatFlow: requiredElement<HTMLElement>("#story-beat-flow"),
  storyBeatKeepMoving: requiredElement<HTMLInputElement>("#story-beat-keep-moving"),
  storyBeatWrite: requiredElement<HTMLButtonElement>("#story-beat-write"),
  storyBeatResult: requiredElement<HTMLElement>("#story-beat-result"),
  storyBeatResultLabel: requiredElement<HTMLElement>("#story-beat-result-label"),
  storyBeatResultText: requiredElement<HTMLElement>("#story-beat-result-text"),
  storyTrail: requiredElement<HTMLDetailsElement>("#story-trail"),
  storyTrailCount: requiredElement<HTMLElement>("#story-trail-count"),
  storyTrailList: requiredElement<HTMLOListElement>("#story-trail-list"),
  storyTrailCopy: requiredElement<HTMLButtonElement>("#story-trail-copy"),
  storyBeatAnnouncement: requiredElement<HTMLElement>("#story-beat-announcement"),
  goal: requiredElement<HTMLElement>("#scene-goal"),
  consequence: requiredElement<HTMLElement>("#scene-consequence"),
  decision: requiredElement<HTMLElement>("#scene-decision"),
  healthText: requiredElement<HTMLElement>("#hero-health-text"),
  healthBar: requiredElement<HTMLProgressElement>("#hero-health-bar"),
  manaText: requiredElement<HTMLElement>("#hero-mana-text"),
  manaBar: requiredElement<HTMLProgressElement>("#hero-mana-bar"),
  experienceText: requiredElement<HTMLElement>("#hero-xp-text"),
  experienceBar: requiredElement<HTMLProgressElement>("#hero-xp-bar"),
  strength: requiredElement<HTMLElement>("#stat-strength"),
  agility: requiredElement<HTMLElement>("#stat-agility"),
  vitality: requiredElement<HTMLElement>("#stat-vitality"),
  intellect: requiredElement<HTMLElement>("#stat-intellect"),
  spirit: requiredElement<HTMLElement>("#stat-spirit"),
  luck: requiredElement<HTMLElement>("#stat-luck"),
  armor: requiredElement<HTMLElement>("#stat-armor"),
  power: requiredElement<HTMLElement>("#stat-power"),
  initiative: requiredElement<HTMLElement>("#stat-initiative"),
  heroGrowthSummary: requiredElement<HTMLElement>("#hero-growth-summary"),
  questTitle: requiredElement<HTMLElement>("#quest-title"),
  questSummary: requiredElement<HTMLElement>("#quest-summary"),
  questLead: requiredElement<HTMLElement>("#quest-lead"),
  questObjectives: requiredElement<HTMLUListElement>("#quest-objectives"),
  traversalLabel: requiredElement<HTMLElement>("#traversal-label"),
  traversalText: requiredElement<HTMLElement>("#traversal-progress-text"),
  traversalProgress: requiredElement<HTMLProgressElement>("#traversal-progress"),
  traversalDirective: requiredElement<HTMLElement>("#traversal-directive"),
  counterDuelSummary: requiredElement<HTMLElement>("#counter-duel-summary"),
  battleTurnStrip: requiredElement<HTMLElement>("#battle-turn-strip"),
  battleOverview: requiredElement<HTMLElement>("#battle-overview"),
  battleThreat: requiredElement<HTMLElement>("#battle-threat"),
  battleRoster: requiredElement<HTMLOListElement>("#battle-roster"),
  battleUpcoming: requiredElement<HTMLOListElement>("#battle-upcoming"),
  companionCard: requiredElement<HTMLElement>("#companion-card"),
  companionName: requiredElement<HTMLElement>("#companion-name"),
  companionStatus: requiredElement<HTMLElement>("#companion-status"),
  companionRole: requiredElement<HTMLElement>("#companion-role"),
  companionHealthText: requiredElement<HTMLElement>("#companion-health-text"),
  companionHealthBar: requiredElement<HTMLProgressElement>("#companion-health-bar"),
  companionPurpose: requiredElement<HTMLElement>("#companion-purpose"),
  companionOrigin: requiredElement<HTMLElement>("#companion-origin"),
  companionDestination: requiredElement<HTMLElement>("#companion-destination"),
  companionVictories: requiredElement<HTMLElement>("#companion-victories"),
  companionBond: requiredElement<HTMLElement>("#companion-bond"),
  openStatusLog: requiredElement<HTMLButtonElement>("#open-status-log"),
  heroHud: requiredElement<HTMLElement>("#hero-hud"),
  chronicle: requiredElement<HTMLElement>("#chronicle"),
  viewToolbar: requiredElement<HTMLElement>("#view-toolbar"),
  miniMap: requiredElement<HTMLButtonElement>("#mini-map"),
  miniMapPlace: requiredElement<HTMLElement>("#mini-map-place"),
  miniMapGraphic: requiredElement<SVGSVGElement>("#mini-map-graphic"),
  miniMapRoute: requiredElement<HTMLElement>("#mini-map-route"),
  mapInspector: requiredElement<HTMLElement>("#map-inspector"),
  mapTitle: requiredElement<HTMLElement>("#map-view-title"),
  mapCurrentPlace: requiredElement<HTMLElement>("#map-current-place"),
  mapParty: requiredElement<HTMLElement>("#map-party"),
  mapQuestLead: requiredElement<HTMLElement>("#map-quest-lead"),
  mapRoute: requiredElement<HTMLElement>("#map-route"),
  mapDiscovery: requiredElement<HTMLElement>("#map-discovery"),
  mapGazetteer: requiredElement<HTMLDetailsElement>("#map-gazetteer"),
  gazetteerPlace: requiredElement<HTMLSelectElement>("#gazetteer-place"),
  gazetteerEntry: requiredElement<HTMLElement>("#gazetteer-entry"),
  mapHeroActivity: requiredElement<HTMLElement>("#map-hero-activity"),
  inspectionScreen: requiredElement<HTMLElement>("#inspection-screen"),
  inspectionTitle: requiredElement<HTMLElement>("#inspection-title"),
  inspectionSubtitle: requiredElement<HTMLElement>("#inspection-subtitle"),
  screenHeroActivity: requiredElement<HTMLElement>("#screen-hero-activity"),
  inventoryView: requiredElement<HTMLElement>("#inventory-view"),
  inventoryTitle: requiredElement<HTMLElement>("#inventory-title"),
  inventoryClass: requiredElement<HTMLElement>("#inventory-class"),
  inventoryGold: requiredElement<HTMLElement>("#inventory-gold"),
  inventoryStacks: requiredElement<HTMLElement>("#inventory-stacks"),
  inventoryItems: requiredElement<HTMLElement>("#inventory-items"),
  inventoryEquipped: requiredElement<HTMLElement>("#inventory-equipped"),
  inventoryGrid: requiredElement<HTMLUListElement>("#inventory-grid"),
  journalView: requiredElement<HTMLElement>("#journal-view"),
  journalSummary: requiredElement<HTMLElement>("#journal-summary"),
  journalQuestLead: requiredElement<HTMLElement>("#journal-quest-lead"),
  journalQuestList: requiredElement<HTMLElement>("#journal-quest-list"),
  journalCompletedList: requiredElement<HTMLOListElement>("#journal-completed-list"),
  journalCompanionSummary: requiredElement<HTMLElement>("#journal-companion-summary"),
  journalCompanionActive: requiredElement<HTMLElement>("#journal-companion-active"),
  journalCompanionFormer: requiredElement<HTMLOListElement>("#journal-companion-former"),
  journalMentorSummary: requiredElement<HTMLElement>("#journal-mentor-summary"),
  journalMentorList: requiredElement<HTMLOListElement>("#journal-mentor-list"),
  journalStatus: requiredElement<HTMLElement>("#journal-status"),
  journalStatusButton: requiredElement<HTMLButtonElement>("#journal-status-button"),
  journalGrowthSummary: requiredElement<HTMLElement>("#journal-growth-summary"),
  journalGrowthCheckpoints: requiredElement<HTMLOListElement>("#journal-growth-checkpoints"),
  journalGrowthAttributes: requiredElement<HTMLDListElement>("#journal-growth-attributes"),
  journalGrowthRecords: requiredElement<HTMLOListElement>("#journal-growth-records"),
  codexView: requiredElement<HTMLElement>("#codex-view"),
  codexSummary: requiredElement<HTMLElement>("#codex-summary"),
  codexRecorded: requiredElement<HTMLElement>("#codex-recorded"),
  codexLearned: requiredElement<HTMLElement>("#codex-learned"),
  codexHeld: requiredElement<HTMLElement>("#codex-held"),
  codexRejected: requiredElement<HTMLElement>("#codex-rejected"),
  codexUnverified: requiredElement<HTMLElement>("#codex-unverified"),
  codexGrid: requiredElement<HTMLOListElement>("#codex-grid"),
  codexOverflow: requiredElement<HTMLElement>("#codex-overflow"),
  spellbookView: requiredElement<HTMLElement>("#spellbook-view"),
  spellbookSummary: requiredElement<HTMLElement>("#spellbook-summary"),
  spellbookOwned: requiredElement<HTMLElement>("#spellbook-owned"),
  spellbookSpells: requiredElement<HTMLElement>("#spellbook-spells"),
  spellbookTechniques: requiredElement<HTMLElement>("#spellbook-techniques"),
  spellbookSecrets: requiredElement<HTMLElement>("#spellbook-secrets"),
  spellbookUses: requiredElement<HTMLElement>("#spellbook-uses"),
  spellbookBreakthrough: requiredElement<HTMLElement>("#spellbook-breakthrough"),
  spellbookBreakthroughName: requiredElement<HTMLElement>("#spellbook-breakthrough-name"),
  spellbookBreakthroughDetail: requiredElement<HTMLElement>("#spellbook-breakthrough-detail"),
  spellbookGrid: requiredElement<HTMLOListElement>("#spellbook-grid"),
  spellbookOverflow: requiredElement<HTMLElement>("#spellbook-overflow"),
  hallView: requiredElement<HTMLElement>("#hall-view"),
  hallSummary: requiredElement<HTMLElement>("#hall-summary"),
  hallTotal: requiredElement<HTMLElement>("#hall-total"),
  hallEarned: requiredElement<HTMLElement>("#hall-earned"),
  hallAdopted: requiredElement<HTMLElement>("#hall-adopted"),
  hallAdmitted: requiredElement<HTMLElement>("#hall-admitted"),
  hallLegacySummary: requiredElement<HTMLElement>("#hall-legacy-summary"),
  hallLegacyGrid: requiredElement<HTMLOListElement>("#hall-legacy-grid"),
  hallGrid: requiredElement<HTMLOListElement>("#hall-grid"),
  hallOverflow: requiredElement<HTMLElement>("#hall-overflow"),
  viewAnnouncement: requiredElement<HTMLElement>("#view-announcement"),
  watchBadge: requiredElement<HTMLElement>("#watch-badge"),
  spectatorInbox: requiredElement<HTMLElement>("#spectator-inbox"),
  spectatorInboxSummary: requiredElement<HTMLElement>("#spectator-inbox-summary"),
  spectatorInboxDropped: requiredElement<HTMLElement>("#spectator-inbox-dropped"),
  spectatorInboxList: requiredElement<HTMLOListElement>("#spectator-inbox-list"),
  spectatorInboxClose: requiredElement<HTMLButtonElement>("#spectator-inbox-close"),
  updateStatus: requiredElement<HTMLElement>("#update-status"),
  trapCutaway: requiredElement<HTMLElement>("#trap-cutaway"),
  elsewhereLoafCutaway: requiredElement<HTMLElement>("#elsewhere-loaf-cutaway"),
  elsewhereLoafTitle: requiredElement<HTMLElement>("#elsewhere-loaf-title"),
  elsewhereLoafActor: requiredElement<HTMLElement>("#elsewhere-loaf-actor"),
  elsewhereLoafLine: requiredElement<HTMLElement>("#elsewhere-loaf-line"),
  elsewhereLoafSupply: requiredElement<HTMLElement>("#elsewhere-loaf-supply"),
  elsewhereLoafOutcome: requiredElement<HTMLButtonElement>("#elsewhere-loaf-outcome"),
  trapCutawayTitle: requiredElement<HTMLElement>("#trap-cutaway-title"),
  trapCutawayEvent: requiredElement<HTMLElement>("#trap-cutaway-event"),
  trapCutawayCommand: requiredElement<HTMLElement>("#trap-cutaway-command"),
  trapCutawayInspection: requiredElement<HTMLElement>("#trap-cutaway-inspection"),
  trapCutawayCheck: requiredElement<HTMLElement>("#trap-cutaway-check"),
  trapCutawayResult: requiredElement<HTMLElement>("#trap-cutaway-result"),
  trapCutawayConsequence: requiredElement<HTMLElement>("#trap-cutaway-consequence"),
  trapCutawayProgress: requiredElement<HTMLElement>("#trap-cutaway-progress"),
  trapCutawayOutcome: requiredElement<HTMLButtonElement>("#trap-cutaway-outcome"),
  trapCutawayAnnouncement: requiredElement<HTMLElement>("#trap-cutaway-announcement"),
  farewellCutaway: requiredElement<HTMLElement>("#farewell-cutaway"),
  farewellCutawayTitle: requiredElement<HTMLElement>("#farewell-cutaway-title"),
  farewellCutawayEvent: requiredElement<HTMLElement>("#farewell-cutaway-event"),
  farewellCutawayPromise: requiredElement<HTMLElement>("#farewell-cutaway-promise"),
  farewellCutawayJourney: requiredElement<HTMLElement>("#farewell-cutaway-journey"),
  farewellCutawayArrival: requiredElement<HTMLElement>("#farewell-cutaway-arrival"),
  farewellCutawayDeparture: requiredElement<HTMLElement>("#farewell-cutaway-departure"),
  farewellCutawayLegacy: requiredElement<HTMLElement>("#farewell-cutaway-legacy"),
  farewellCutawayProgress: requiredElement<HTMLElement>("#farewell-cutaway-progress"),
  farewellCutawayOutcome: requiredElement<HTMLButtonElement>("#farewell-cutaway-outcome"),
  farewellCutawayAnnouncement: requiredElement<HTMLElement>("#farewell-cutaway-announcement"),
  levelUpCutaway: requiredElement<HTMLElement>("#level-up-cutaway"),
  levelUpCutawayKicker: requiredElement<HTMLElement>("#level-up-cutaway-kicker"),
  levelUpCutawayTitle: requiredElement<HTMLElement>("#level-up-cutaway-title"),
  levelUpCutawayEvent: requiredElement<HTMLElement>("#level-up-cutaway-event"),
  levelUpCutawaySource: requiredElement<HTMLElement>("#level-up-cutaway-source"),
  levelUpCutawayThreshold: requiredElement<HTMLElement>("#level-up-cutaway-threshold"),
  levelUpCutawayLevel: requiredElement<HTMLElement>("#level-up-cutaway-level"),
  levelUpCutawaySelectionStep: requiredElement<HTMLLIElement>("#level-up-cutaway-selection-step"),
  levelUpCutawaySelection: requiredElement<HTMLElement>("#level-up-cutaway-selection"),
  levelUpCutawayCandidates: requiredElement<HTMLOListElement>("#level-up-cutaway-candidates"),
  levelUpCutawayMechanics: requiredElement<HTMLElement>("#level-up-cutaway-mechanics"),
  levelUpCutawayTableauLabel: requiredElement<HTMLElement>("#level-up-cutaway-tableau-label"),
  levelUpCutawayTableau: requiredElement<HTMLElement>("#level-up-cutaway-tableau"),
  levelUpCutawayHallSeal: requiredElement<HTMLElement>("#level-up-cutaway-hall-seal"),
  levelUpCutawayHallSealTitle: requiredElement<HTMLElement>("#level-up-cutaway-hall-seal-title"),
  levelUpCutawayHallSealRecord: requiredElement<HTMLElement>("#level-up-cutaway-hall-seal-record"),
  levelUpCutawayHallSealProvenance: requiredElement<HTMLElement>("#level-up-cutaway-hall-seal-provenance"),
  levelUpCutawayHallSealTruth: requiredElement<HTMLElement>("#level-up-cutaway-hall-seal-truth"),
  levelUpCutawayProgress: requiredElement<HTMLElement>("#level-up-cutaway-progress"),
  levelUpCutawayOutcome: requiredElement<HTMLButtonElement>("#level-up-cutaway-outcome"),
  levelUpCutawayAnnouncement: requiredElement<HTMLElement>("#level-up-cutaway-announcement"),
  abilityResonanceCutaway: requiredElement<HTMLElement>("#ability-resonance-cutaway"),
  abilityResonanceCutawayTitle: requiredElement<HTMLElement>("#ability-resonance-cutaway-title"),
  abilityResonanceCutawayEvent: requiredElement<HTMLElement>("#ability-resonance-cutaway-event"),
  abilityResonanceCutawaySource: requiredElement<HTMLElement>("#ability-resonance-cutaway-source"),
  abilityResonanceCutawayThreshold: requiredElement<HTMLElement>("#ability-resonance-cutaway-threshold"),
  abilityResonanceCutawayEffect: requiredElement<HTMLElement>("#ability-resonance-cutaway-effect"),
  abilityResonanceCutawayMastery: requiredElement<HTMLElement>("#ability-resonance-cutaway-mastery"),
  abilityResonanceCutawayNextUse: requiredElement<HTMLElement>("#ability-resonance-cutaway-next-use"),
  abilityResonanceCutawayProgress: requiredElement<HTMLElement>("#ability-resonance-cutaway-progress"),
  abilityResonanceCutawayOutcome: requiredElement<HTMLButtonElement>("#ability-resonance-cutaway-outcome"),
  abilityResonanceCutawayAnnouncement: requiredElement<HTMLElement>("#ability-resonance-cutaway-announcement"),
  weaponMemoryCutaway: requiredElement<HTMLElement>("#weapon-memory-cutaway"),
  weaponMemoryCutawayTitle: requiredElement<HTMLElement>("#weapon-memory-cutaway-title"),
  weaponMemoryCutawayEvent: requiredElement<HTMLElement>("#weapon-memory-cutaway-event"),
  weaponMemoryCutawayName: requiredElement<HTMLElement>("#weapon-memory-cutaway-name"),
  weaponMemoryCutawayFirst: requiredElement<HTMLElement>("#weapon-memory-cutaway-first"),
  weaponMemoryCutawayRoad: requiredElement<HTMLElement>("#weapon-memory-cutaway-road"),
  weaponMemoryCutawayStrongest: requiredElement<HTMLElement>("#weapon-memory-cutaway-strongest"),
  weaponMemoryCutawayForm: requiredElement<HTMLElement>("#weapon-memory-cutaway-form"),
  weaponMemoryCutawayFinal: requiredElement<HTMLElement>("#weapon-memory-cutaway-final"),
  weaponMemoryCutawayProgress: requiredElement<HTMLElement>("#weapon-memory-cutaway-progress"),
  weaponMemoryCutawayOutcome: requiredElement<HTMLButtonElement>("#weapon-memory-cutaway-outcome"),
  weaponMemoryCutawayAnnouncement: requiredElement<HTMLElement>("#weapon-memory-cutaway-announcement"),
  battleSpoilsCutaway: requiredElement<HTMLElement>("#battle-spoils-cutaway"),
  battleSpoilsCutawayTitle: requiredElement<HTMLElement>("#battle-spoils-cutaway-title"),
  battleSpoilsCutawayEvent: requiredElement<HTMLElement>("#battle-spoils-cutaway-event"),
  battleSpoilsCutawayFound: requiredElement<HTMLElement>("#battle-spoils-cutaway-found"),
  battleSpoilsCutawayOld: requiredElement<HTMLElement>("#battle-spoils-cutaway-old"),
  battleSpoilsCutawayNew: requiredElement<HTMLElement>("#battle-spoils-cutaway-new"),
  battleSpoilsCutawayStats: requiredElement<HTMLElement>("#battle-spoils-cutaway-stats"),
  battleSpoilsCutawayResources: requiredElement<HTMLElement>("#battle-spoils-cutaway-resources"),
  battleSpoilsCutawayContinuity: requiredElement<HTMLElement>("#battle-spoils-cutaway-continuity"),
  battleSpoilsCutawayProgress: requiredElement<HTMLElement>("#battle-spoils-cutaway-progress"),
  battleSpoilsCutawayOutcome: requiredElement<HTMLButtonElement>("#battle-spoils-cutaway-outcome"),
  battleSpoilsCutawayAnnouncement: requiredElement<HTMLElement>("#battle-spoils-cutaway-announcement"),
  townItineraryCutaway: requiredElement<HTMLElement>("#town-itinerary-cutaway"),
  townItineraryCutawayTitle: requiredElement<HTMLElement>("#town-itinerary-cutaway-title"),
  townItineraryCutawayEvent: requiredElement<HTMLElement>("#town-itinerary-cutaway-event"),
  townItineraryCutawayArrival: requiredElement<HTMLElement>("#town-itinerary-cutaway-arrival"),
  townItineraryCutawayDistrict: requiredElement<HTMLElement>("#town-itinerary-cutaway-district"),
  townItineraryCutawayRoute: requiredElement<HTMLElement>("#town-itinerary-cutaway-route"),
  townItineraryCutawayResident: requiredElement<HTMLElement>("#town-itinerary-cutaway-resident"),
  townItineraryCutawayConsequence: requiredElement<HTMLElement>("#town-itinerary-cutaway-consequence"),
  townItineraryCutawayProgress: requiredElement<HTMLElement>("#town-itinerary-cutaway-progress"),
  townItineraryCutawayOutcome: requiredElement<HTMLButtonElement>("#town-itinerary-cutaway-outcome"),
  townItineraryCutawayAnnouncement: requiredElement<HTMLElement>("#town-itinerary-cutaway-announcement"),
  fieldNoteCutaway: requiredElement<HTMLElement>("#field-note-cutaway"),
  fieldNoteCutawayTitle: requiredElement<HTMLElement>("#field-note-cutaway-title"),
  fieldNoteCutawayEvent: requiredElement<HTMLElement>("#field-note-cutaway-event"),
  fieldNoteCutawayObservations: requiredElement<HTMLElement>("#field-note-cutaway-observations"),
  fieldNoteCutawayCurrent: requiredElement<HTMLElement>("#field-note-cutaway-current"),
  fieldNoteCutawayInference: requiredElement<HTMLElement>("#field-note-cutaway-inference"),
  fieldNoteCutawayPrecedence: requiredElement<HTMLElement>("#field-note-cutaway-precedence"),
  fieldNoteCutawayLiveTell: requiredElement<HTMLElement>("#field-note-cutaway-live-tell"),
  fieldNoteCutawayLiveTellSignal: requiredElement<HTMLElement>("#field-note-cutaway-live-tell-signal"),
  fieldNoteCutawayLiveTellPriority: requiredElement<HTMLElement>("#field-note-cutaway-live-tell-priority"),
  fieldNoteCutawayLiveTellHabit: requiredElement<HTMLElement>("#field-note-cutaway-live-tell-habit"),
  fieldNoteCutawayLiveTellHidden: requiredElement<HTMLElement>("#field-note-cutaway-live-tell-hidden"),
  fieldNoteCutawayLiveTellHiddenCopy: requiredElement<HTMLElement>("#field-note-cutaway-live-tell-hidden-copy"),
  fieldNoteCutawayNotes: requiredElement<HTMLOListElement>("#field-note-cutaway-notes"),
  fieldNoteCutawayProgress: requiredElement<HTMLElement>("#field-note-cutaway-progress"),
  fieldNoteCutawayOutcome: requiredElement<HTMLButtonElement>("#field-note-cutaway-outcome"),
  fieldNoteCutawayAnnouncement: requiredElement<HTMLElement>("#field-note-cutaway-announcement"),
};

const trapCutawaySteps = Array.from(elements.trapCutaway.querySelectorAll<HTMLElement>("[data-cutaway-step]"));
const farewellCutawaySteps = Array.from(elements.farewellCutaway.querySelectorAll<HTMLElement>("[data-farewell-step]"));
const levelUpCutawaySteps = Array.from(elements.levelUpCutaway.querySelectorAll<HTMLElement>("[data-level-step]"));
const abilityResonanceCutawaySteps = Array.from(elements.abilityResonanceCutaway.querySelectorAll<HTMLElement>("[data-ability-resonance-step]"));
const weaponMemoryCutawaySteps = Array.from(elements.weaponMemoryCutaway.querySelectorAll<HTMLElement>("[data-weapon-memory-step]"));
const battleSpoilsCutawaySteps = Array.from(elements.battleSpoilsCutaway.querySelectorAll<HTMLElement>("[data-battle-spoils-step]"));
const townItineraryCutawaySteps = Array.from(elements.townItineraryCutaway.querySelectorAll<HTMLElement>("[data-town-itinerary-step]"));
const fieldNoteCutawaySteps = Array.from(elements.fieldNoteCutaway.querySelectorAll<HTMLElement>("[data-field-note-step]"));

const viewButtons = Array.from(elements.viewToolbar.querySelectorAll<HTMLButtonElement>("[data-view]"));
if (viewButtons.length !== inspectionViews.length) throw new Error("View toolbar is incomplete");

// One set of live detail nodes, hosted by the same screen as the other tabs.
elements.adventureView.append(elements.heroHud, elements.chronicle);
const inspectionScrollByView: Partial<Record<InspectionView, number>> = {};
const atlasGazetteerView = createAtlasGazetteerView(elements.gazetteerPlace, elements.gazetteerEntry);
const sharedOpeningPip = document.createElement("span");
sharedOpeningPip.id = "shared-opening-pip";
sharedOpeningPip.className = "shared-opening-pip";
sharedOpeningPip.textContent = "◆ Shared Opening 1/1";

const repository = new CampaignRepository();
const renderer = await GameRenderer.mount(elements.stage);
void applyDungeonPerspective();
let champions: readonly ChampionInduction[] = await repository.listChampions();
const restoredWorld = await repository.loadActive();
let state = restoredWorld ?? createNewWorld();
const lastPresentedStory = createLastPresentedStory(state.campaignId);
const narrativeJournal = createNarrativeJournal();
const narrativeJournalView = createNarrativeJournalView(elements.journalView, narrativeJournal, () => state.campaignId);
const chroniclePlateArchive = createChroniclePlateArchive();
const chroniclePlateView = createChroniclePlateView(requiredElement<HTMLDetailsElement>("#journal-chronicle-plates"),
  chroniclePlateArchive, () => ({ campaignId: state.campaignId, currentTick: state.tick }));
const statusHistoryView = createStatusHistoryView(elements.journalStatus);
const reparteeView = createReparteeView(requiredElement<HTMLElement>("#repartee-caption"),
  requiredElement<HTMLDetailsElement>("#journal-repartee"));
const bellView = createBorrowedBellView(requiredElement<HTMLElement>("#bell-caption"),
  requiredElement<HTMLDetailsElement>("#journal-bell"));
let durableState = state;
let factualStoryBeatOpportunity: FactualStoryBeatOpportunityV1 | null = null;
const simulation = new SimulationClient();
let paused = false;
let startupHold = true;
let freshPlayChoice = restoredWorld === undefined;
let requestedPlayMode: PlayMode | null = readPlayModePreference();
let pauseRequestGeneration = 0;
let stepping = false;
let pendingInteractions = 0;
let loop: number | undefined;
let runtimeWatchdog: number | undefined;
let activeView: InspectionView = "watch";
const compactStageFocusMedia = window.matchMedia(compactStageFocusQuery);
let explicitStageChromePreference = false;
let stageChromeMode: StageChromeMode = "panels";
let observedPresentationState = state;
let spectatorInbox = createSpectatorInbox(state);
let spectatorRecapOpen = false;
let automaticUpdateMonitor: AutomaticUpdateMonitor | null = null;
let presentationSuspended = document.hidden;
let lastAdvanceAtMs = Date.now();
let runtimeRecovering = false;
let cutawayController: CutawayControllerState = createCutawayController();
let trapCutawayFatigueMemory: TrapCutawayFatigueMemory = createTrapCutawayFatigueMemory();
let presentationBusy = false;
let narrativeReading = false;
let reparteeScene: ReparteeSceneView | null = null;
let bellScene: BorrowedBellSceneView | null = null;
let reparteeDwell = createReparteeDwell();
let reparteeVisible: boolean | null = null;
let narrativeReplay = false;
let narrativeCheckQueued = false;
let lastNarrativeClosedAtMs = -Infinity;
let storytellingPreferences = readStorytellingPreferences();
let cutawayStartedAtMs = 0;
let cutawayPausedAtMs: number | null = null;
let catchUpAfterPresentation = false;
const activityFocusByView: Partial<Record<HeroInspectionView, string>> = {};
const localNarratorAssetStore = createLocalNarratorAssetStore({
  runtimeSourceUrls: localNarratorRuntimeSourceUrls,
});
const localNarratorClient = new NarratorClient({
  workerFactory: createLocalNarratorWorker,
  tokenMeter: localNarratorHostTokenMeter,
  clock: {
    now: () => performance.now(),
    setTimeout: (callback, milliseconds) => window.setTimeout(callback, milliseconds),
    clearTimeout: (handle) => window.clearTimeout(handle as number),
  },
});
const localNarratorController = createLocalNarratorUiController({
  storage: window.localStorage,
  assetStore: localNarratorAssetStore,
  client: localNarratorClient,
  getCapability: detectNarratorCapability,
  onChange: (snapshot) => renderLocalNarratorUi(snapshot),
});
let storyTrailCopyInFlight = false;
const storyBeatController = createStoryBeatController({
  author: localNarratorClient,
  onChange: (snapshot) => renderStoryBeatUi(snapshot),
});
const creativeStoryController = createCreativeStoryController({
  createWriter: (onIdleFailure) => createCreativeWriterClient({ onIdleFailure }),
  hasCachedModel: hasCachedCreativeWriterModel,
  removeCachedModel: removeCachedCreativeWriterModel,
  allowVignette: () => storytellingPreferences.draftRecovery === "vignette",
  continuity: (moment) => selectNarrativeContinuity(narrativeJournal.snapshot.entries, moment.job, moment.viewpoint, moment.departure),
  previousStage: () => {
    const previous = lastPresentedStory.get(state.campaignId);
    return previous === null ? undefined : previous.direction?.stage ?? "parchment";
  },
  onChange: (snapshot) => renderCreativeStoryUi(snapshot),
});
const creativeStoryDirector = createCreativeStoryDirector({
  writer: creativeStoryController,
  cadenceMs: () => storytellingCadenceMs(storytellingPreferences.rhythm),
  presentationNotBeforeMs: () => lastNarrativeClosedAtMs + storytellingCadenceMs(storytellingPreferences.rhythm),
  storyFocus: () => storytellingPreferences.focus,
  onWritten: (passage) => {
    narrativeJournal.record(passage);
    narrativeJournalView.render();
  },
  onReady: () => requestNarrativeCheck(),
});
const narrativeIntermission = createNarrativeIntermission({
  onClose: () => releaseNarrativeReading(),
});
const playModeStartup = createPlayModeStartup({
  readMode: readPlayModePreference,
  writeMode: writePlayModePreference,
  hasCachedModel: hasCachedCreativeWriterModel,
  applyMode: applyPlayMode,
  showChoice: (cached) => {
    elements.playStartCache.textContent = cached
      ? "Saved LLM found on this device. No download needed."
      : "Needs WebGPU. First use downloads about 900 MB. Any saved model files are reused.";
    if (!elements.playStartDialog.open) elements.playStartDialog.showModal();
    elements.playStartDeterministic.focus();
  },
  onHold: (held) => {
    startupHold = held;
    if (!held) lastAdvanceAtMs = Date.now();
    syncPresentationPaused();
    requestNarrativeCheck();
  },
});
let staticCutawayNarratorFingerprint: string | null = null;

document.documentElement.dataset.appVersion = __APP_VERSION__;

function readStageChromePreference(): string | null {
  try {
    return localStorage.getItem(stageFocusPreferenceKey);
  } catch {
    return null;
  }
}

function writeStageChromePreference(mode: StageChromeMode): void {
  try {
    localStorage.setItem(stageFocusPreferenceKey, mode);
  } catch {
    // The current page can still focus when browser storage is unavailable.
  }
}

function formatNarratorBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  return `${(bytes / 1_000_000).toFixed(bytes >= 100_000_000 ? 0 : 1)} MB`;
}

function narratorStatusLabel(snapshot: LocalNarratorControllerSnapshot): string {
  if (snapshot.status === "checking") return "Checking verified local model";
  if (snapshot.status === "needs-setup") return "Model missing · setup required";
  if (snapshot.status === "downloading") return "Downloading and verifying on this device";
  if (snapshot.status === "ready") return "Ready · Experimental / Unrated";
  if (snapshot.status === "suppressed") {
    return snapshot.suppression === "eco"
      ? "On · paused for this device's Eco limits"
      : "On · waiting for an eligible story moment";
  }
  if (snapshot.status === "unsupported") return "Unavailable on this device";
  if (snapshot.status === "failed") return "Failed · deterministic Chronicle unchanged";
  return "Off · nothing downloaded automatically";
}

function renderPlayModeStatus(): void {
  elements.playModeSelect.value = requestedPlayMode ?? "deterministic";
  elements.app.dataset.playMode = requestedPlayMode ?? "choose";
  const creative = creativeStoryController.snapshot;
  const phase = creative.phase;
  // Removal already stops the writer; don't accept an activation it cannot start yet.
  const removing = phase === "off" && creative.busy;
  elements.playModeSelect.disabled = removing;
  elements.playModeRetry.disabled = removing;
  elements.playModeRetry.hidden = requestedPlayMode !== "llm"
    || phase === "loading" || phase === "ready" || phase === "writing"
    || localNarratorController.snapshot.enabled || localNarratorController.snapshot.downloading;
  elements.playModeStatus.textContent = removing
    ? "Removing saved LLM files · activation is available when finished"
    : requestedPlayMode !== "llm"
      ? "Without LLM · deterministic adventure"
      : phase === "loading" || localNarratorController.snapshot.downloading
        ? "Preparing the LLM · the adventure continues"
        : phase === "ready" || phase === "writing" || localNarratorController.snapshot.enabled
          ? "With LLM · stories are written on this device"
          : phase === "failed" ? `LLM is not running · ${creativeStoryController.snapshot.status}`
            : "LLM is not running · the adventure continues. You can retry here.";
}

function applyPlayMode(mode: PlayMode, cacheOnly = false): void {
  requestedPlayMode = mode;
  freshPlayChoice = false;
  if (elements.playStartDialog.open) elements.playStartDialog.close();
  cancelNarrativeIntermission();
  localNarratorController.disable();
  if (mode === "deterministic") creativeStoryController.stop();
  else void creativeStoryController.load({ cacheOnly });
  renderPlayModeStatus();
  requestNarrativeCheck();
}

function renderLocalNarratorUi(snapshot: LocalNarratorControllerSnapshot): void {
  elements.narratorButton.textContent = "Options";
  renderPlayModeStatus();
  elements.narratorButton.dataset.narratorState = snapshot.enabled
    ? "enabled"
    : snapshot.status;
  elements.narratorStatus.textContent = narratorStatusLabel(snapshot);
  const progress = snapshot.progress;
  elements.narratorDownloadProgress.hidden = progress === null;
  elements.narratorDownloadProgress.max = progress?.totalDownloadBytes ?? 1;
  elements.narratorDownloadProgress.value = progress?.totalBytes ?? 0;
  elements.narratorDownloadDetail.textContent = progress === null
    ? snapshot.detail
    : `${snapshot.detail} · file ${progress.artifactIndex + 1}/${progress.artifactCount} · ${formatNarratorBytes(progress.totalBytes)} of ${formatNarratorBytes(progress.totalDownloadBytes)}`;

  elements.narratorDownload.hidden = snapshot.enabled
    || snapshot.downloading
    || snapshot.status === "checking";
  elements.narratorDownload.disabled = snapshot.status === "unsupported";
  elements.narratorDownload.textContent = snapshot.status === "failed"
    ? "Verify cache & re-enable"
    : snapshot.status === "needs-setup"
      ? "Download again & enable"
      : "Enable · reuse saved files";
  elements.narratorCancel.hidden = !snapshot.downloading;
  elements.narratorDisable.hidden = snapshot.downloading
    || (!snapshot.consented && !snapshot.enabled);
  elements.narratorRemove.hidden = snapshot.downloading;

  const line = snapshot.line;
  const showLine = line !== null
    && snapshot.enabled
    && snapshot.suppression === null;
  elements.narratorLine.hidden = !showLine;
  elements.stageFocusNarrator.hidden = !showLine;
  if (!showLine || line === null) {
    elements.narratorLineLabel.textContent = "";
    elements.narratorLineText.textContent = "";
    elements.stageFocusNarrator.textContent = "";
    elements.stageFocusNarrator.removeAttribute("title");
    delete elements.narratorLine.dataset.source;
    delete elements.stageFocusNarrator.dataset.source;
    syncStoryBeatPresentation(snapshot);
    return;
  }
  const label = line.source === "model"
    ? "Local Narrator · Experimental / Unrated"
    : "Local Narrator · Safe fallback";
  elements.narratorLine.dataset.source = line.source;
  elements.narratorLineLabel.textContent = label;
  elements.narratorLineText.textContent = line.text;
  elements.stageFocusNarrator.dataset.source = line.source;
  elements.stageFocusNarrator.textContent = line.text;
  elements.stageFocusNarrator.title = `${label}: ${line.text}`;
  syncStoryBeatPresentation(snapshot);
}

function renderStoryBeatUi(snapshot: StoryBeatUiSnapshot): void {
  const transferFocus = (
    !snapshot.visible
    && document.activeElement instanceof Node
    && elements.storyBeatControl.contains(document.activeElement)
  ) || (
    !snapshot.actionVisible
    && document.activeElement === elements.storyBeatWrite
  );
  const fallbackPresentation = snapshot.phase === "fallback" && snapshot.fallbackReason !== null
    ? storyBeatFallbackPresentation(snapshot.fallbackReason)
    : null;
  const opportunity = currentFactualStoryBeatOpportunity();
  const heldOpportunity = snapshot.actionVisible
    && opportunity?.timing === "held"
    && storyBeatController.currentWriteIdentity()
      === storyBeatSourceIdentity(opportunity.job)
    ? opportunity
    : null;
  const heldLocation = heldOpportunity?.job.facts.narrative.location ?? null;
  elements.storyBeatControl.hidden = !snapshot.visible;
  if (heldLocation === null) {
    delete elements.storyBeatControl.dataset.opportunityTiming;
  } else {
    elements.storyBeatControl.dataset.opportunityTiming = "held";
  }
  elements.storyBeatControl.setAttribute(
    "aria-label",
    heldLocation === null
      ? "Optional local story drafts"
      : `Optional held local story draft from ${heldLocation}`,
  );
  elements.storyBeatKicker.textContent = heldLocation === null
    ? "Fact-bound story ink"
    : `Held story · ${heldLocation}`;
  if (heldLocation === null) {
    elements.storyBeatKicker.removeAttribute("title");
  } else {
    elements.storyBeatKicker.title =
      `Held committed story from ${heldLocation}`;
  }
  elements.storyBeatWrite.hidden = !snapshot.actionVisible;
  elements.storyBeatWrite.disabled = snapshot.busy;
  const writeLabel = heldOpportunity !== null && snapshot.phase === "ready"
    ? "Write held beat"
    : storyBeatWriteLabel(snapshot.phase);
  elements.storyBeatWrite.textContent = writeLabel;
  if (heldLocation === null) {
    elements.storyBeatWrite.removeAttribute("aria-label");
  } else {
    elements.storyBeatWrite.setAttribute(
      "aria-label",
      snapshot.phase === "ready"
        ? `Write held story beat from ${heldLocation}`
        : snapshot.phase === "writing"
          ? `Writing held story beat from ${heldLocation} locally`
          : snapshot.phase === "fallback"
            ? `Try held story beat from ${heldLocation} again`
            : `Write another held story beat from ${heldLocation}`,
    );
  }
  elements.storyBeatWrite.setAttribute("aria-busy", String(snapshot.busy));
  elements.storyBeatControl.dataset.phase = snapshot.phase;

  const line = snapshot.line;
  const showFlowChoice = snapshot.actionVisible
    && snapshot.phase === "ready"
    && line === null;
  elements.storyBeatFlow.hidden = !showFlowChoice;
  elements.storyBeatKeepMoving.disabled = !showFlowChoice;
  const currentLensLabel = line?.source === "model"
    ? storyBeatLensLabel(line.lensId)
    : null;
  elements.storyBeatResult.hidden = line === null;
  if (line === null) {
    elements.storyBeatResultLabel.textContent = "";
    elements.storyBeatResultText.textContent = "";
    delete elements.storyBeatResult.dataset.source;
    delete elements.storyBeatResult.dataset.sourceFingerprint;
  } else {
    elements.storyBeatResult.dataset.source = line.source;
    elements.storyBeatResult.dataset.sourceFingerprint = line.sourceFingerprint;
    elements.storyBeatResultLabel.textContent = line.source === "model"
      ? currentLensLabel === null
        ? snapshot.phase === "retained"
          ? "Fact-bound draft kept · EXP"
          : "Fact-bound local draft · EXP"
        : snapshot.phase === "retained"
          ? `${currentLensLabel} · EXP · kept`
          : `${currentLensLabel} · EXP`
      : fallbackPresentation?.label ?? "Safe headline";
    elements.storyBeatResultText.textContent = line.text;
  }

  const earlierTrail = snapshot.trail.filter((entry) => (
    line?.source !== "model"
    || entry.eventId !== line.eventId
    || entry.tick !== line.tick
    || entry.sourceFingerprint !== line.sourceFingerprint
  ));
  const showEarlierTrail = earlierTrail.length > 0 && line === null;
  elements.storyTrail.hidden = !showEarlierTrail;
  if (!showEarlierTrail) elements.storyTrail.open = false;
  elements.storyTrailCount.textContent = earlierTrail.length === 1
    ? "1 session beat"
    : `${earlierTrail.length} session beats`;
  elements.storyTrailCopy.disabled = storyTrailCopyInFlight
    || earlierTrail.length === 0;
  elements.storyTrailCopy.setAttribute(
    "aria-busy",
    String(storyTrailCopyInFlight),
  );
  elements.storyTrailList.replaceChildren(...earlierTrail.map((entry, index) => {
    const item = document.createElement("li");
    item.className = entry.spotlit
      ? "story-trail-item is-spotlit"
      : "story-trail-item";
    const shell = document.createElement("span");
    shell.className = "story-trail-entry";
    const copy = document.createElement("span");
    copy.className = "story-trail-entry-copy";
    const metadata = document.createElement("span");
    metadata.className = "story-trail-metadata";
    const location = document.createElement("span");
    location.className = "story-trail-location";
    location.textContent = entry.location;
    metadata.append(location);
    const lensLabel = storyBeatLensLabel(entry.lensId);
    if (lensLabel !== null) {
      const lens = document.createElement("span");
      lens.className = "story-beat-lens";
      lens.textContent = lensLabel;
      metadata.append(lens);
    }
    const text = document.createElement("span");
    text.className = "story-trail-text";
    text.textContent = entry.text;
    copy.append(metadata, text);
    const spotlight = document.createElement("button");
    spotlight.className = "story-trail-spotlight";
    spotlight.type = "button";
    spotlight.textContent = entry.spotlit ? "Spotlit" : "Spotlight";
    spotlight.setAttribute("aria-pressed", String(entry.spotlit));
    spotlight.setAttribute("aria-describedby", "story-trail-session-note");
    spotlight.setAttribute(
      "aria-label",
      entry.spotlit
        ? `Remove ${entry.location} from this session's spotlight`
        : `Spotlight ${entry.location} for this session`,
    );
    spotlight.addEventListener("click", () => {
      if (!storyBeatController.toggleTrailSpotlight(entry)) return;
      window.requestAnimationFrame(() => {
        elements.storyTrailList
          .querySelectorAll<HTMLButtonElement>(".story-trail-spotlight")
          .item(index)
          .focus();
      });
    });
    shell.append(copy, spotlight);
    item.append(shell);
    return item;
  }));
  if (elements.storyBeatAnnouncement.textContent !== snapshot.announcement) {
    elements.storyBeatAnnouncement.textContent = snapshot.announcement;
  }
  if (transferFocus) window.requestAnimationFrame(focusWatchControl);
}

function renderCreativeStoryUi(snapshot: CreativeStorySnapshot): void {
  const focusedControl = document.activeElement;
  const focusInsideStop = focusedControl === elements.creativeStop;
  elements.creativeStatus.textContent = snapshot.phase === "writing"
    ? "Weaving a story quietly · the adventure continues"
    : snapshot.phase === "ready"
      ? "On · stories appear between scenes, with no writing button"
      : snapshot.status;
  const active = snapshot.phase === "ready" || snapshot.phase === "writing";
  elements.creativeLoad.hidden = active || snapshot.phase === "loading";
  elements.creativeLoad.disabled = snapshot.busy;
  elements.creativeLoad.textContent = snapshot.cached ? "Use saved model" : "Download & try creative writer";
  elements.creativeStop.hidden = !active && snapshot.phase !== "loading";
  elements.creativeStop.textContent = snapshot.busy
    ? "Cancel · keep saved files"
    : snapshot.cached ? "Turn off · keep model" : "Turn off";
  elements.creativeRemove.hidden = snapshot.phase === "loading" || snapshot.phase === "writing";
  elements.creativeRemove.disabled = snapshot.busy;
  elements.creativeFocus.value = storytellingPreferences.focus;
  elements.creativeFocus.disabled = snapshot.busy;
  elements.creativeRhythm.value = storytellingPreferences.rhythm;
  elements.creativeRhythm.disabled = snapshot.busy;
  elements.creativeDraftRecovery.value = storytellingPreferences.draftRecovery;
  elements.creativeDraftRecovery.disabled = snapshot.busy;
  elements.app.dataset.creativeStoryState = snapshot.phase;
  elements.narratorButton.dataset.creativeState = snapshot.phase;
  elements.narratorButton.textContent = "Options";
  renderPlayModeStatus();
  if (focusInsideStop && elements.creativeStop.hidden) {
    window.requestAnimationFrame(() => { if (elements.narratorDialog.open) elements.creativeLoad.focus(); });
  }
  requestNarrativeCheck();
}

function syncCreativeStoryPresentation(context = narratorPresentationContext()): void {
  lastPresentedStory.syncCampaign(state.campaignId);
  renderLastStoryControl();
  const source = state.chronicle.at(-1);
  const job = projectStoryBeatJobV1(state.campaignId, state.scene, source, source?.id);
  const viewpoint = projectCreativeStoryViewpoint(state.hero, projectParty(state.depth));
  elements.creativeRelationshipFocus.disabled = viewpoint?.companion == null;
  elements.creativeFocus.value = storytellingPreferences.focus;
  elements.creativeRhythm.value = storytellingPreferences.rhythm;
  elements.creativeDraftRecovery.value = storytellingPreferences.draftRecovery;
  elements.creativeFocusAvailability.textContent = storytellingPreferences.focus === "shared-road" && viewpoint?.companion == null
    ? "Shared road is remembered. Solo scenes use Inner life; captured companion moments can still take priority."
    : storytellingPreferences.focus === "shared-road"
      ? "Shared road prioritizes companion milestones. First victories can pair imagined voices; character stats stay unchanged."
      : "Focus shapes imagined feelings, not character stats or recorded events.";
  const focus = effectiveStoryFocus(storytellingPreferences.focus, viewpoint?.companion != null);
  const directorState = creativeStoryDirector.snapshot;
  if (!creativeStoryController.snapshot.busy && !directorState.generating && directorState.ready === null
    && creativeStoryController.snapshot.focus !== focus) {
    creativeStoryController.sync({ job, mode: state.scene.mode, viewpoint, eligible: false });
    creativeStoryController.setFocus(focus);
  }
  creativeStoryDirector.sync({
    campaignId: state.campaignId,
    candidate: job === null ? null : { job, mode: state.scene.mode, viewpoint },
    active: !context.documentHidden && context.view === "watch"
      && !startupHold && !paused && !narrativeReading && !stepping && !runtimeRecovering && pendingInteractions === 0
      && !["saving", "reloading"].includes(document.documentElement.dataset.updateStatus ?? "")
      && document.querySelector("dialog[open]") === null,
  });
}

function requestNarrativeCheck(): void {
  if (narrativeCheckQueued) return;
  narrativeCheckQueued = true;
  queueMicrotask(() => {
    narrativeCheckQueued = false;
    syncCreativeStoryPresentation();
    tryPresentNarrativeIntermission();
  });
}

function narrativePresentationAvailable(allowGameMenu = false): boolean {
  if (startupHold || narrativeReading || stepping || pendingInteractions > 0
    || runtimeRecovering || document.hidden || presentationSuspended
    || activeView !== "watch" || presentationBusy || catchUpAfterPresentation
    || cutawayController.queue.active !== null || cutawayController.queue.pending !== null
    || state.scene.mode === "battle" || elements.stage.dataset.encounterEngine !== undefined
    || state.depth.repartee.active !== null || reparteeScene !== null || bellScene !== null
    || state.depth.usefulReply !== null && state.depth.usefulReply.reply === null
    || state.depth.roomChallenge !== null && state.depth.roomChallenge.result === null
    || selectCompanionReunion(state.depth) !== null
    || selectCompanionCredit(state.depth) !== null
    || state.depth.bellExpedition !== null && state.depth.bellExpedition.completion === null
    || ["saving", "reloading"].includes(document.documentElement.dataset.updateStatus ?? "")
    || document.querySelector(allowGameMenu ? "dialog[open]:not(#game-menu)" : "dialog[open]") !== null) return false;
  const source = state.chronicle.at(-1);
  return source === undefined || elements.stage.dataset.cutawayFallbackEvent !== source.id;
}

function tryPresentNarrativeIntermission(): void {
  if (paused || !narrativePresentationAvailable()
    || Date.now() < lastNarrativeClosedAtMs + storytellingCadenceMs(storytellingPreferences.rhythm)) return;
  const ready = creativeStoryDirector.snapshot.ready;
  if (ready === null || ready.campaignId !== state.campaignId || state.tick <= ready.sourceTick) return;
  const passage = creativeStoryDirector.takeReady();
  if (passage === null) return;
  showNarrativePassage(passage, false);
}

function showNarrativePassage(passage: HeldNarrative, replay: boolean): void {
  narrativeReplay = replay;
  narrativeReading = true;
  elements.app.dataset.narrativeIntermission = "true";
  syncPresentationPaused();
  try {
    narrativeIntermission.show(passage, { held: replay });
    if (!narrativeIntermission.active) releaseNarrativeReading();
    else if (!replay) {
      lastPresentedStory.remember(passage);
      narrativeJournal.markPresented(passage);
      narrativeJournalView.render();
    }
  } catch {
    narrativeIntermission.close();
    releaseNarrativeReading();
  }
  syncNarratorPresentationContext();
}

function releaseNarrativeReading(): void {
  if (!narrativeReading) return;
  const restoreMenuFocus = narrativeReplay;
  narrativeReplay = false;
  narrativeReading = false;
  elements.app.dataset.narrativeIntermission = "false";
  lastAdvanceAtMs = Date.now();
  lastNarrativeClosedAtMs = Date.now();
  syncPresentationPaused();
  requestNarrativeCheck();
  if (restoreMenuFocus) window.requestAnimationFrame(() => {
    if (!document.hidden && !presentationSuspended && !startupHold && activeView === "watch"
      && !narrativeReading && document.querySelector("dialog[open]") === null
      && !["saving", "reloading"].includes(document.documentElement.dataset.updateStatus ?? "")) {
      focusVisibleMenuControl();
    }
  });
  if (catchUpAfterPresentation) void resumeDeferredCatchUp();
}

function cancelNarrativeIntermission(): void {
  creativeStoryDirector.invalidate();
  narrativeIntermission.close();
}

function narratorPresentationContext() {
  const capability = detectNarratorCapability();
  return {
    documentHidden: document.hidden || presentationSuspended,
    ecoMode: capability.execution === "none" || capability.budget !== "standard",
    cutawayActive: narrativeReading || presentationBusy || elements.stage.dataset.cutawayFallback !== undefined,
    view: activeView,
    battleActive: state.scene.mode === "battle"
      || elements.stage.dataset.encounterEngine !== undefined,
  } as const;
}

function retainFactualStoryBeatTransition(
  before: Readonly<WorldState>,
  after: Readonly<WorldState>,
): void {
  factualStoryBeatOpportunity = retainFactualStoryBeatOpportunityV1(
    factualStoryBeatOpportunity,
    projectFactualStoryBeatTransitionV2(before, after),
    { campaignId: after.campaignId, currentTick: after.tick },
  );
}

function clearFactualStoryBeatOpportunity(): void {
  factualStoryBeatOpportunity = null;
}

function currentFactualStoryBeatOpportunity(): FactualStoryBeatOpportunityViewV1 | null {
  const opportunity = projectFactualStoryBeatOpportunityV1(
    factualStoryBeatOpportunity,
    { campaignId: state.campaignId, currentTick: state.tick },
  );
  if (opportunity === null) clearFactualStoryBeatOpportunity();
  return opportunity;
}

function syncStoryBeatPresentation(
  narratorSnapshot = localNarratorController.snapshot,
  context = narratorPresentationContext(),
): void {
  syncCreativeStoryPresentation(context);
  if (!narratorSnapshot.enabled) clearFactualStoryBeatOpportunity();
  const opportunity = currentFactualStoryBeatOpportunity();
  const job = opportunity?.job ?? null;
  const chromeMakesControlReachable = activeView === "adventure";
  const eligible = narratorSnapshot.enabled
    && narratorSnapshot.suppression === null
    && !context.documentHidden
    && !context.ecoMode
    && !context.cutawayActive
    && context.view === "adventure"
    && !context.battleActive
    && chromeMakesControlReachable;
  storyBeatController.sync({
    enabled: narratorSnapshot.enabled,
    eligible,
    campaignId: state.campaignId,
    job,
    opportunityTiming: opportunity?.timing ?? null,
  });
}

function syncNarratorPresentationContext(): void {
  const context = narratorPresentationContext();
  localNarratorController.setPresentationContext(context);
  syncStoryBeatPresentation(localNarratorController.snapshot, context);
}

function presentNarratorScene(): void {
  const context = narratorPresentationContext();
  localNarratorController.setPresentationContext(context);
  syncStoryBeatPresentation(localNarratorController.snapshot, context);
  // Adventure hosts deliberate fact-bound writing, not automatic scene offers.
  // The legacy present(job, false) adapter means "cutaway", so do not use it
  // merely to hide this line while the manual writing control remains usable.
  if (activeView === "adventure") {
    localNarratorController.presentScene(null);
    return;
  }
  const source = state.chronicle.at(-1);
  const job = projectSceneNarratorJob(
    state.campaignId,
    state.scene,
    source,
    source?.id,
  );
  if (job === null) {
    localNarratorController.present(null, false);
    return;
  }
  if (staticCutawayNarratorFingerprint !== null
    && staticCutawayNarratorFingerprint !== job.sourceFingerprint) {
    staticCutawayNarratorFingerprint = null;
  }
  if (elements.stage.dataset.cutawayFallbackEvent === source?.id) {
    staticCutawayNarratorFingerprint = job.sourceFingerprint;
  }
  const eligible = !context.documentHidden
    && !context.ecoMode
    && !context.cutawayActive
    && context.view === "watch"
    && !context.battleActive
    && staticCutawayNarratorFingerprint !== job.sourceFingerprint;
  localNarratorController.present(job, eligible);
}

let lastViewNavigationGeometry = "";
let lastViewNavigationView: InspectionView | null = null;

function revealViewNavigation(force = false): void {
  const toolbar = elements.viewToolbar;
  if (toolbar.clientWidth === 0 || toolbar.getClientRects().length === 0) {
    // Focus and compact cutaways hide the row. Reveal the destination when it
    // returns, even if its dimensions are unchanged.
    lastViewNavigationGeometry = "";
    lastViewNavigationView = null;
    return;
  }
  // Resizing must also keep an unactivated, keyboard-focused tab reachable.
  const focusedButton = lastViewNavigationView === activeView
    ? viewButtons.find((item) => item === document.activeElement) : undefined;
  const button = focusedButton
    ?? viewButtons.find((item) => item.dataset.view === activeView);
  if (button === undefined) return;
  const geometry = [activeView, button.dataset.view, toolbar.clientWidth,
    toolbar.scrollWidth, button.offsetLeft, button.offsetWidth].join(":");
  if (!force && geometry === lastViewNavigationGeometry) return;
  lastViewNavigationGeometry = geometry;
  lastViewNavigationView = activeView;
  // Deliberate scrolling is not part of the signature: never pull the row back
  // during normal play. Scroll only this container, not the document or reader.
  const bounds = toolbar.getBoundingClientRect();
  const target = button.getBoundingClientRect();
  const left = bounds.left + toolbar.clientLeft + 4;
  const right = bounds.left + toolbar.clientLeft + toolbar.clientWidth - 4;
  const delta = target.width > right - left || target.left < left - 1
    ? target.left - left : target.right > right + 1 ? target.right - right : 0;
  if (Math.abs(delta) > 1) toolbar.scrollLeft += delta;
}

function syncInspectionViewportGeometry(): void {
  revealViewNavigation();
  const appTop = elements.app.getBoundingClientRect().top;
  const header = elements.topbar.getBoundingClientRect();
  const headerBottom = header.width > 0 && header.height > 0 ? Math.ceil(header.bottom - appTop) : 0;
  if (header.width > 0 && header.height > 0) {
    const navigationTop = `${headerBottom}px`;
    if (elements.app.style.getPropertyValue("--view-toolbar-top") !== navigationTop) {
      elements.app.style.setProperty("--view-toolbar-top", navigationTop);
    }
  }
  if (elements.inspectionScreen.hidden && elements.mapInspector.hidden) return;
  // Both offsets share the same measurement. Reading the toolbar's position
  // immediately after changing its custom property can retain the prior top.
  const navigation = elements.viewToolbar.getBoundingClientRect();
  const top = `${Math.ceil(headerBottom + (navigation.width > 0 ? navigation.height : 0))}px`;
  if (elements.app.style.getPropertyValue("--inspection-viewport-top") !== top) {
    elements.app.style.setProperty("--inspection-viewport-top", top);
  }
}

function syncWatchStageGeometry(): void {
  const ribbon = elements.stageFocusRibbon;
  const bounds = ribbon.getBoundingClientRect();
  const appBounds = elements.app.getBoundingClientRect();
  const showing = !ribbon.hidden && bounds.height > 0 && getComputedStyle(ribbon).display !== "none";
  const reserved = showing ? Math.ceil(appBounds.bottom - bounds.top + 8) : 0;
  const header = elements.topbar.getBoundingClientRect();
  const headerBottom = header.width > 0 && header.height > 0 ? Math.ceil(header.bottom - appBounds.top) : null;
  let chromeBottom = 0;
  if (showing) for (const chrome of [elements.topbar, elements.viewToolbar, elements.stageFocusControls]) {
    const chromeBounds = chrome.getBoundingClientRect();
    if (chromeBounds.width <= 0 || chromeBounds.height <= 0) continue;
    // Match --view-toolbar-top's header measurement. A position read can still
    // contain the old top inside the resize callback, even after the CSS write.
    const bottom = chrome === elements.viewToolbar && headerBottom !== null
      ? headerBottom + chromeBounds.height : chromeBounds.bottom - appBounds.top;
    chromeBottom = Math.max(chromeBottom, bottom);
  }
  for (const [property, value] of [["--watch-ribbon-height", reserved], ["--watch-stage-top", Math.ceil(chromeBottom + (showing ? 8 : 0))]] as const) {
    const cssValue = `${value}px`;
    if (elements.app.style.getPropertyValue(property) !== cssValue) elements.app.style.setProperty(property, cssValue);
  }
}

function syncStageChromePresentation(announce: boolean): void {
  const focused = stageChromeMode === "focus" && activeView === "watch";
  elements.app.dataset.chromeMode = focused ? "focus" : "panels";
  elements.app.dataset.watchLayout = "portraits";
  elements.app.dataset.chromePreference = explicitStageChromePreference ? "explicit" : "responsive";
  elements.stageFocusButton.setAttribute("aria-pressed", String(focused));
  const focusControlHost = focused ? elements.stageFocusControls : elements.topbarControls;
  if (elements.stageFocusButton.parentElement !== focusControlHost) focusControlHost.prepend(elements.stageFocusButton);
  elements.stageFocusButton.title = focused ? "Restore navigation" : "Hide navigation and focus on the adventure";
  elements.stageFocusControls.hidden = !focused;
  elements.stageFocusRibbon.hidden = activeView !== "watch";
  presentSpectatorInbox();
  if (announce) {
    elements.viewAnnouncement.textContent = focused
      ? "Stage Focus. The party and current scene remain visible. Focus restores navigation; Character opens the Adventure tab."
      : "Navigation restored. The party and current scene remain visible; Character opens the Adventure tab.";
  }
  syncStoryBeatPresentation();
  syncInspectionViewportGeometry();
  syncWatchStageGeometry();
  renderer.refreshLayout();
}

function focusWatchControl(): void {
  const focused = stageChromeMode === "focus" && activeView === "watch";
  if (focused) {
    elements.stageMenuButton.focus();
    return;
  }
  const watchButton = viewButtons.find((button) => button.dataset.view === "watch");
  if (watchButton !== undefined && watchButton.getClientRects().length > 0) watchButton.focus();
  else elements.gameMenuButton.focus();
}

function focusIsInsidePanelsChrome(): boolean {
  const focused = document.activeElement;
  return focused instanceof Element
    && focused.closest("#topbar, #view-toolbar, #mini-map, #hero-hud, #chronicle") !== null;
}


let narratorDialogReturnFocus: HTMLElement | null = null;
let menuReturnFocus: HTMLElement | null = null;

function renderLastStoryControl(): void {
  const reason = lastPresentedStory.get(state.campaignId) === null ? "Let a story appear first."
    : activeView !== "watch" ? "Return to Watch to read the last story."
      : !narrativePresentationAvailable(true) ? "Available between scenes."
        : null;
  elements.lastStoryButton.disabled = reason !== null;
  elements.lastStoryButton.title = reason ?? "Read the last story again · no new writing.";
}

function focusVisibleMenuControl(): void {
  const control = [elements.stageMenuButton, elements.gameMenuButton]
    .find((button) => button.getClientRects().length > 0 && !button.disabled);
  control?.focus();
}

function revisitLastStory(): void {
  renderLastStoryControl();
  if (elements.lastStoryButton.disabled) return;
  const passage = lastPresentedStory.get(state.campaignId);
  if (passage === null) return;
  // Close and claim reading ownership in the same task: the menu's queued check
  // cannot consume a different finished story between these two actions.
  closeGameMenu(false);
  showNarrativePassage(passage, true);
}

function openGameMenu(): void {
  if (startupHold || elements.gameMenu.open || elements.playStartDialog.open) return;
  presentDungeonPerspectivePreference();
  menuReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  elements.gameMenu.showModal();
  renderLastStoryControl();
  elements.gameMenuButton.setAttribute("aria-expanded", "true");
  elements.stageMenuButton.setAttribute("aria-expanded", "true");
  elements.gameMenuClose.focus();
}

function closeGameMenu(restoreFocus = true): void {
  if (!elements.gameMenu.open) return;
  elements.gameMenu.close();
  elements.gameMenuButton.setAttribute("aria-expanded", "false");
  elements.stageMenuButton.setAttribute("aria-expanded", "false");
  if (restoreFocus) {
    const target = menuReturnFocus;
    window.requestAnimationFrame(() => {
      if (target !== null && target.getClientRects().length > 0) target.focus();
      else focusWatchControl();
    });
  }
  requestNarrativeCheck();
}

function openNarratorDialog(): void {
  if (elements.narratorDialog.open) return;
  narratorDialogReturnFocus = elements.gameMenu.open ? menuReturnFocus
    : stageChromeMode === "focus" && activeView === "watch" ? elements.stageMenuButton : elements.gameMenuButton;
  closeGameMenu(false);
  elements.narratorDialog.showModal();
  void creativeStoryController.checkCache();
  elements.narratorClose.focus();
}

function closeNarratorDialog(restoreFocus = true): void {
  if (!elements.narratorDialog.open) return;
  if (localNarratorController.snapshot.downloading) {
    localNarratorController.cancelInstall();
  }
  elements.narratorDialog.close();
  requestNarrativeCheck();
  if (!restoreFocus) return;
  const returnFocus = narratorDialogReturnFocus;
  narratorDialogReturnFocus = null;
  window.requestAnimationFrame(() => {
    if (returnFocus !== null && returnFocus.getClientRects().length > 0) {
      returnFocus.focus();
      return;
    }
    focusWatchControl();
  });
}


function setStageChromeMode(mode: StageChromeMode, persistOverride: boolean, announce = true): void {
  if (mode === "focus" && activeView !== "watch") setActiveView("watch", false, true);
  stageChromeMode = mode;
  if (mode === "focus") {
    spectatorRecapOpen = false;
    presentSpectatorInbox();
  }
  if (persistOverride) {
    explicitStageChromePreference = true;
    writeStageChromePreference(mode);
  }
  syncStageChromePresentation(announce);
  if (persistOverride && mode === "focus") focusWatchControl();
}

{
  const resolved = resolveStageChromeMode(readStageChromePreference(), compactStageFocusMedia.matches);
  stageChromeMode = resolved.mode;
  explicitStageChromePreference = resolved.explicit;
  syncStageChromePresentation(false);
}

const inspectionChromeObserver = new ResizeObserver(() => {
  syncInspectionViewportGeometry();
  // The toolbar's top can change without resizing its border box. Recompute
  // the Watch reservation after updating that offset, not only on size events.
  syncWatchStageGeometry();
});
for (const chrome of [elements.topbar, elements.viewToolbar, elements.app]) {
  inspectionChromeObserver.observe(chrome, { box: "border-box" });
}
const watchChromeObserver = new ResizeObserver(syncWatchStageGeometry);
for (const chrome of [elements.stageFocusRibbon, elements.topbar, elements.viewToolbar, elements.stageFocusControls, elements.app]) {
  watchChromeObserver.observe(chrome, { box: "border-box" });
}

compactStageFocusMedia.addEventListener("change", (event) => {
  const transferFromCompactControl = !event.matches
    && document.activeElement instanceof Element
    && elements.stageFocusControls.contains(document.activeElement);
  if (explicitStageChromePreference) {
    return;
  }
  const transferFocus = event.matches && activeView === "watch" && focusIsInsidePanelsChrome();
  stageChromeMode = resolveStageChromeMode(null, event.matches).mode;
  syncStageChromePresentation(true);
  if (transferFocus) focusWatchControl();
  if (transferFromCompactControl) window.requestAnimationFrame(focusWatchControl);
});

function isInspectionView(value: string | undefined): value is InspectionView {
  return value !== undefined && inspectionViews.some((view) => view === value);
}

type ScreenInspectionView = Exclude<InspectionView, "watch" | "map">;

const inspectionCopy = {
  adventure: {
    title: "Adventure",
    subtitle: "Your character, current purpose, and the scene unfolding now.",
  },
  inventory: {
    title: "Inventory",
    subtitle: "Every carried stack, modifier, rarity, quantity, and equipped slot.",
  },
  journal: {
    title: "Journal",
    subtitle: "Quests and relationships, recorded actions, and imagined stories.",
  },
  codex: {
    title: "Monster Codex",
    subtitle: "Encountered species, studied victories, and only verified secret techniques.",
  },
  spellbook: {
    title: "Spellbook & Mastery",
    subtitle: "Every owned spell, technique, monster secret, and exact current-tier mastery band.",
  },
  hall: {
    title: "Hall of Champions",
    subtitle: "Immutable Level 1000 records saved by this browser; every Eternal adventure continues.",
  },
} satisfies Record<ScreenInspectionView, { title: string; subtitle: string }>;

function isScreenInspectionView(view: InspectionView): view is ScreenInspectionView {
  return view !== "watch" && view !== "map";
}

function modifierLabel(name: string, value: number): string {
  const spaced = name.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return `${value >= 0 ? "+" : ""}${value} ${spaced}`;
}

interface HeroActivityHost {
  root: HTMLElement;
  kicker: HTMLElement;
  label: HTMLElement;
  subject: HTMLElement;
  detail: HTMLElement;
  scene: HTMLElement;
  notice: HTMLElement;
}

function activityHost(root: HTMLElement): HeroActivityHost {
  const field = (name: string): HTMLElement => {
    const element = root.querySelector<HTMLElement>(`[data-activity-field="${name}"]`);
    if (element === null) throw new Error(`Hero activity host is missing ${name}`);
    return element;
  };
  return {
    root,
    kicker: field("kicker"),
    label: field("label"),
    subject: field("subject"),
    detail: field("detail"),
    scene: field("scene"),
    notice: field("notice"),
  };
}

const heroActivityHosts = {
  map: activityHost(elements.mapHeroActivity),
  screen: activityHost(elements.screenHeroActivity),
};

function cssColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

function presentHeroActivityHost(host: HeroActivityHost, activity: HeroInspectionActivity): void {
  host.root.dataset.view = activity.view;
  host.root.dataset.activityTick = String(activity.tick);
  host.root.dataset.liveSceneMode = activity.sceneMode;
  host.root.dataset.attention = activity.attention;
  host.root.dataset.pose = activity.pose;
  host.root.dataset.prop = activity.prop;
  if (activity.subjectId === null) delete host.root.dataset.subjectId;
  else host.root.dataset.subjectId = activity.subjectId;
  host.kicker.textContent = `Storybook margin · T${activity.tick}`;
  host.label.textContent = `${activity.heroName} · ${activity.activityLabel}`;
  host.subject.textContent = activity.subjectLabel;
  host.detail.textContent = activity.subjectDetail;
  host.scene.textContent = `${activity.location} · ${activity.sceneHeadline} · ${activity.sceneAction}`;
  host.notice.hidden = activity.liveNotice === null;
  host.notice.textContent = activity.liveNotice ?? "";
  host.root.style.setProperty("--hero-skin-color", cssColor(activity.identity.skin));
  host.root.style.setProperty("--hero-hair-color", cssColor(activity.identity.hair));
  host.root.style.setProperty("--hero-tunic-color", cssColor(activity.identity.tunic));
  host.root.style.setProperty("--hero-cloak-color", cssColor(activity.identity.cloak));
  host.root.style.setProperty("--hero-belt-color", cssColor(activity.identity.belt));
  for (const [slot, gear] of Object.entries(activity.appearance)) {
    host.root.dataset[`${slot}Silhouette`] = gear?.silhouette ?? "none";
    host.root.style.setProperty(`--hero-${slot}-color`, gear === null ? "#52606d" : cssColor(gear.color));
    host.root.style.setProperty(`--hero-${slot}-accent`, gear === null ? "#2b3540" : cssColor(gear.accent));
  }
}

function presentHeroInspectionActivity(): void {
  heroActivityHosts.screen.root.hidden = activeView === "adventure";
  if (activeView === "watch" || activeView === "adventure") return;
  const preferredSubjectId = activityFocusByView[activeView];
  const activity = projectViewHero(state, activeView, preferredSubjectId, { paused: isPresentationPaused() });
  if (activity.subjectId === null) delete activityFocusByView[activeView];
  else activityFocusByView[activeView] = activity.subjectId;
  presentHeroActivityHost(activeView === "map" ? heroActivityHosts.map : heroActivityHosts.screen, activity);
}

function isPresentationPaused(): boolean {
  return startupHold || paused || presentationSuspended || narrativeReading;
}

function syncReparteePresentation(): void {
  const visible = activeView === "watch" && !presentationBusy && !narrativeReading;
  if (visible !== reparteeVisible) {
    reparteeVisible = visible;
    reparteeView.setVisible(visible);
    bellView.setVisible(visible);
    renderer.refreshLayout();
  }
  reparteeDwell = updateReparteeDwell(reparteeDwell,
    reparteeScene?.commandId ?? bellScene?.commandId ?? null,
    performance.now(), visible && !isPresentationPaused() && !document.hidden);
  if (reparteeScene === null) delete elements.app.dataset.reparteeDwellMs;
  else elements.app.dataset.reparteeDwellMs = String(Math.floor(reparteeDwell.elapsedMs));
  if (bellScene === null) delete elements.app.dataset.bellDwellMs;
  else elements.app.dataset.bellDwellMs = String(Math.floor(reparteeDwell.elapsedMs));
}

function syncPresentationPaused(): void {
  const presentationPaused = isPresentationPaused();
  const now = Date.now();
  if (presentationBusy && presentationPaused && cutawayPausedAtMs === null) {
    cutawayPausedAtMs = now;
  } else if (!presentationPaused && cutawayPausedAtMs !== null) {
    cutawayStartedAtMs += now - cutawayPausedAtMs;
    cutawayPausedAtMs = null;
  }
  elements.app.dataset.presentationPaused = String(presentationPaused);
  renderer.setPaused(presentationPaused);
  syncReparteePresentation();
  presentHeroInspectionActivity();
}

const trapCutawayPhaseOrder: readonly TrapCutawayPhase[] = [
  "command",
  "inspection",
  "attempt",
  "reveal",
  "consequence",
  "final",
];

function trapCutawayPhaseIndex(phase: TrapCutawayPhase): number {
  if (phase === "static" || phase === "settled") return trapCutawayPhaseOrder.length - 1;
  return trapCutawayPhaseOrder.indexOf(phase);
}

function presentTrapCutawayPhase(phase: TrapCutawayPhase): void {
  const currentIndex = trapCutawayPhaseIndex(phase);
  elements.trapCutaway.dataset.phase = phase;
  for (const step of trapCutawaySteps) {
    const stepPhase = step.dataset.cutawayStep as TrapCutawayPhase | undefined;
    const stepIndex = stepPhase === undefined ? -1 : trapCutawayPhaseIndex(stepPhase);
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === currentIndex || (currentIndex >= 5 && stepPhase === "consequence"));
  }
}

function presentTrapCutawayPacket(packet: TrapResolutionPacket, staging: TrapCutawayStaging): void {
  const outcome = trapCutawayOutcome(packet);
  const mechanism = dungeonTrapKindLabel(packet.trapKind);
  const tool = packet.schemaVersion === 1 ? null : packet.tool;
  elements.trapCutaway.hidden = false;
  elements.farewellCutaway.hidden = true;
  elements.levelUpCutaway.hidden = true;
  elements.weaponMemoryCutaway.hidden = true;
  elements.battleSpoilsCutaway.hidden = true;
  elements.townItineraryCutaway.hidden = true;
  elements.trapCutaway.dataset.active = "true";
  elements.trapCutaway.dataset.eventId = packet.eventId;
  elements.trapCutaway.dataset.outcome = outcome;
  elements.trapCutaway.dataset.stage = packet.stage;
  elements.trapCutaway.dataset.shot = staging.shot;
  elements.trapCutaway.dataset.flavor = staging.flavor;
  elements.trapCutawayTitle.textContent = `${state.depth.hero.name} · ${mechanism}`;
  elements.trapCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.trapCutawayCommand.textContent = packet.commandType === "enter-dungeon"
    ? "Cross the dungeon threshold"
    : packet.stage === "detect"
      ? "Enter the marked chamber"
      : "Disarm the detected mechanism";
  elements.trapCutawayInspection.textContent = `${mechanism} · ${packet.phaseBefore}`;
  elements.trapCutawayCheck.textContent = `${packet.attribute} · ${packet.skill} + ${packet.roll}${tool !== null ? " + 2 KIT" : ""} = ${packet.total} vs ${packet.difficulty}`;
  elements.trapCutawayResult.textContent = `${outcome.toUpperCase()} · ${packet.phaseBefore} → ${packet.phaseAfter}`;
  elements.trapCutawayConsequence.textContent = (packet.schemaVersion === 3
    ? `MP ${packet.manaBefore} → ${packet.manaAfter} (−${packet.manaLost}) · HP ${packet.healthBefore} → ${packet.healthAfter} (unchanged)`
    : `HP ${packet.healthBefore} → ${packet.healthAfter}${packet.damage > 0 ? ` (−${packet.damage})` : " (no damage)"}`)
    + (tool !== null ? " · Disarming Kit 1 → 0 (consumed)" : "");
  elements.trapCutawayProgress.textContent = `${packet.completedExit ? "Exit reached" : "Maze continues"} · Cross-maze quest ${packet.crossMazeDelta > 0 ? `+${packet.crossMazeDelta}` : "unchanged"} · the viewer cannot alter this resolved result.`;
  elements.trapCutawayOutcome.hidden = false;
  elements.trapCutawayOutcome.disabled = false;
  presentTrapCutawayPhase(fastMode ? "static" : "command");
}

const farewellCutawayPhaseOrder: readonly FarewellCutawayPhase[] = [
  "promise",
  "journey",
  "arrival",
  "farewell",
  "legacy",
  "final",
];

function farewellCutawayPhaseIndex(phase: FarewellCutawayPhase): number {
  if (phase === "static" || phase === "settled") return farewellCutawayPhaseOrder.length - 1;
  return farewellCutawayPhaseOrder.indexOf(phase);
}

function presentFarewellCutawayPhase(phase: FarewellCutawayPhase): void {
  const currentIndex = farewellCutawayPhaseIndex(phase);
  elements.farewellCutaway.dataset.phase = phase;
  for (const step of farewellCutawaySteps) {
    const stepPhase = step.dataset.farewellStep as FarewellCutawayPhase | undefined;
    const stepIndex = stepPhase === undefined ? -1 : farewellCutawayPhaseIndex(stepPhase);
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === currentIndex || (currentIndex >= 5 && stepPhase === "legacy"));
  }
}

function presentFarewellCutawayPacket(packet: CompanionFarewellPacket): void {
  elements.trapCutaway.hidden = true;
  elements.farewellCutaway.hidden = false;
  elements.levelUpCutaway.hidden = true;
  elements.weaponMemoryCutaway.hidden = true;
  elements.battleSpoilsCutaway.hidden = true;
  elements.townItineraryCutaway.hidden = true;
  elements.farewellCutaway.dataset.active = "true";
  elements.farewellCutaway.dataset.eventId = packet.eventId;
  elements.farewellCutaway.dataset.outcome = packet.outcome;
  elements.farewellCutaway.dataset.companionId = packet.companionId;
  elements.farewellCutaway.dataset.profession = packet.profession;
  elements.farewellCutawayTitle.textContent = `${packet.companionName} · ${packet.profession}`;
  elements.farewellCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.farewellCutawayPromise.textContent = `${packet.originName} → ${packet.destinationName}`;
  elements.farewellCutawayJourney.textContent = `${packet.victories === 0 ? "Quiet road" : `${packet.victories} victories`} · bond ${packet.bond}`;
  elements.farewellCutawayArrival.textContent = packet.outcome === "injured"
    ? `${packet.injury} · HP ${packet.health}/${packet.maxHealth}`
    : `Promise fulfilled · HP ${packet.health}/${packet.maxHealth}`;
  elements.farewellCutawayDeparture.textContent = `${packet.companionName} leaves with ${packet.profession} tools`;
  elements.farewellCutawayLegacy.textContent = `Former companion recorded · T${packet.departureTick}`;
  elements.farewellCutawayProgress.textContent = "No item changes hands · this resolved departure cannot be altered by the viewer.";
  elements.farewellCutawayOutcome.hidden = false;
  elements.farewellCutawayOutcome.disabled = false;
  presentFarewellCutawayPhase(fastMode ? "static" : "promise");
}

const heroLevelUpCutawayPhaseOrder: readonly HeroLevelUpCutawayPhase[] = [
  "source",
  "threshold",
  "ascent",
  "mechanics",
  "tableau",
  "final",
];

function heroLevelUpPhaseIndex(phase: HeroLevelUpCutawayPhase): number {
  if (phase === "static" || phase === "settled") return heroLevelUpCutawayPhaseOrder.length - 1;
  return heroLevelUpCutawayPhaseOrder.indexOf(phase);
}

function presentHeroLevelUpPhase(phase: HeroLevelUpCutawayPhase): void {
  const currentIndex = heroLevelUpPhaseIndex(phase);
  elements.levelUpCutaway.dataset.phase = phase;
  for (const step of levelUpCutawaySteps) {
    const stepPhase = step.dataset.levelStep as HeroLevelUpCutawayPhase | undefined;
    const stepIndex = stepPhase === undefined ? -1 : heroLevelUpPhaseIndex(stepPhase);
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === currentIndex || (currentIndex >= 5 && stepPhase === "tableau"));
  }
}

const heroGrowthAllocationPhaseOrder: readonly HeroGrowthAllocationCutawayPhase[] = [
  "deed",
  "options",
  "decision",
  "allocation",
  "mechanics",
  "resources",
  "final",
];

function heroGrowthAllocationPhaseIndex(phase: HeroGrowthAllocationCutawayPhase): number {
  if (phase === "static" || phase === "settled") return heroGrowthAllocationPhaseOrder.length - 1;
  return heroGrowthAllocationPhaseOrder.indexOf(phase);
}

function presentHeroGrowthAllocationPhase(phase: HeroGrowthAllocationCutawayPhase): void {
  const currentIndex = heroGrowthAllocationPhaseIndex(phase);
  const stepPhase: Readonly<Record<string, number>> = {
    source: 0,
    threshold: 0,
    ascent: 3,
    selection: Math.min(3, currentIndex),
    mechanics: 4,
    tableau: 5,
  };
  elements.levelUpCutaway.dataset.phase = phase;
  for (const step of levelUpCutawaySteps) {
    const index = stepPhase[step.dataset.levelStep ?? ""] ?? -1;
    step.dataset.reached = String(index >= 0 && index <= currentIndex);
    step.dataset.current = String(index === currentIndex || (currentIndex >= 6 && step.dataset.levelStep === "tableau"));
  }
}

function derivedDeltaLabel(delta: HeroLevelUpDerivedDelta): string {
  const labels = [
    ["Power", delta.power],
    ["Armor", delta.armor],
    ["Initiative", delta.initiative],
    ["Max HP", delta.maxHealth],
    ["Max MP", delta.maxMana],
  ] as const;
  const changed = labels.filter(([, value]) => value !== 0);
  return changed.length === 0
    ? "none"
    : changed.map(([label, value]) => `${label} ${value > 0 ? "+" : ""}${value}`).join(" · ");
}

const growthAttributeLabels = {
  strength: "STR",
  agility: "AGI",
  vitality: "VIT",
  intellect: "INT",
  spirit: "SPI",
  luck: "LCK",
} as const;

function growthAttributeChanges(
  before: HeroGrowthAllocationPacketV1["selections"][number]["attributesBefore"],
  after: HeroGrowthAllocationPacketV1["selections"][number]["attributesAfter"],
  includeUnchanged: boolean,
): string {
  const facts = Object.entries(growthAttributeLabels).flatMap(([attribute, label]) => {
    const key = attribute as keyof typeof growthAttributeLabels;
    const from = before[key];
    const to = after[key];
    return from === to && !includeUnchanged ? [] : [`${label} ${from}→${to}${from === to ? " STAYS" : ""}`];
  });
  return facts.join(" · ");
}

function growthReasonLabel(reason: HeroGrowthAllocationPacketV1["selections"][number]["selectedCandidate"]["reasonCodes"][number]): string {
  switch (reason) {
    case "combat-pressure": return "combat pressure";
    case "roadcraft": return "roadcraft";
    case "disciplined-study": return "disciplined study";
    case "class-affinity": return "class affinity";
    case "personal-value": return "personal value";
    case "underdeveloped-path": return "underdeveloped path";
    case "steady-practice": return "steady practice";
  }
}

function presentHeroGrowthAllocationPacket(packet: HeroGrowthAllocationPacketV1): void {
  const first = packet.selections[0];
  const last = packet.selections.at(-1);
  if (first === undefined || last === undefined) throw new Error("Growth allocation packet has no persisted selection");
  elements.trapCutaway.hidden = true;
  elements.farewellCutaway.hidden = true;
  elements.levelUpCutaway.hidden = false;
  elements.weaponMemoryCutaway.hidden = true;
  elements.battleSpoilsCutaway.hidden = true;
  elements.townItineraryCutaway.hidden = true;
  elements.levelUpCutaway.dataset.active = "true";
  elements.levelUpCutaway.dataset.montageKind = "growth";
  elements.levelUpCutaway.dataset.eventId = packet.eventId;
  elements.levelUpCutaway.dataset.emphasis = "milestone";
  elements.levelUpCutaway.dataset.progressionBand = "adventurer";
  clearChampionSealDom();
  elements.levelUpCutawayKicker.textContent = packet.selectionCount === 1
    ? "Autonomous Turning Point"
    : `${packet.selectionCount} autonomous Turning Points`;
  elements.levelUpCutawayTitle.textContent = packet.selectionCount === 1
    ? `${packet.heroName} · Level ${first.record.checkpointLevel} · Turning Point ${first.turningPointOrdinal} of 3`
    : `${packet.heroName} · Levels ${packet.selections.map((selection) => selection.record.checkpointLevel).join(" · ")} · Turning Points ${first.turningPointOrdinal}–${last.turningPointOrdinal} of 3`;
  elements.levelUpCutawayEvent.textContent = `SETTLED T${packet.applicationTick} · RECEIPT ${packet.applicationId.slice(-8).toUpperCase()}`;
  elements.levelUpCutawayOutcome.textContent = "Show settled result";
  elements.levelUpCutawaySource.textContent = packet.applicationTiming === "immediate"
    ? `${packet.sourceHeadline} · crossed and chosen now`
    : `${packet.sourceHeadline} · held at T${first.record.crossedTick} · settled after the encounter`;
  elements.levelUpCutawayThreshold.textContent = packet.selections.map((selection) =>
    `L${selection.record.levelBefore}→${selection.record.levelAfter} · Level ${selection.record.checkpointLevel} · Turning Point ${selection.turningPointOrdinal} of 3 · ${selection.settlementTiming === "immediate" ? `T${selection.record.tick}` : `crossed T${selection.record.crossedTick} / settled T${selection.record.tick}`}`
  ).join(" · ");
  elements.levelUpCutawayLevel.textContent = packet.levelTransition === null
    ? `LEVEL ${first.record.appliedLevel} · DEFERRED COMMITMENT SETTLED`
    : `LEVEL ${packet.levelTransition.levelBefore} → ${packet.levelTransition.levelAfter}`;
  elements.levelUpCutawaySelectionStep.hidden = false;
  elements.levelUpCutawaySelection.textContent = packet.selections.map((selection) =>
    `Level ${selection.record.checkpointLevel} · Turning Point ${selection.turningPointOrdinal} of 3: ${selection.selectedCandidate.label} became the path forward. ${selection.record.rationale}`
  ).join(" ");
  elements.levelUpCutawayCandidates.replaceChildren(...packet.selections.flatMap((selection) =>
    selection.record.candidates.map((candidate) => {
      const item = document.createElement("li");
      const chosen = candidate.packageId === selection.record.selectedPackageId;
      item.dataset.package = candidate.packageId;
      item.dataset.selected = String(chosen);
      item.dataset.checkpoint = String(selection.record.checkpointLevel);
      const heading = document.createElement("strong");
      heading.textContent = `${chosen ? "CHOSEN" : "CONSIDERED"} · L${selection.record.checkpointLevel} · ${candidate.label}`;
      const affected = growthAttributeChanges(selection.attributesBefore, candidate.attributesAfter, false);
      const details = document.createElement("small");
      details.textContent = `${affected} · FIT ${candidate.score} · ${candidate.reasonCodes.map(growthReasonLabel).join(" / ")}`;
      item.append(heading, details);
      return item;
    })
  ));
  const growth = derivedDeltaLabel(packet.growthDerivedDelta);
  const level = derivedDeltaLabel(packet.levelOnlyDerivedDelta);
  const other = derivedDeltaLabel(packet.otherSameBeatDerivedDelta);
  const total = derivedDeltaLabel(packet.totalDerivedDelta);
  elements.levelUpCutawayMechanics.textContent = `${growthAttributeChanges(first.attributesBefore, last.attributesAfter, true)} · Growth: ${growth} · Level: ${level} · Other same beat: ${other} · Total: ${total}`;
  const equipment = packet.equipmentAfter.length === 0
    ? "No equipped items"
    : packet.equipmentAfter.map((item) => item.itemName).join(" · ");
  elements.levelUpCutawayTableau.textContent = `${packet.className} · ${last.selectedCandidate.label} · ${equipment}`;
  const turningPointRange = first.turningPointOrdinal === last.turningPointOrdinal
    ? `${first.turningPointOrdinal}`
    : `${first.turningPointOrdinal}–${last.turningPointOrdinal}`;
  elements.levelUpCutawayProgress.textContent = `GROWTH TRUTH · HP ${first.resourcesBefore.health}→${last.resourcesAfter.health} STAYS · MAX ${first.resourcesBefore.maxHealth}→${last.resourcesAfter.maxHealth} · NO HEAL · MP ${first.resourcesBefore.mana}→${last.resourcesAfter.mana} STAYS · MAX ${first.resourcesBefore.maxMana}→${last.resourcesAfter.maxMana} · NO REFILL · Turning Point ${turningPointRange} of 3`;
  elements.levelUpCutawayOutcome.hidden = false;
  elements.levelUpCutawayOutcome.disabled = false;
  presentHeroGrowthAllocationPhase(fastMode ? "static" : "deed");
}

function clearChampionSealDom(): void {
  elements.levelUpCutawayHallSeal.hidden = true;
  elements.levelUpCutawayTableauLabel.textContent = "Final build";
  elements.levelUpCutawayHallSealRecord.textContent = "";
  elements.levelUpCutawayHallSealProvenance.textContent = "";
  elements.levelUpCutawayHallSealTruth.textContent = "";
  delete elements.levelUpCutaway.dataset.hallChampionId;
  delete elements.levelUpCutaway.dataset.hallChampionHash;
  delete elements.levelUpCutaway.dataset.hallRecordedTick;
  delete elements.levelUpCutaway.dataset.hallQualification;
  delete elements.levelUpCutaway.dataset.hallSourceCommandId;
  delete elements.levelUpCutaway.dataset.hallSourceCommandType;
  delete elements.levelUpCutaway.dataset.hallCompletedQuests;
  delete elements.levelUpCutaway.dataset.hallEquipmentCount;
  delete elements.levelUpCutaway.dataset.hallAbilityCount;
  delete elements.levelUpCutaway.dataset.hallMechanicalEffect;
  delete elements.levelUpCutaway.dataset.hallCampaignContinues;
}

function presentHeroLevelUpPacket(packet: HeroLevelUpPresentationPacket): void {
  elements.trapCutaway.hidden = true;
  elements.farewellCutaway.hidden = true;
  elements.levelUpCutaway.hidden = false;
  elements.weaponMemoryCutaway.hidden = true;
  elements.battleSpoilsCutaway.hidden = true;
  elements.townItineraryCutaway.hidden = true;
  elements.levelUpCutaway.dataset.active = "true";
  elements.levelUpCutaway.dataset.montageKind = "level";
  elements.levelUpCutaway.dataset.eventId = packet.eventId;
  elements.levelUpCutaway.dataset.emphasis = packet.emphasis;
  elements.levelUpCutaway.dataset.progressionBand = packet.progressionBand;
  clearChampionSealDom();
  elements.levelUpCutawayKicker.textContent = packet.schemaVersion === 2
    ? "Immutable earned record"
    : "Earned progression";
  elements.levelUpCutawayOutcome.textContent = packet.schemaVersion === 2
    ? "Show earned Hall seal"
    : "Show level";
  elements.levelUpCutawaySelectionStep.hidden = true;
  elements.levelUpCutawaySelection.textContent = "";
  elements.levelUpCutawayCandidates.replaceChildren();
  elements.levelUpCutawayTitle.textContent = packet.emphasis === "maximum"
    ? `${packet.heroName} · Maximum reached`
    : `${packet.heroName} · Level ${packet.levelAfter}`;
  elements.levelUpCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.levelUpCutawaySource.textContent = packet.sourceKind === "quest-reward"
    ? `${packet.questTitle ?? "Quest reward"} · +${packet.experienceDelta} XP`
    : `${packet.sourceHeadline} · +${packet.experienceDelta} XP`;
  elements.levelUpCutawayThreshold.textContent = packet.levelDelta === 1
    ? `${packet.experienceBefore} + ${packet.experienceDelta} = ${packet.experienceAfter} XP · threshold ${packet.thresholdSpan.firstRequiredExperience}`
    : `${packet.experienceBefore} + ${packet.experienceDelta} = ${packet.experienceAfter} XP · L${packet.thresholdSpan.firstLevel} @ ${packet.thresholdSpan.firstRequiredExperience} → L${packet.thresholdSpan.lastLevel} @ ${packet.thresholdSpan.lastRequiredExperience} · ${packet.levelDelta} thresholds`;
  elements.levelUpCutawayLevel.textContent = `LEVEL ${packet.levelBefore} → ${packet.levelAfter}`;
  const levelEffect = derivedDeltaLabel(packet.levelOnlyDerivedDelta);
  const sameBeatEffect = derivedDeltaLabel(packet.concurrentDerivedDelta);
  elements.levelUpCutawayMechanics.textContent = levelEffect === "none"
    ? `Level effect: mechanical plateau at ${packet.mechanicalLevelAfter}${sameBeatEffect === "none" ? "" : ` · Same beat: ${sameBeatEffect}`}`
    : `Level effect: ${levelEffect}${sameBeatEffect === "none" ? "" : ` · Same beat: ${sameBeatEffect}`}`;
  const equipment = packet.equipmentAfter.length === 0
    ? "No equipped items"
    : packet.equipmentAfter.map((item) => item.itemName).join(" · ");
  elements.levelUpCutawayTableau.textContent = `${packet.className} · Mastery ${packet.masteryAfter} · ${equipment}`;
  if (packet.schemaVersion === 2) {
    const seal = packet.championInductionSeal;
    const induction = seal.induction;
    elements.levelUpCutawayTableauLabel.textContent = "Final build · Hall record";
    elements.levelUpCutawayHallSeal.hidden = false;
    elements.levelUpCutawayHallSealRecord.textContent = `${packet.heroName} · ${packet.className} · Record ${induction.id} · Hash ${induction.contentHash}`;
    elements.levelUpCutawayHallSealProvenance.textContent = `Recorded T${induction.recordedTick} · Source ${seal.commandType} · ${seal.commandId} · ${seal.totalCompletedQuests} completed quests · ${seal.archivedEquipmentCount} equipped items · ${seal.archivedAbilityCount} abilities`;
    elements.levelUpCutawayHallSealTruth.textContent = "NO BONUS POWER · HERO NOT RETIRED · ETERNAL CAMPAIGN CONTINUES";
    elements.levelUpCutaway.dataset.hallChampionId = induction.id;
    elements.levelUpCutaway.dataset.hallChampionHash = induction.contentHash;
    elements.levelUpCutaway.dataset.hallRecordedTick = String(induction.recordedTick);
    elements.levelUpCutaway.dataset.hallQualification = induction.qualification;
    elements.levelUpCutaway.dataset.hallSourceCommandId = seal.commandId;
    elements.levelUpCutaway.dataset.hallSourceCommandType = seal.commandType;
    elements.levelUpCutaway.dataset.hallCompletedQuests = String(seal.totalCompletedQuests);
    elements.levelUpCutaway.dataset.hallEquipmentCount = String(seal.archivedEquipmentCount);
    elements.levelUpCutaway.dataset.hallAbilityCount = String(seal.archivedAbilityCount);
    elements.levelUpCutaway.dataset.hallMechanicalEffect = seal.mechanicalEffect;
    elements.levelUpCutaway.dataset.hallCampaignContinues = String(seal.campaignContinues);
    elements.levelUpCutawayProgress.textContent = "Level 1000 maximum reached · the immutable Hall record is presentation only · the Eternal adventure continues.";
  } else {
    elements.levelUpCutawayProgress.textContent = packet.nextLevelRequirement === null
      ? "Level 1000 maximum reached · the Eternal adventure continues."
      : `Next: Level ${packet.levelAfter + 1} at ${packet.nextLevelRequirement} XP · this earned transition cannot be altered by the viewer.`;
  }
  elements.levelUpCutawayOutcome.hidden = false;
  elements.levelUpCutawayOutcome.disabled = false;
  presentHeroLevelUpPhase(fastMode ? "static" : "source");
}

const weaponMemoryPhaseOrder: readonly WeaponMemoryCutawayPhase[] = [
  "name",
  "first-use",
  "road",
  "familiar-form",
  "final-strike",
  "memory",
  "final",
];

function weaponMemoryPhaseIndex(phase: WeaponMemoryCutawayPhase): number {
  if (phase === "static" || phase === "settled") return weaponMemoryPhaseOrder.length - 1;
  return weaponMemoryPhaseOrder.indexOf(phase);
}

function presentWeaponMemoryPhase(phase: WeaponMemoryCutawayPhase): void {
  const currentIndex = weaponMemoryPhaseIndex(phase);
  const stepIndexes: Readonly<Record<string, number>> = {
    name: 0,
    "first-use": 1,
    road: 2,
    strongest: 2,
    "familiar-form": 3,
    "final-strike": 4,
  };
  elements.weaponMemoryCutaway.dataset.phase = phase;
  for (const step of weaponMemoryCutawaySteps) {
    const stepIndex = stepIndexes[step.dataset.weaponMemoryStep ?? ""] ?? -1;
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === Math.min(currentIndex, 4));
  }
}

function weaponMemoryReceiptText(receipt: WeaponMemoryCeremonyPacketV1["receipts"][number]): string {
  return `T${receipt.resolvedTick} · ${receipt.outcome} · ${receipt.basicStrikes} basic ${receipt.basicStrikes === 1 ? "strike" : "strikes"} · ${receipt.damage} damage`;
}

function presentWeaponMemoryPacket(packet: WeaponMemoryCeremonyPacketV1): void {
  const first = packet.receipts.find((receipt) => receipt.id === packet.firstReceiptId);
  const strongest = packet.receipts.find((receipt) => receipt.id === packet.highestDamageReceiptId);
  const final = packet.receipts.find((receipt) => receipt.id === packet.finalReceiptId);
  const formUnlock = packet.receipts.find((receipt) => receipt.id === packet.familiarFormUnlockReceiptId);
  if (first === undefined || strongest === undefined || final === undefined || formUnlock === undefined) {
    throw new Error("Weapon-memory packet has unresolved representative receipts");
  }
  elements.trapCutaway.hidden = true;
  elements.farewellCutaway.hidden = true;
  elements.levelUpCutaway.hidden = true;
  elements.weaponMemoryCutaway.hidden = false;
  elements.battleSpoilsCutaway.hidden = true;
  elements.townItineraryCutaway.hidden = true;
  elements.weaponMemoryCutaway.dataset.active = "true";
  elements.weaponMemoryCutaway.dataset.eventId = packet.eventId;
  elements.weaponMemoryCutaway.dataset.outcome = final.outcome;
  elements.weaponMemoryCutaway.dataset.weaponId = packet.weaponId;
  elements.weaponMemoryCutaway.dataset.silhouette = packet.silhouette;
  elements.weaponMemoryCutaway.dataset.firstReceipt = packet.firstReceiptId;
  elements.weaponMemoryCutaway.dataset.strongestReceipt = packet.highestDamageReceiptId;
  elements.weaponMemoryCutaway.dataset.finalReceipt = packet.finalReceiptId;
  elements.weaponMemoryCutaway.dataset.formReceipt = packet.familiarFormUnlockReceiptId;
  elements.weaponMemoryCutaway.dataset.outcomes = `${packet.outcomeCounts.victories}:${packet.outcomeCounts.defeats}:${packet.outcomeCounts.stalemates}`;
  elements.weaponMemoryCutaway.dataset.contribution = `${packet.totalBasicStrikes}:${packet.totalDamage}`;
  elements.weaponMemoryCutaway.dataset.equippedAfter = String(packet.equippedAfter);
  elements.weaponMemoryCutaway.dataset.equippedWeaponAfter = packet.equippedWeaponIdAfter ?? "none";
  elements.weaponMemoryCutaway.dataset.mechanicalBonus = String(packet.mechanicalBonus);
  elements.weaponMemoryCutawayTitle.textContent = `${packet.heroName} · ${packet.weaponName}`;
  elements.weaponMemoryCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.weaponMemoryCutawayName.textContent = `${packet.weaponName} · ${packet.rarity} ${packet.silhouette} · Use Mastery ${packet.levelAfter}/${packet.maximumLevel}`;
  elements.weaponMemoryCutawayFirst.textContent = weaponMemoryReceiptText(first);
  elements.weaponMemoryCutawayRoad.textContent = `${packet.receipts.length}/${packet.maximumExperience} recorded encounters · ${packet.outcomeCounts.victories} victories · ${packet.outcomeCounts.defeats} defeats · ${packet.outcomeCounts.stalemates} stalemates · ${packet.totalBasicStrikes} basic strikes · ${packet.totalDamage} damage`;
  elements.weaponMemoryCutawayStrongest.textContent = weaponMemoryReceiptText(strongest);
  const unlockMark = packet.receipts.findIndex((receipt) => receipt.id === packet.familiarFormUnlockReceiptId) + 1;
  elements.weaponMemoryCutawayForm.textContent = `${packet.familiarFormName} · unlocked by mark ${unlockMark} at T${formUnlock.resolvedTick} · visual handling only`;
  elements.weaponMemoryCutawayFinal.textContent = `${weaponMemoryReceiptText(final)} · use XP ${packet.experienceBefore}→${packet.experienceAfter} · Use Level ${packet.levelBefore}→${packet.levelAfter}`;
  const currentWeapon = packet.equippedWeaponIdAfter === null
    ? null
    : state.depth.hero.inventory.find((item) => item.id === packet.equippedWeaponIdAfter);
  const equipmentTruth = packet.equippedAfter
    ? `${packet.weaponName} remains equipped`
    : `Mastered with ${packet.weaponName}; now carrying ${currentWeapon?.name ?? packet.equippedWeaponIdAfter ?? "no weapon"}`;
  elements.weaponMemoryCutawayProgress.textContent = `USE MASTERY 10 / 10 · 45 RECORDED ENCOUNTERS · NO COMBAT BONUS · ${equipmentTruth}.`;
  elements.weaponMemoryCutawayOutcome.hidden = false;
  elements.weaponMemoryCutawayOutcome.disabled = false;
  presentWeaponMemoryPhase(fastMode ? "static" : "name");
}

const battleSpoilsPhaseOrder: readonly BattleSpoilsCutawayPhase[] = [
  "found",
  "compare",
  "exchange",
  "consequence",
  "final",
];

function battleSpoilsPhaseIndex(phase: BattleSpoilsCutawayPhase): number {
  if (phase === "static" || phase === "settled") return battleSpoilsPhaseOrder.length - 1;
  return battleSpoilsPhaseOrder.indexOf(phase);
}

function presentBattleSpoilsPhase(phase: BattleSpoilsCutawayPhase): void {
  const currentIndex = battleSpoilsPhaseIndex(phase);
  const stepIndexes: Readonly<Record<string, number>> = {
    found: 0,
    old: 1,
    new: 1,
    stats: 1,
    resources: 3,
    continuity: 4,
  };
  elements.battleSpoilsCutaway.dataset.phase = phase;
  for (const step of battleSpoilsCutawaySteps) {
    const stepIndex = stepIndexes[step.dataset.battleSpoilsStep ?? ""] ?? -1;
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === Math.min(currentIndex, 4));
  }
}

function battleSpoilsModifierText(item: BattleSpoilsComparisonPacketV1["newItem"]): string {
  return item.modifiers.length === 0
    ? "no modifiers"
    : item.modifiers.map((fact) => `${fact.modifier} +${fact.amount}`).join(" · ");
}

function battleSpoilsDeltaStatus(delta: number): "IMPROVED" | "REDUCED" | "UNCHANGED" {
  return delta > 0 ? "IMPROVED" : delta < 0 ? "REDUCED" : "UNCHANGED";
}

function battleSpoilsDeltaText(packet: BattleSpoilsComparisonPacketV1): string {
  const labels = {
    power: "POWER",
    armor: "ARMOR",
    initiative: "INITIATIVE",
    maxHealth: "MAX HP",
    maxMana: "MAX MP",
  } as const;
  return (Object.keys(labels) as Array<keyof typeof labels>).map((key) => {
    const delta = packet.derivedDelta[key];
    const signed = delta > 0 ? `+${delta}` : String(delta);
    return `${labels[key]} ${packet.derivedBefore[key]}→${packet.derivedAfter[key]} (${signed}) ${battleSpoilsDeltaStatus(delta)}`;
  }).join(" · ");
}

function presentBattleSpoilsPacket(packet: BattleSpoilsComparisonPacketV1): void {
  elements.trapCutaway.hidden = true;
  elements.farewellCutaway.hidden = true;
  elements.levelUpCutaway.hidden = true;
  elements.weaponMemoryCutaway.hidden = true;
  elements.battleSpoilsCutaway.hidden = false;
  elements.townItineraryCutaway.hidden = true;
  elements.battleSpoilsCutaway.dataset.active = "true";
  elements.battleSpoilsCutaway.dataset.eventId = packet.eventId;
  elements.battleSpoilsCutaway.dataset.combatId = packet.combatId;
  elements.battleSpoilsCutaway.dataset.slot = packet.slot;
  elements.battleSpoilsCutaway.dataset.oldItem = packet.oldItem?.id ?? "empty";
  elements.battleSpoilsCutaway.dataset.newItem = packet.newItem.id;
  elements.battleSpoilsCutaway.dataset.oldSilhouette = packet.oldItem?.silhouette ?? "empty";
  elements.battleSpoilsCutaway.dataset.newSilhouette = packet.newItem.silhouette;
  elements.battleSpoilsCutaway.dataset.derivedDelta = [
    packet.derivedDelta.power,
    packet.derivedDelta.armor,
    packet.derivedDelta.initiative,
    packet.derivedDelta.maxHealth,
    packet.derivedDelta.maxMana,
  ].join(":");
  elements.battleSpoilsCutaway.dataset.oldDisposition = packet.oldItemDisposition;
  elements.battleSpoilsCutawayTitle.textContent = `${packet.heroName} · ${packet.slot}`;
  elements.battleSpoilsCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.battleSpoilsCutawayFound.textContent = `${packet.newItem.name} · ${packet.newItem.rarity} ${packet.newItem.slot} · ${battleSpoilsModifierText(packet.newItem)}`;
  elements.battleSpoilsCutawayOld.textContent = packet.oldItem === null
    ? `Empty ${packet.slot} slot`
    : `${packet.oldItem.name} · ${packet.oldItem.rarity} ${packet.oldItem.silhouette} · ${battleSpoilsModifierText(packet.oldItem)}`;
  elements.battleSpoilsCutawayNew.textContent = `${packet.newItem.name} · ${packet.newItem.rarity} ${packet.newItem.silhouette} · already auto-equipped`;
  elements.battleSpoilsCutawayStats.textContent = battleSpoilsDeltaText(packet);
  elements.battleSpoilsCutawayResources.textContent = `CURRENT HP ${packet.resourcesBefore.health}→${packet.resourcesAfter.health} · MAX HP ${packet.resourcesBefore.maxHealth}→${packet.resourcesAfter.maxHealth} · CURRENT MP ${packet.resourcesBefore.mana}→${packet.resourcesAfter.mana} · MAX MP ${packet.resourcesBefore.maxMana}→${packet.resourcesAfter.maxMana} · no refill claimed`;
  const mastery = packet.oldItem?.mastery;
  elements.battleSpoilsCutawayContinuity.textContent = packet.oldItem === null
    ? `Previously empty ${packet.slot} slot · no item displaced`
    : `${packet.oldItem.name} remains in Inventory${mastery == null ? "" : ` · Use Mastery L${mastery.level}/10 · ${mastery.receiptCount} recorded encounters`}`;
  elements.battleSpoilsCutawayProgress.textContent = `AUTO-EQUIPPED · COMPARISON · ${packet.newItem.name} IS NOW WORN · ${packet.oldItem === null ? "EMPTY SLOT FILLED" : "OLD ITEM REMAINS IN PACK"}`;
  elements.battleSpoilsCutawayOutcome.hidden = false;
  elements.battleSpoilsCutawayOutcome.disabled = false;
  presentBattleSpoilsPhase(fastMode ? "static" : "found");
}

const abilityResonancePhaseOrder: readonly AbilityResonanceCutawayPhase[] = [
  "source",
  "experience",
  "resonance",
  "mastery",
  "tableau",
  "final",
];

function abilityResonancePhaseIndex(phase: AbilityResonanceCutawayPhase): number {
  if (phase === "static" || phase === "settled") return abilityResonancePhaseOrder.length - 1;
  return abilityResonancePhaseOrder.indexOf(phase);
}

function presentAbilityResonancePhase(phase: AbilityResonanceCutawayPhase): void {
  const currentIndex = abilityResonancePhaseIndex(phase);
  const stepIndexes: Readonly<Record<string, number>> = {
    source: 0,
    threshold: 1,
    effect: 2,
    mastery: 3,
    "next-use": 4,
  };
  elements.abilityResonanceCutaway.dataset.phase = phase;
  for (const step of abilityResonanceCutawaySteps) {
    const stepIndex = stepIndexes[step.dataset.abilityResonanceStep ?? ""] ?? -1;
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === Math.min(currentIndex, 4));
  }
}

function abilityResonanceProvenance(packet: AbilityResonancePacketV1): string {
  if (packet.provenanceStatus === "verified") {
    return `Verified monster art · ${packet.sourceMonsterName} · discovery T${packet.discoveryTick}`;
  }
  if (packet.abilityKind === "secret") {
    return `Monster origin unverified · ${packet.sourceMonsterId ?? "no source record"}`;
  }
  return "Origin unverified · no monster acquisition record claimed";
}

function abilityResonanceMechanicalDelta(packet: AbilityResonancePacketV1): string {
  const status = packet.statusPotencyBefore === null
    ? "status potency not applicable"
    : `status potency ${packet.statusPotencyBefore}→${packet.statusPotencyAfter}`;
  return `subsequent-use level damage contribution +${packet.damageLevelContributionBefore}→+${packet.damageLevelContributionAfter} · ${status}`;
}

function presentAbilityResonancePacket(packet: AbilityResonancePacketV1): void {
  elements.abilityResonanceCutaway.hidden = false;
  elements.abilityResonanceCutaway.dataset.active = "true";
  elements.abilityResonanceCutaway.dataset.eventId = packet.eventId;
  elements.abilityResonanceCutaway.dataset.abilityId = packet.abilityId;
  elements.abilityResonanceCutaway.dataset.sourceKind = packet.sourceKind;
  elements.abilityResonanceCutaway.dataset.sourceCue = projectAbilityResonanceSourcePresentation(packet.sourceKind).cue;
  elements.abilityResonanceCutaway.dataset.effect = packet.effect;
  elements.abilityResonanceCutaway.dataset.experience = `${packet.experienceBefore}:${packet.experienceAfter}`;
  elements.abilityResonanceCutaway.dataset.uses = `${packet.usesBefore}:${packet.usesAfter}`;
  elements.abilityResonanceCutaway.dataset.damageContribution = `${packet.damageLevelContributionBefore}:${packet.damageLevelContributionAfter}`;
  elements.abilityResonanceCutaway.dataset.statusPotency = `${packet.statusPotencyBefore ?? "none"}:${packet.statusPotencyAfter ?? "none"}`;
  elements.abilityResonanceCutaway.dataset.provenance = packet.provenanceStatus;
  elements.abilityResonanceCutawayTitle.textContent = `${packet.abilityName} · Mastery`;
  elements.abilityResonanceCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.abilityResonanceCutawaySource.textContent = packet.sourceKind === "battle-use"
    ? `Battle use · ${packet.commandId} · uses ${packet.usesBefore}→${packet.usesAfter}`
    : `Deliberate practice · ${packet.commandId} · uses remain ${packet.usesAfter}`;
  elements.abilityResonanceCutawayThreshold.textContent = `${packet.experienceBefore} + ${packet.experienceDelta} = ${packet.experienceAfter}/${packet.maximumExperience} ability XP`;
  elements.abilityResonanceCutawayEffect.textContent = `${packet.abilityKind} · ${packet.effect} · base potency ${packet.basePotency} unchanged · mana ${packet.manaCost} unchanged · ${abilityResonanceMechanicalDelta(packet)} · ${abilityResonanceProvenance(packet)}`;
  elements.abilityResonanceCutawayMastery.textContent = `${packet.abilityName} · Level ${packet.levelBefore} → ${packet.levelAfter}`;
  elements.abilityResonanceCutawayNextUse.textContent = packet.crossingActionLevel === 19
    ? "Crossing action resolved at Level 19 · subsequent uses read Level 20"
    : "No crossing action · subsequent uses read Level 20";
  elements.abilityResonanceCutawayProgress.textContent = "ABILITY MASTERED · LEVEL 19 → 20 · NO NEW ABILITY · NO BRANCH";
  elements.abilityResonanceCutawayOutcome.hidden = false;
  elements.abilityResonanceCutawayOutcome.disabled = false;
  presentAbilityResonancePhase(fastMode ? "static" : "source");
}

const townItineraryPhaseOrder: readonly TownItineraryCutawayPhase[] = [
  "arrival",
  "district",
  "route",
  "encounter",
  "consequence",
];

function townItineraryPhaseIndex(phase: TownItineraryCutawayPhase): number {
  if (phase === "static" || phase === "settled") return townItineraryPhaseOrder.length - 1;
  return townItineraryPhaseOrder.indexOf(phase);
}

function presentTownItineraryPhase(phase: TownItineraryCutawayPhase): void {
  const currentIndex = townItineraryPhaseIndex(phase);
  const stepIndexes: Readonly<Record<string, number>> = {
    arrival: 0,
    district: 1,
    route: 2,
    resident: 3,
    consequence: 4,
  };
  elements.townItineraryCutaway.dataset.phase = phase;
  for (const step of townItineraryCutawaySteps) {
    const stepIndex = stepIndexes[step.dataset.townItineraryStep ?? ""] ?? -1;
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === currentIndex);
  }
}

function presentTownItineraryPacket(packet: TownItineraryPacketV1): void {
  elements.trapCutaway.hidden = true;
  elements.farewellCutaway.hidden = true;
  elements.levelUpCutaway.hidden = true;
  elements.weaponMemoryCutaway.hidden = true;
  elements.battleSpoilsCutaway.hidden = true;
  elements.townItineraryCutaway.hidden = false;
  elements.townItineraryCutaway.dataset.active = "true";
  elements.townItineraryCutaway.dataset.eventId = packet.eventId;
  elements.townItineraryCutaway.dataset.townId = packet.town.id;
  elements.townItineraryCutaway.dataset.districtId = packet.district.id;
  elements.townItineraryCutaway.dataset.buildingId = packet.building.id;
  elements.townItineraryCutaway.dataset.residentId = packet.resident.id;
  elements.townItineraryCutaway.dataset.routeIds = packet.routeStops.map((stop) => stop.id).join(":");
  elements.townItineraryCutaway.dataset.visit = `${packet.visit.before}:${packet.visit.after}`;
  elements.townItineraryCutaway.dataset.reputation = `${packet.reputation.before}:${packet.reputation.after}`;
  elements.townItineraryCutawayTitle.textContent = `${packet.hero.name} · ${packet.town.name}`;
  elements.townItineraryCutawayEvent.textContent = `T${packet.tick} · ${packet.eventId}`;
  elements.townItineraryCutawayArrival.textContent = `${packet.location.name} · ${packet.town.specialty} · founded ${packet.town.foundedYear}`;
  elements.townItineraryCutawayDistrict.textContent = `${packet.district.name} · ${packet.district.character}`;
  elements.townItineraryCutawayRoute.textContent = packet.routeStops
    .map((stop, index) => `${index + 1}. ${stop.name} (${stop.kind})`)
    .join(" → ");
  elements.townItineraryCutawayResident.textContent = `${packet.resident.name} · ${packet.resident.role} · ${packet.resident.disposition} · home: ${packet.building.name}`;
  elements.townItineraryCutawayConsequence.textContent = `Visit ${packet.visit.before}→${packet.visit.after} · Reputation ${packet.reputation.before}→${packet.reputation.after} · XP ${packet.experience.before}→${packet.experience.after}`;
  elements.townItineraryCutawayProgress.textContent = `REAL ROUTE · ${packet.routeStops.length} ${packet.routeStops.length === 1 ? "STOP" : "STOPS"} · RESIDENT ${packet.selectionIndex + 1} OF ${packet.residentCount} · VISIT AND REPUTATION ALREADY APPLIED`;
  elements.townItineraryCutawayOutcome.hidden = false;
  elements.townItineraryCutawayOutcome.disabled = false;
  presentTownItineraryPhase(fastMode ? "static" : "arrival");
}

const fieldNoteCutawayPhaseOrder: readonly FieldNoteCutawayPhase[] = [
  "observations",
  "third-mark",
  "inference",
  "precedence",
  "final",
];

function fieldNoteCutawayPhaseIndex(phase: FieldNoteCutawayPhase): number {
  if (phase === "static" || phase === "settled") return fieldNoteCutawayPhaseOrder.length - 1;
  return fieldNoteCutawayPhaseOrder.indexOf(phase);
}

function presentFieldNoteCutawayPhase(phase: FieldNoteCutawayPhase): void {
  const currentIndex = fieldNoteCutawayPhaseIndex(phase);
  elements.fieldNoteCutaway.dataset.phase = phase;
  for (const step of fieldNoteCutawaySteps) {
    const stepPhase = step.dataset.fieldNoteStep as FieldNoteCutawayPhase | undefined;
    const stepIndex = stepPhase === undefined ? -1 : fieldNoteCutawayPhaseIndex(stepPhase);
    step.dataset.reached = String(stepIndex >= 0 && stepIndex <= currentIndex);
    step.dataset.current = String(stepIndex === currentIndex || (currentIndex >= 4 && stepPhase === "final"));
  }
}

function clearFieldNoteLiveTellPresentation(): void {
  delete elements.fieldNoteCutaway.dataset.liveTell;
  delete elements.fieldNoteCutaway.dataset.liveTellId;
  delete elements.fieldNoteCutaway.dataset.liveTellCue;
  delete elements.fieldNoteCutaway.dataset.liveTellStance;
  delete elements.fieldNoteCutaway.dataset.liveTellClarity;
  delete elements.fieldNoteCutaway.dataset.evidenceRelationship;
  delete elements.fieldNoteCutaway.dataset.commitmentVisibility;
  elements.fieldNoteCutawayLiveTell.hidden = true;
  delete elements.fieldNoteCutawayLiveTell.dataset.clarity;
  delete elements.fieldNoteCutawayLiveTell.dataset.relationship;
  elements.fieldNoteCutawayLiveTellSignal.textContent = "";
  elements.fieldNoteCutawayLiveTellPriority.textContent = "";
  elements.fieldNoteCutawayLiveTellHabit.textContent = "";
  elements.fieldNoteCutawayLiveTellHiddenCopy.textContent = "";
}

function presentFieldNoteCutawayPacket(packet: FieldNoteResolutionPresentationPacket): void {
  elements.fieldNoteCutaway.hidden = false;
  elements.fieldNoteCutaway.dataset.active = "true";
  elements.fieldNoteCutaway.dataset.eventId = packet.eventId;
  elements.fieldNoteCutaway.dataset.encounterMode = packet.encounterMode;
  elements.fieldNoteCutaway.dataset.sourceCommand = packet.sourceCommandType;
  elements.fieldNoteCutaway.dataset.speciesKey = packet.speciesKey;
  elements.fieldNoteCutaway.dataset.noteCount = String(packet.unlocks.length);
  elements.fieldNoteCutaway.dataset.priorEvidence = packet.priorEvidence;
  elements.fieldNoteCutaway.dataset.packetVersion = String(packet.schemaVersion);
  clearFieldNoteLiveTellPresentation();
  elements.fieldNoteCutawayTitle.textContent = packet.unlocks.length === 1
    ? `${packet.unlocks[0]!.speciesName} · Field Note complete`
    : `${packet.unlocks.length} Field Notes completed`;
  elements.fieldNoteCutawayEvent.textContent = `${packet.heroName} · T${packet.tick} · ${packet.eventId} · ${packet.sourceCommandType}`;
  elements.fieldNoteCutawayObservations.textContent = "Two prior observations retained as aggregate counts · earlier event receipts are not claimed";
  elements.fieldNoteCutawayCurrent.textContent = `${packet.encounterMode === "pattern-duel" ? "Pattern Duel" : "Tactical encounter"} · exact current source raises every listed species from 2/3 to 3/3`;
  elements.fieldNoteCutawayInference.textContent = packet.unlocks
    .map((unlock) => `${unlock.speciesName}: ${unlock.habitLabel}`)
    .join(" · ");
  elements.fieldNoteCutawayPrecedence.textContent = packet.precedenceText;
  if (packet.schemaVersion === 2) {
    const unlock = packet.unlocks[0]!;
    const tell = packet.publicTell;
    const relationship = fieldNoteEvidenceRelationship(packet);
    const agrees = relationship === "agree";
    elements.fieldNoteCutaway.dataset.liveTell = "public";
    elements.fieldNoteCutaway.dataset.liveTellId = tell.tellId;
    elements.fieldNoteCutaway.dataset.liveTellCue = tell.cue;
    elements.fieldNoteCutaway.dataset.liveTellStance = tell.suggestedStance;
    elements.fieldNoteCutaway.dataset.liveTellClarity = String(tell.clarity);
    elements.fieldNoteCutaway.dataset.evidenceRelationship = relationship;
    elements.fieldNoteCutaway.dataset.commitmentVisibility = packet.commitmentVisibility;
    elements.fieldNoteCutawayLiveTell.hidden = false;
    elements.fieldNoteCutawayLiveTell.dataset.clarity = String(tell.clarity);
    elements.fieldNoteCutawayLiveTell.dataset.relationship = relationship;
    elements.fieldNoteCutawayLiveTellSignal.textContent = `${fieldNotePublicTellText(tell)} · CLARITY ${tell.clarity}/3 (${fieldNotePublicTellClarityLabel(tell.clarity)}) · SUGGESTS ${counterDuelStanceLabel(tell.suggestedStance)} · suggestion, not certainty`;
    elements.fieldNoteCutawayLiveTellPriority.textContent = agrees
      ? "AGREE · LIVE SIGNAL STILL TAKES PRIORITY"
      : "LIVE SIGNAL TAKES PRIORITY FOR THIS READ";
    elements.fieldNoteCutawayLiveTellHabit.textContent = unlock.habitLabel;
    elements.fieldNoteCutawayLiveTellHiddenCopy.textContent = "Rival commitment remains hidden; the signal may be false.";
  }
  elements.fieldNoteCutawayNotes.replaceChildren(...packet.unlocks.map((unlock) => {
    const item = document.createElement("li");
    item.dataset.speciesId = unlock.speciesId;
    item.dataset.observations = `${unlock.beforeEncounterCount}:${unlock.afterEncounterCount}`;
    item.dataset.preferredStance = unlock.preferredStance;
    item.textContent = `${unlock.speciesName} · ${unlock.beforeEncounterCount}→${unlock.afterEncounterCount}/${unlock.requiredEncounterCount} · ${unlock.habitLabel}`;
    return item;
  }));
  elements.fieldNoteCutawayProgress.textContent = "KNOWLEDGE ONLY · NO XP, GOLD, ABILITY, SECRET TECHNIQUE OR CURRENT INTENT GRANTED";
  elements.fieldNoteCutawayOutcome.hidden = false;
  elements.fieldNoteCutawayOutcome.disabled = false;
  presentFieldNoteCutawayPhase(fastMode ? "static" : "observations");
}

function finishFieldNoteCutaway(packet: FieldNoteResolutionPresentationPacket): void {
  elements.fieldNoteCutaway.dataset.active = "false";
  elements.fieldNoteCutawayOutcome.hidden = true;
  elements.fieldNoteCutawayOutcome.disabled = true;
  presentFieldNoteCutawayPhase("final");
  const notes = packet.unlocks.map((unlock) => `${unlock.speciesName}: ${unlock.habitLabel}`).join("; ");
  const signal = packet.schemaVersion === 2
    ? ` Public live signal: ${fieldNotePublicTellText(packet.publicTell)}; clarity ${packet.publicTell.clarity} of 3; suggestion only. Rival commitment remains hidden.`
    : "";
  elements.fieldNoteCutawayAnnouncement.textContent = `${packet.unlocks.length === 1 ? "Field Note completed" : `${packet.unlocks.length} Field Notes completed`}. ${notes}. Each crossed from two to three observations at tick ${packet.tick}.${signal} ${packet.precedenceText} No reward or technique was granted.`;
}

interface CutawayRecipeAdapter {
  readonly root: HTMLElement;
  readonly outcomeButton: HTMLButtonElement;
  readonly prepare: (candidate: ProductionCutawayCandidate) => TrapCutawayStaging | null;
  readonly present: (candidate: ProductionCutawayCandidate, staging: TrapCutawayStaging | null) => void;
  readonly presentPhase: (phase: string) => void;
  readonly finish: (candidate: ProductionCutawayCandidate) => void;
}

const cutawayAdapters: Record<ProductionCutawayRecipeKey, CutawayRecipeAdapter> = {
  "elsewhere-loaf@1": {
    root: elements.elsewhereLoafCutaway, outcomeButton: elements.elsewhereLoafOutcome, prepare: () => null,
    present: (candidate) => {
      const packet = (candidate as ElsewhereLoafCutawayCandidate).packet;
      elements.app.dataset.elsewhereLoaf = packet.phase;
      Object.assign(elements.elsewhereLoafCutaway.dataset, { active: "true", event: packet.eventId,
        source: packet.sourceCommandId, phase: packet.phase, actor: packet.residentId, location: packet.locationId });
      elements.elsewhereLoafTitle.textContent = elsewhereLoafTitle(packet, state.tick);
      elements.elsewhereLoafActor.textContent = `${packet.companionName} · ${packet.innName}`;
      elements.elsewhereLoafLine.textContent = packet.line;
      elements.elsewhereLoafSupply.textContent = packet.phase === "admission"
        ? `Inn-supplied dough: 1 · ${packet.style === "experimental" ? "An experiment" : "A steady recipe"}`
        : "Trial dough: 1→0 · One loaf. The hero is elsewhere.";
      elements.elsewhereLoafOutcome.disabled = false;
      elements.elsewhereLoafCutaway.hidden = false;
    },
    presentPhase: phase => { elements.elsewhereLoafCutaway.dataset.presentationPhase = phase; },
    finish: () => {
      elements.elsewhereLoafCutaway.hidden = true;
      elements.elsewhereLoafCutaway.dataset.active = "false";
      elements.elsewhereLoafOutcome.disabled = true;
      delete elements.app.dataset.elsewhereLoaf;
      renderer.render(state);
    },
  },
  "trap-resolution@1": {
    root: elements.trapCutaway,
    outcomeButton: elements.trapCutawayOutcome,
    prepare: (candidate) => {
      const packet = (candidate as TrapCutawayCandidate).packet;
      const selection = selectTrapCutawayStaging(trapCutawayFatigueMemory, packet, {
        allowMotionFlavor: !fastMode && !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        semanticFingerprint: cutawayRepetitionFingerprint(cutawayRegistry, candidate),
      });
      trapCutawayFatigueMemory = selection.memory;
      return selection.staging;
    },
    present: (candidate, staging) => presentTrapCutawayPacket(
      (candidate as TrapCutawayCandidate).packet,
      staging ?? { shot: "static-tableau", flavor: "none" },
    ),
    presentPhase: (phase) => presentTrapCutawayPhase(phase as TrapCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as TrapCutawayCandidate).packet;
      elements.trapCutaway.dataset.active = "false";
      elements.trapCutawayOutcome.hidden = true;
      elements.trapCutawayOutcome.disabled = true;
      presentTrapCutawayPhase("final");
      const outcome = trapCutawayOutcome(packet).toUpperCase();
      const resource = packet.schemaVersion === 3
        ? `MP ${packet.manaBefore} to ${packet.manaAfter}; ${packet.manaLost} mana lost. HP ${packet.healthAfter} unchanged.`
        : `HP ${packet.healthBefore} to ${packet.healthAfter}.`;
      const tool = packet.schemaVersion === 1 ? null : packet.tool;
      elements.trapCutawayAnnouncement.textContent = `${outcome}. ${resource} ${tool !== null ? "Disarming Kit consumed: 1 to 0; +2 to the check. " : ""}${packet.completedExit ? "Dungeon exit reached." : "The maze continues."}`;
    },
  },
  "companion-farewell@1": {
    root: elements.farewellCutaway,
    outcomeButton: elements.farewellCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentFarewellCutawayPacket((candidate as FarewellCutawayCandidate).packet),
    presentPhase: (phase) => presentFarewellCutawayPhase(phase as FarewellCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as FarewellCutawayCandidate).packet;
      elements.farewellCutaway.dataset.active = "false";
      elements.farewellCutawayOutcome.hidden = true;
      elements.farewellCutawayOutcome.disabled = true;
      presentFarewellCutawayPhase("final");
      elements.farewellCutawayAnnouncement.textContent = `${packet.companionName} reached ${packet.destinationName} and left the party. Former companion recorded at tick ${packet.departureTick}.`;
    },
  },
  "hero-level-up@1": {
    root: elements.levelUpCutaway,
    outcomeButton: elements.levelUpCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentHeroLevelUpPacket((candidate as HeroLevelUpCutawayCandidate).packet),
    presentPhase: (phase) => presentHeroLevelUpPhase(phase as HeroLevelUpCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as HeroLevelUpCutawayCandidate).packet;
      elements.levelUpCutaway.dataset.active = "false";
      elements.levelUpCutawayOutcome.hidden = true;
      elements.levelUpCutawayOutcome.disabled = true;
      presentHeroLevelUpPhase("final");
      elements.levelUpCutawayAnnouncement.textContent = packet.levelAfter === 1_000
        ? `${packet.heroName} reached the maximum, Level 1000.`
        : `${packet.heroName} earned Level ${packet.levelAfter} with ${packet.experienceAfter} experience.`;
    },
  },
  "hero-level-up@2": {
    root: elements.levelUpCutaway,
    outcomeButton: elements.levelUpCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentHeroLevelUpPacket((candidate as HeroLevelUpChampionCutawayCandidate).packet),
    presentPhase: (phase) => presentHeroLevelUpPhase(phase as HeroLevelUpCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as HeroLevelUpChampionCutawayCandidate).packet;
      const induction = packet.championInductionSeal.induction;
      elements.levelUpCutaway.dataset.active = "false";
      elements.levelUpCutawayOutcome.hidden = true;
      elements.levelUpCutawayOutcome.disabled = true;
      presentHeroLevelUpPhase("final");
      elements.levelUpCutawayAnnouncement.textContent = `${packet.heroName} earned Level 1000. Hall record ${induction.id} was sealed at tick ${induction.recordedTick}. No bonus power. The hero is not retired. The Eternal campaign continues.`;
    },
  },
  "hero-growth-allocation@1": {
    root: elements.levelUpCutaway,
    outcomeButton: elements.levelUpCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentHeroGrowthAllocationPacket((candidate as HeroGrowthAllocationCutawayCandidate).packet),
    presentPhase: (phase) => presentHeroGrowthAllocationPhase(phase as HeroGrowthAllocationCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as HeroGrowthAllocationCutawayCandidate).packet;
      const first = packet.selections[0];
      const last = packet.selections.at(-1);
      if (first === undefined || last === undefined) throw new Error("Growth allocation packet has no persisted selection");
      elements.levelUpCutaway.dataset.active = "false";
      elements.levelUpCutawayOutcome.hidden = true;
      elements.levelUpCutawayOutcome.disabled = true;
      presentHeroGrowthAllocationPhase("final");
      const changed = growthAttributeChanges(first.attributesBefore, last.attributesAfter, false);
      elements.levelUpCutawayAnnouncement.textContent = `${packet.heroName} settled ${packet.selectionCount === 1 ? `Level ${first.record.checkpointLevel}, Turning Point ${first.turningPointOrdinal} of 3` : `Levels ${first.record.checkpointLevel} through ${last.record.checkpointLevel}, Turning Points ${first.turningPointOrdinal} through ${last.turningPointOrdinal} of 3`}. ${packet.selections.map((selection) => selection.selectedCandidate.label).join(", ")} became the path forward. ${changed}. Current health and mana did not refill during growth.`;
    },
  },
  "field-note-resolution@1": {
    root: elements.fieldNoteCutaway,
    outcomeButton: elements.fieldNoteCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentFieldNoteCutawayPacket((candidate as FieldNoteResolutionCutawayCandidate).packet),
    presentPhase: (phase) => presentFieldNoteCutawayPhase(phase as FieldNoteCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as FieldNoteResolutionCutawayCandidate).packet;
      finishFieldNoteCutaway(packet);
    },
  },
  "field-note-resolution@2": {
    root: elements.fieldNoteCutaway,
    outcomeButton: elements.fieldNoteCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentFieldNoteCutawayPacket((candidate as FieldNoteResolutionLiveTellCutawayCandidate).packet),
    presentPhase: (phase) => presentFieldNoteCutawayPhase(phase as FieldNoteCutawayPhase),
    finish: (candidate) => finishFieldNoteCutaway((candidate as FieldNoteResolutionLiveTellCutawayCandidate).packet),
  },
  "ability-resonance@1": {
    root: elements.abilityResonanceCutaway,
    outcomeButton: elements.abilityResonanceCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentAbilityResonancePacket((candidate as AbilityResonanceCutawayCandidate).packet),
    presentPhase: (phase) => presentAbilityResonancePhase(phase as AbilityResonanceCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as AbilityResonanceCutawayCandidate).packet;
      elements.abilityResonanceCutaway.dataset.active = "false";
      elements.abilityResonanceCutawayOutcome.hidden = true;
      elements.abilityResonanceCutawayOutcome.disabled = true;
      presentAbilityResonancePhase("final");
      elements.abilityResonanceCutawayAnnouncement.textContent = `${packet.abilityName} reached Ability Level 20. ${packet.crossingActionLevel === 19 ? "The crossing action used Level 19; " : ""}subsequent uses read Level 20 with ${abilityResonanceMechanicalDelta(packet)}. No new ability or branch was granted.`;
    },
  },
  "weapon-memory@1": {
    root: elements.weaponMemoryCutaway,
    outcomeButton: elements.weaponMemoryCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentWeaponMemoryPacket((candidate as WeaponMemoryCutawayCandidate).packet),
    presentPhase: (phase) => presentWeaponMemoryPhase(phase as WeaponMemoryCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as WeaponMemoryCutawayCandidate).packet;
      const final = packet.receipts.find((receipt) => receipt.id === packet.finalReceiptId);
      if (final === undefined) throw new Error("Weapon-memory packet lost its final receipt");
      elements.weaponMemoryCutaway.dataset.active = "false";
      elements.weaponMemoryCutawayOutcome.hidden = true;
      elements.weaponMemoryCutawayOutcome.disabled = true;
      presentWeaponMemoryPhase("final");
      elements.weaponMemoryCutawayAnnouncement.textContent = `${packet.heroName} remembers the road carried with ${packet.weaponName}. Use Mastery 10 of 10 across 45 recorded encounters. Final outcome: ${final.outcome}. No combat bonus.`;
    },
  },
  "battle-spoils@1": {
    root: elements.battleSpoilsCutaway,
    outcomeButton: elements.battleSpoilsCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentBattleSpoilsPacket((candidate as BattleSpoilsCutawayCandidate).packet),
    presentPhase: (phase) => presentBattleSpoilsPhase(phase as BattleSpoilsCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as BattleSpoilsCutawayCandidate).packet;
      elements.battleSpoilsCutaway.dataset.active = "false";
      elements.battleSpoilsCutawayOutcome.hidden = true;
      elements.battleSpoilsCutawayOutcome.disabled = true;
      presentBattleSpoilsPhase("final");
      const continuity = packet.oldItem === null
        ? `The previously empty ${packet.slot} slot is filled.`
        : `${packet.oldItem.name} remains in Inventory.`;
      elements.battleSpoilsCutawayAnnouncement.textContent = `${packet.newItem.name} was auto-equipped in the ${packet.slot} slot after combat. ${battleSpoilsDeltaText(packet)}. ${continuity}`;
    },
  },
  "town-itinerary@1": {
    root: elements.townItineraryCutaway,
    outcomeButton: elements.townItineraryCutawayOutcome,
    prepare: () => null,
    present: (candidate) => presentTownItineraryPacket((candidate as TownItineraryCutawayCandidate).packet),
    presentPhase: (phase) => presentTownItineraryPhase(phase as TownItineraryCutawayPhase),
    finish: (candidate) => {
      const packet = (candidate as TownItineraryCutawayCandidate).packet;
      elements.townItineraryCutaway.dataset.active = "false";
      elements.townItineraryCutawayOutcome.hidden = true;
      elements.townItineraryCutawayOutcome.disabled = true;
      presentTownItineraryPhase("settled");
      elements.townItineraryCutawayAnnouncement.textContent = `${packet.hero.name} followed ${packet.routeStops.length} ${packet.routeStops.length === 1 ? "stop" : "stops"} through ${packet.district.name} and met ${packet.resident.name}, ${packet.resident.role}, at ${packet.building.name}. Visit ${packet.visit.after}; reputation ${packet.reputation.after}.`;
    },
  },
};

const cutawayAdapterManifest = cutawayRegistry.recipes.map((recipe) => {
  const adapter = cutawayAdapters[recipe.key as ProductionCutawayRecipeKey];
  return {
    recipeKey: recipe.key,
    domEquivalentId: adapter.root.id,
    truthCueIds: recipe.truthCueIds.filter((id) => adapter.root.querySelector(`#${id}`) !== null),
  };
});
if (!validateCutawayAdapterManifest(cutawayRegistry, cutawayAdapterManifest)) {
  throw new Error("Cutaway DOM adapters do not match the production registry");
}

function hideCutawayAdapterRoots(): void {
  for (const root of new Set(Object.values(cutawayAdapters).map((adapter) => adapter.root))) {
    root.hidden = true;
  }
}

function syncCutawayBusy(): void {
  presentationBusy = isCutawayBusy(cutawayController);
  elements.app.dataset.presentationBusy = String(presentationBusy);
  syncReparteePresentation();
  syncNarratorPresentationContext();
}

function finishCutaway(candidate: ProductionCutawayCandidate, generation: number): void {
  const completed = completeActiveCutaway(cutawayController, candidate, generation);
  if (completed.action === "stale") return;
  const adapter = cutawayAdapters[candidate.recipeKey];
  const restoreOutcomeFocus = document.activeElement === adapter.outcomeButton;
  adapter.finish(candidate);
  cutawayController = completed.state;
  syncCutawayBusy();
  if (restoreOutcomeFocus) viewButtons.find((button) => button.dataset.view === "watch")?.focus();
  lastAdvanceAtMs = Date.now();
  cutawayPausedAtMs = null;
  const next = cutawayController.queue.active as ProductionCutawayCandidate | null;
  if (next !== null) {
    beginCutaway(next);
  } else if (catchUpAfterPresentation) {
    void resumeDeferredCatchUp();
  } else {
    presentNarratorScene();
    requestNarrativeCheck();
  }
}

function beginCutaway(candidate: ProductionCutawayCandidate): void {
  const adapter = cutawayAdapters[candidate.recipeKey];
  const staging = adapter.prepare(candidate);
  const generation = cutawayController.generation;
  syncCutawayBusy();
  cutawayStartedAtMs = Date.now();
  cutawayPausedAtMs = startupHold || paused || presentationSuspended ? cutawayStartedAtMs : null;
  delete elements.stage.dataset.cutawayFallback;
  delete elements.stage.dataset.cutawayFallbackEvent;
  hideCutawayAdapterRoots();
  adapter.present(candidate, staging);
  const started = renderer.startCutaway(candidate, {
    fast: fastMode,
    staging,
    onPhase: (phase) => {
      if (generation === cutawayController.generation) adapter.presentPhase(phase);
    },
    onComplete: () => finishCutaway(candidate, generation),
  });
  if (!started) finishCutaway(candidate, generation);
}

function enqueueCutaway(candidate: ProductionCutawayCandidate): void {
  if (activeView !== "watch") return;
  const offered = offerCommittedCutaway(cutawayRegistry, cutawayController, candidate);
  cutawayController = offered.state;
  if (offered.action === "fallback") {
    const fallback = offered.resolution.staticEnvelope;
    if (fallback !== null) {
      elements.stage.dataset.cutawayFallback = "static-chronicle";
      elements.stage.dataset.cutawayFallbackEvent = fallback.eventId;
      elements.viewAnnouncement.textContent = `${fallback.headline}. ${fallback.action} ${fallback.consequence}`;
    }
    syncCutawayBusy();
    return;
  }
  if (offered.action === "start") beginCutaway(candidate);
}

function settleActiveCutaway(promotePending = true): void {
  if (!promotePending) cutawayController = discardPendingCutawayPresentation(cutawayController);
  const active = cutawayController.queue.active as ProductionCutawayCandidate | null;
  if (active === null) {
    syncCutawayBusy();
    return;
  }
  const generation = cutawayController.generation;
  if (!renderer.settleCutaway()) finishCutaway(active, generation);
}

function cancelCutawayPresentation(): void {
  elements.elsewhereLoafCutaway.hidden = true;
  elements.elsewhereLoafCutaway.dataset.active = "false";
  elements.elsewhereLoafOutcome.disabled = true;
  delete elements.app.dataset.elsewhereLoaf;
  cutawayController = cancelCutawayController(cutawayController);
  trapCutawayFatigueMemory = createTrapCutawayFatigueMemory();
  syncCutawayBusy();
  cutawayStartedAtMs = 0;
  cutawayPausedAtMs = null;
  catchUpAfterPresentation = false;
  delete elements.stage.dataset.cutawayFallback;
  delete elements.stage.dataset.cutawayFallbackEvent;
  elements.trapCutaway.hidden = true;
  elements.trapCutaway.dataset.active = "false";
  delete elements.trapCutaway.dataset.shot;
  delete elements.trapCutaway.dataset.flavor;
  elements.trapCutawayAnnouncement.textContent = "";
  elements.farewellCutaway.hidden = true;
  elements.farewellCutaway.dataset.active = "false";
  delete elements.farewellCutaway.dataset.eventId;
  delete elements.farewellCutaway.dataset.outcome;
  delete elements.farewellCutaway.dataset.companionId;
  delete elements.farewellCutaway.dataset.profession;
  delete elements.farewellCutaway.dataset.phase;
  elements.farewellCutawayAnnouncement.textContent = "";
  elements.farewellCutawayOutcome.hidden = true;
  elements.farewellCutawayOutcome.disabled = true;
  elements.levelUpCutaway.hidden = true;
  elements.levelUpCutaway.dataset.active = "false";
  delete elements.levelUpCutaway.dataset.eventId;
  delete elements.levelUpCutaway.dataset.emphasis;
  delete elements.levelUpCutaway.dataset.progressionBand;
  delete elements.levelUpCutaway.dataset.phase;
  elements.levelUpCutawayAnnouncement.textContent = "";
  elements.levelUpCutawayOutcome.hidden = true;
  elements.levelUpCutawayOutcome.disabled = true;
  elements.abilityResonanceCutaway.hidden = true;
  elements.abilityResonanceCutaway.dataset.active = "false";
  delete elements.abilityResonanceCutaway.dataset.eventId;
  delete elements.abilityResonanceCutaway.dataset.abilityId;
  delete elements.abilityResonanceCutaway.dataset.sourceKind;
  delete elements.abilityResonanceCutaway.dataset.effect;
  delete elements.abilityResonanceCutaway.dataset.experience;
  delete elements.abilityResonanceCutaway.dataset.uses;
  delete elements.abilityResonanceCutaway.dataset.damageContribution;
  delete elements.abilityResonanceCutaway.dataset.statusPotency;
  delete elements.abilityResonanceCutaway.dataset.provenance;
  delete elements.abilityResonanceCutaway.dataset.phase;
  elements.abilityResonanceCutawayAnnouncement.textContent = "";
  elements.abilityResonanceCutawayOutcome.hidden = true;
  elements.abilityResonanceCutawayOutcome.disabled = true;
  elements.weaponMemoryCutaway.hidden = true;
  elements.weaponMemoryCutaway.dataset.active = "false";
  delete elements.weaponMemoryCutaway.dataset.eventId;
  delete elements.weaponMemoryCutaway.dataset.outcome;
  delete elements.weaponMemoryCutaway.dataset.weaponId;
  delete elements.weaponMemoryCutaway.dataset.phase;
  elements.weaponMemoryCutawayAnnouncement.textContent = "";
  elements.weaponMemoryCutawayOutcome.hidden = true;
  elements.weaponMemoryCutawayOutcome.disabled = true;
  elements.battleSpoilsCutaway.hidden = true;
  elements.battleSpoilsCutaway.dataset.active = "false";
  delete elements.battleSpoilsCutaway.dataset.eventId;
  delete elements.battleSpoilsCutaway.dataset.combatId;
  delete elements.battleSpoilsCutaway.dataset.slot;
  delete elements.battleSpoilsCutaway.dataset.oldItem;
  delete elements.battleSpoilsCutaway.dataset.newItem;
  delete elements.battleSpoilsCutaway.dataset.phase;
  elements.battleSpoilsCutawayAnnouncement.textContent = "";
  elements.battleSpoilsCutawayOutcome.hidden = true;
  elements.battleSpoilsCutawayOutcome.disabled = true;
  elements.townItineraryCutaway.hidden = true;
  elements.townItineraryCutaway.dataset.active = "false";
  delete elements.townItineraryCutaway.dataset.eventId;
  delete elements.townItineraryCutaway.dataset.townId;
  delete elements.townItineraryCutaway.dataset.residentId;
  delete elements.townItineraryCutaway.dataset.buildingId;
  delete elements.townItineraryCutaway.dataset.districtId;
  delete elements.townItineraryCutaway.dataset.routeIds;
  delete elements.townItineraryCutaway.dataset.visit;
  delete elements.townItineraryCutaway.dataset.reputation;
  delete elements.townItineraryCutaway.dataset.phase;
  elements.townItineraryCutawayAnnouncement.textContent = "";
  elements.townItineraryCutawayOutcome.hidden = true;
  elements.townItineraryCutawayOutcome.disabled = true;
  elements.fieldNoteCutaway.hidden = true;
  elements.fieldNoteCutaway.dataset.active = "false";
  delete elements.fieldNoteCutaway.dataset.eventId;
  delete elements.fieldNoteCutaway.dataset.encounterMode;
  delete elements.fieldNoteCutaway.dataset.sourceCommand;
  delete elements.fieldNoteCutaway.dataset.speciesKey;
  delete elements.fieldNoteCutaway.dataset.noteCount;
  delete elements.fieldNoteCutaway.dataset.priorEvidence;
  delete elements.fieldNoteCutaway.dataset.packetVersion;
  delete elements.fieldNoteCutaway.dataset.phase;
  clearFieldNoteLiveTellPresentation();
  elements.fieldNoteCutawayNotes.replaceChildren();
  elements.fieldNoteCutawayAnnouncement.textContent = "";
  elements.fieldNoteCutawayOutcome.hidden = true;
  elements.fieldNoteCutawayOutcome.disabled = true;
  renderer.cancelCutaway();
}

const svgNamespace = "http://www.w3.org/2000/svg";

function miniMapPolyline(line: MiniMapLine, className: string): SVGPolylineElement {
  const polyline = document.createElementNS(svgNamespace, "polyline");
  polyline.classList.add(className);
  polyline.dataset.mapId = line.id;
  polyline.setAttribute("points", line.points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" "));
  return polyline;
}

function questLeadPhaseLabel(phase: "revealed" | "routed" | "at-lead" | "resolved"): string {
  return phase === "at-lead" ? "At lead" : phase === "routed" ? "Route planned" : phase === "resolved" ? "Resolved" : "Unrouted";
}

function presentMiniMap(): void {
  const questLead = projectSuccessorQuestLead(state.seed, state.depth.atlas, state.depth.quest);
  const partyMarker = projectAtlasPartyMarker(state.depth);
  const miniMap = projectMiniMap(state.depth.atlas, partyMarker, questLead);
  elements.miniMap.setAttribute("aria-label", miniMap.ariaLabel);
  elements.miniMapPlace.textContent = miniMap.currentPlace;
  elements.miniMapRoute.textContent = miniMap.routeSummary;
  const coastLayer = document.createElementNS(svgNamespace, "g");
  coastLayer.classList.add("mini-map-coasts");
  coastLayer.append(...miniMap.coastlines.map((coastline) => miniMapPolyline(coastline, "mini-map-coast")));
  const riverLayer = document.createElementNS(svgNamespace, "g");
  riverLayer.classList.add("mini-map-rivers");
  riverLayer.append(...miniMap.rivers.map((river) => miniMapPolyline(river, "mini-map-river")));
  const roadLayer = document.createElementNS(svgNamespace, "g");
  roadLayer.classList.add("mini-map-roads");
  roadLayer.append(...miniMap.roads.map((road) => {
    const polyline = miniMapPolyline(road, "mini-map-road");
    polyline.dataset.selected = String(road.selected);
    polyline.dataset.terrain = road.terrain;
    return polyline;
  }));
  const siteLayer = document.createElementNS(svgNamespace, "g");
  siteLayer.classList.add("mini-map-sites");
  siteLayer.append(...miniMap.sites.map((site) => {
    const marker = document.createElementNS(svgNamespace, site.kind === "unknown" ? "polygon" : "circle");
    marker.classList.add("mini-map-site");
    marker.dataset.siteId = site.id;
    marker.dataset.kind = site.kind;
    marker.dataset.current = String(site.current);
    marker.dataset.destination = String(site.destination);
    marker.dataset.lead = String(site.lead);
    marker.setAttribute("aria-label", site.name);
    if (marker instanceof SVGPolygonElement) {
      marker.setAttribute("points", `${site.x},${site.y - 3.3} ${site.x + 3.3},${site.y} ${site.x},${site.y + 3.3} ${site.x - 3.3},${site.y}`);
    } else {
      marker.setAttribute("cx", String(site.x));
      marker.setAttribute("cy", String(site.y));
      marker.setAttribute("r", site.current || site.destination ? "3" : "2.2");
    }
    return marker;
  }));
  const partyHalo = document.createElementNS(svgNamespace, "circle");
  partyHalo.classList.add("mini-map-party-halo");
  partyHalo.setAttribute("cx", String(miniMap.party.x));
  partyHalo.setAttribute("cy", String(miniMap.party.y));
  partyHalo.setAttribute("r", "5.5");
  const party = document.createElementNS(svgNamespace, "g");
  party.classList.add("mini-map-party-marker");
  party.dataset.partyMarker = "true";
  if (partyMarker !== null) {
    party.dataset.partySize = partyMarker.formation.kind === "solo" ? "1" : "2";
    party.dataset.formation = partyMarker.formation.kind;
    party.dataset.motion = "static";
    party.setAttribute("aria-label", partyMarker.accessibleText);
    const companion = partyMarker.formation.companion;
    if (companion !== null) {
      party.dataset.companionId = companion.id;
      party.dataset.companionStatus = companion.status;
    }
    const supportLink = projectAtlasPartySupportLink(partyMarker);
    if (supportLink !== null) {
      const support = document.createElementNS(svgNamespace, "path");
      support.classList.add("mini-map-party-support");
      support.dataset.partySupport = "true";
      support.setAttribute(
        "d",
        `M ${(miniMap.party.x + supportLink.fromX).toFixed(2)} ${(miniMap.party.y + supportLink.fromY).toFixed(2)} L ${(miniMap.party.x + supportLink.toX).toFixed(2)} ${(miniMap.party.y + supportLink.toY).toFixed(2)}`,
      );
      party.append(support);
    }
    for (const member of projectAtlasPartyGlyphs(partyMarker)) {
      const glyph = document.createElementNS(svgNamespace, "g");
      glyph.classList.add("mini-map-party-member", `mini-map-party-member--${member.kind}`);
      glyph.dataset.member = member.kind;
      glyph.dataset.pose = member.pose;
      glyph.setAttribute("transform", `translate(${(miniMap.party.x + member.offsetX).toFixed(2)} ${(miniMap.party.y + member.offsetY).toFixed(2)})`);
      const head = document.createElementNS(svgNamespace, member.kind === "hero" ? "circle" : "polygon");
      head.classList.add("mini-map-party-head");
      if (head instanceof SVGCircleElement) {
        head.setAttribute("cx", "0");
        head.setAttribute("cy", "-1.8");
        head.setAttribute("r", "1.25");
      } else {
        head.setAttribute("points", "0,-3.1 1.25,-1.8 0,-0.5 -1.25,-1.8");
      }
      const body = document.createElementNS(svgNamespace, "path");
      body.classList.add("mini-map-party-body");
      body.setAttribute("d", member.pose === "supported" ? "M -1 -0.1 L 1.5 1.3 L 2 3" : "M 0 -0.3 L 0 2.8 M -1.5 0.7 L 1.5 0.7 M 0 2.8 L -1.25 4 M 0 2.8 L 1.25 4");
      glyph.append(head, body);
      party.append(glyph);
    }
  }
  elements.miniMapGraphic.replaceChildren(
    coastLayer,
    riverLayer,
    roadLayer,
    ...(partyMarker === null ? [siteLayer] : [partyHalo, siteLayer, party]),
  );
}

function championInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
}

function presentHallOfChampions(): void {
  const hall = projectHallOfChampions(champions);
  const mentorArc = state.legacyManifestations.mentorArc;
  const admittedChampionIds = new Set(state.legacy.cards.map((card) => card.sourceChampionId));
  const appearedChampionIds = new Set(state.legacyManifestations.appearances.map((appearance) => appearance.sourceChampionId));
  const appearedCount = appearedChampionIds.size;
  const remainingCount = Math.max(0, state.legacy.cards.length - appearedCount);
  elements.hallTotal.textContent = String(hall.totalCount);
  elements.hallEarned.textContent = String(hall.earnedCount);
  elements.hallAdopted.textContent = String(hall.adoptedCount);
  elements.hallAdmitted.textContent = String(state.legacy.cards.length);
  elements.hallSummary.textContent = hall.totalCount === 0
    ? "No champion has reached Level 1000 in this browser. Eternal adventures keep their own pace."
    : `${hall.totalCount} immutable ${hall.totalCount === 1 ? "champion is" : "champions are"} remembered here; their source adventures continue.`;

  elements.hallLegacySummary.textContent = state.legacy.cards.length === 0
    ? "No prior Champion was admitted when this adventure began. No power is inherited."
    : `${state.legacy.cards.length} selected · ${appearedCount} appeared · ${remainingCount} still eligible · ${mentorArc === null ? "no recurring road" : mentorArc.memoryFact === null ? "1 mentor road unfolding" : "1 mentor memory kept"}. No stats, gear, gold, quests, or powers were imported.`;
  elements.hallLegacyGrid.replaceChildren(...state.legacy.cards.map((record) => {
    const appearance = state.legacyManifestations.appearances.find((fact) => fact.legendId === record.id);
    const meeting = appearance === undefined
      ? undefined
      : state.legacyManifestations.meetings.find((fact) => fact.appearanceId === appearance.id);
    const recognition = meeting === undefined
      ? undefined
      : state.legacyManifestations.recognitions.find((fact) => fact.meetingId === meeting.id);
    const lesson = meeting === undefined
      ? undefined
      : state.legacyManifestations.lessons.find((fact) => fact.meetingId === meeting.id);
    const relationship = mentorArc?.legendId === record.id ? mentorArc : null;
    const relationshipPhase = relationship === null
      ? "none"
      : relationship.farewellFact !== null
        ? "farewell"
        : relationship.returnFact !== null
          ? "return"
          : relationship.promiseFact !== null
            ? "promise"
            : "meeting";
    const card = document.createElement("li");
    card.className = "hall-legacy-card";
    card.dataset.legendId = record.id;
    card.dataset.sourceChampionId = record.sourceChampionId;
    card.dataset.selected = "true";
    card.dataset.appeared = String(appearance !== undefined);
    card.dataset.met = String(meeting !== undefined);
    card.dataset.recognized = String(recognition !== undefined);
    card.dataset.practiced = String(lesson !== undefined);
    card.dataset.mentorPhase = relationshipPhase;
    card.dataset.promised = String(relationship?.promiseFact !== null && relationship?.promiseFact !== undefined);
    card.dataset.returned = String(relationship?.returnFact !== null && relationship?.returnFact !== undefined);
    card.dataset.farewelled = String(relationship?.farewellFact !== null && relationship?.farewellFact !== undefined);
    card.dataset.memory = relationship?.memoryFact?.memory ?? "none";
    card.dataset.importedPower = String(lesson?.importedPower ?? false);
    const crest = document.createElement("span");
    crest.className = "hall-legacy-crest";
    crest.textContent = championInitials(record.heroName);
    const copy = document.createElement("div");
    const name = document.createElement("h4");
    name.textContent = record.heroName;
    const identity = document.createElement("p");
    identity.textContent = `${record.className} · Level ${record.level} · ${legendQualificationLabel(record.qualification)}`;
    const signature = document.createElement("small");
    const manifestationSummary = appearance === undefined
      ? record.signatureAbility === null
        ? "Candidate · not appeared · no signature art recorded · no power imported"
        : `Candidate · not appeared · archive remembers ${record.signatureAbility.abilityName} · no power imported`
      : `Appeared T${appearance.tick} · Met: ${meeting === undefined ? "no" : "witnessed demonstration"} · Recognition: ${recognition?.recognition ?? "none"} · Belief: ${recognition?.belief ?? "none"} · Practiced: ${lesson?.abilityName ?? "none"} · no power transferred`;
    const relationshipSummary = relationship === null
      ? ""
      : relationship.farewellFact !== null && relationship.memoryFact !== null
        ? ` · Roads parted T${relationship.farewellFact.tick} · Memory: kept-road-promise · no reward or power`
        : relationship.returnFact !== null
          ? ` · Promise kept T${relationship.returnFact.tick} after ${relationship.returnFact.completedQuestCount - relationship.returnFact.completedQuestBaseline} completed chapter · farewell still ahead`
          : relationship.promiseFact !== null
            ? ` · Promise T${relationship.promiseFact.tick}: return after the next completed chapter · road visit ${relationship.promiseFact.townVisitOrdinal}/${relationship.promiseFact.scheduledTownVisit}`
            : " · First meeting remembered · no future promise was fabricated";
    signature.textContent = manifestationSummary + relationshipSummary;
    copy.append(name, identity, signature);
    card.append(crest, copy);
    return card;
  }));

  const cards = hall.champions.map((record) => {
    const card = document.createElement("li");
    card.className = "hall-champion";
    card.dataset.championId = record.id;
    card.dataset.qualification = record.qualification;
    card.dataset.recordedTick = String(record.recordedTick);
    card.dataset.campaignLegacy = String(admittedChampionIds.has(record.id));

    const identity = projectHeroIdentityAppearance({ id: record.heroId });
    const portrait = document.createElement("div");
    portrait.className = "hall-portrait";
    portrait.setAttribute("aria-hidden", "true");
    portrait.style.setProperty("--champion-skin", cssColor(identity.skin));
    portrait.style.setProperty("--champion-hair", cssColor(identity.hair));
    portrait.style.setProperty("--champion-cloth", cssColor(identity.tunic));
    portrait.style.setProperty("--champion-cloak", cssColor(identity.cloak));
    const crest = document.createElement("span");
    crest.className = "hall-crest";
    crest.textContent = championInitials(record.heroName);
    const silhouette = document.createElement("span");
    silhouette.className = "hall-silhouette";
    const gear = document.createElement("span");
    gear.className = "hall-gear-marks";
    for (const archived of record.equipment) {
      const appearance = projectGearAppearance({
        id: archived.itemId,
        name: archived.itemName,
        kind: "equipment",
        slot: archived.slot,
        rarity: archived.rarity,
        quantity: 1,
        modifiers: {},
        restorative: null,
        useMastery: archived.slot === "weapon" ? {
          schemaVersion: 1,
          rulesVersion: "weapon-effective-use-v1",
          level: 1,
          experience: 0,
          receipts: [],
        } : null,
      });
      if (appearance === null) continue;
      const mark = document.createElement("i");
      mark.dataset.slot = archived.slot;
      mark.dataset.silhouette = appearance.silhouette;
      mark.style.setProperty("--gear-color", cssColor(appearance.color));
      mark.title = `${archived.itemName} · ${appearance.silhouette}`;
      gear.append(mark);
    }
    portrait.append(crest, silhouette, gear);

    const article = document.createElement("article");
    const heading = document.createElement("header");
    const title = document.createElement("div");
    const name = document.createElement("h3");
    name.textContent = record.heroName;
    const classAndLevel = document.createElement("p");
    classAndLevel.textContent = `${record.className} · Level ${record.level}`;
    title.append(name, classAndLevel);
    const qualification = document.createElement("span");
    qualification.className = "hall-qualification";
    qualification.textContent = appearedChampionIds.has(record.id)
      ? "Appeared in this tale"
      : admittedChampionIds.has(record.id)
        ? "Story candidate"
      : record.qualification === "earned" ? "Inducted" : "Recovered save";
    heading.append(title, qualification);

    const facts = document.createElement("dl");
    for (const [label, value] of [
      ["Recorded", `T${record.recordedTick}`],
      ["Experience", String(record.experience)],
      ["Chapters", String(record.totalCompletedQuests)],
    ] as const) {
      const fact = document.createElement("div");
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = value;
      fact.append(term, description);
      facts.append(fact);
    }

    const equipment = document.createElement("p");
    equipment.className = "hall-equipment";
    equipment.textContent = record.equipment.length === 0
      ? "Archived gear · no equipped items"
      : `Archived gear · ${record.equipment.map((item) => item.itemName).join(" · ")}`;
    const abilities = document.createElement("ul");
    abilities.className = "hall-abilities";
    if (record.abilities.length === 0) {
      const item = document.createElement("li");
      item.textContent = "No archived signature arts";
      abilities.append(item);
    } else {
      abilities.append(...record.abilities.map((ability) => {
        const item = document.createElement("li");
        item.dataset.abilityId = ability.abilityId;
        item.textContent = `${ability.abilityName} · ${ability.kind} · ${ability.effect} · L${ability.level}`;
        return item;
      }));
    }
    const note = document.createElement("small");
    const legacyPrefix = appearedChampionIds.has(record.id)
      ? "Appeared in this tale as a mortal mentor · no power imported · "
      : admittedChampionIds.has(record.id)
        ? "Selected once at this campaign's creation · no power imported · "
        : "";
    note.textContent = legacyPrefix + (record.qualification === "earned"
      ? `Earned by ${record.sourceCommandType} · deed ${record.sourceCommandId} · current-browser record · Eternal campaign not retired`
      : "Existing Level 1000 save adopted · source deed unavailable in released save · current-browser record · Eternal campaign not retired");
    article.append(heading, facts, equipment, abilities, note);
    card.append(portrait, article);
    return card;
  });
  if (cards.length === 0) {
    const empty = document.createElement("li");
    empty.className = "hall-empty";
    empty.textContent = "The first name will be carved when a hero reaches Level 1000. No hero is retired or replaced.";
    cards.push(empty);
  }
  elements.hallGrid.replaceChildren(...cards);
  elements.hallOverflow.hidden = hall.hiddenCount === 0;
  elements.hallOverflow.textContent = hall.hiddenCount === 0
    ? ""
    : `${hall.hiddenCount} earlier ${hall.hiddenCount === 1 ? "champion remains" : "champions remain"} safely archived outside this bounded gallery.`;
}

function presentViewScreens(): void {
  const scrollTop = elements.inspectionScreen.scrollTop;
  presentMiniMap();
  const map = projectMapView(state);
  elements.mapTitle.textContent = map.destination === null ? "The known world" : `Road to ${map.destination}`;
  elements.mapCurrentPlace.textContent = map.currentLeg ?? map.currentPlace;
  elements.mapParty.textContent = map.party?.accessibleText ?? "Party marker unavailable.";
  elements.mapParty.dataset.partySize = map.party?.formation.kind === "solo" ? "1" : map.party === null ? "0" : "2";
  elements.mapParty.dataset.formation = map.party?.formation.kind ?? "unavailable";
  const mapCompanion = map.party?.formation.companion ?? null;
  if (mapCompanion === null) {
    delete elements.mapParty.dataset.companionId;
    delete elements.mapParty.dataset.companionStatus;
  } else {
    elements.mapParty.dataset.companionId = mapCompanion.id;
    elements.mapParty.dataset.companionStatus = mapCompanion.status;
  }
  elements.mapQuestLead.hidden = map.questLead === null;
  elements.mapQuestLead.textContent = map.questLead === null
    ? ""
    : `Quest lead · ${map.questLead.locationName} · ${questLeadPhaseLabel(map.questLead.phase)}${map.questLead.discovered ? "" : " · rumored site"}`;
  elements.mapQuestLead.title = map.questLead === null ? "" : `Quest lead at ${map.questLead.locationName}: ${questLeadPhaseLabel(map.questLead.phase)}`;
  elements.mapRoute.textContent = map.nextLegDanger === null
    ? map.progress
    : `${map.progress} · Known place danger ${map.nextLegDanger.score} ${map.nextLegDanger.label}`;
  if (map.nextLegDanger === null) {
    delete elements.mapRoute.dataset.danger;
    delete elements.mapRoute.dataset.threatBand;
  } else {
    elements.mapRoute.dataset.danger = String(map.nextLegDanger.score);
    elements.mapRoute.dataset.threatBand = map.nextLegDanger.band;
  }
  elements.mapDiscovery.textContent = `${map.discovered} · ${map.terrain}`;
  atlasGazetteerView.render(state.depth, state.campaignId);

  const inventory = projectInventoryView(state);
  elements.inventoryTitle.textContent = inventory.heroName;
  elements.inventoryClass.textContent = inventory.classAndLevel;
  elements.inventoryGold.textContent = String(inventory.gold);
  elements.inventoryStacks.textContent = String(inventory.stackCount);
  elements.inventoryItems.textContent = String(inventory.itemCount);
  elements.inventoryEquipped.textContent = String(inventory.equippedCount);
  elements.inventoryGrid.replaceChildren(
    ...inventory.items.map((projected) => {
      const item = document.createElement("li");
      item.className = "inventory-item";
      item.dataset.itemId = projected.id;
      item.dataset.rarity = projected.rarity;
      item.dataset.equipped = String(projected.equippedSlot !== null);
      const canonicalItem = state.depth.hero.inventory.find((candidate) => candidate.id === projected.id);
      const familiarForm = canonicalItem === undefined ? null : projectFamiliarWeaponForm(canonicalItem);
      if (familiarForm !== null) {
        item.dataset.weaponFormId = familiarForm.formId;
        item.dataset.weaponFormSilhouette = familiarForm.silhouette;
        item.dataset.weaponFormUnlockReceipt = familiarForm.unlockReceiptId;
        item.dataset.weaponFormBonus = String(familiarForm.mechanicalBonus);
      }
      const header = document.createElement("header");
      const name = document.createElement("h3");
      name.textContent = projected.name;
      const quantity = document.createElement("span");
      quantity.textContent = `×${projected.quantity}`;
      header.append(name, quantity);
      const kind = document.createElement("p");
      kind.className = "item-kind";
      kind.textContent = `${projected.rarity} · ${projected.slot ?? projected.kind}`;
      const equipped = document.createElement("p");
      equipped.className = "item-equipped";
      equipped.textContent = projected.equippedSlot === null ? "Carried" : `Equipped · ${projected.equippedSlot}`;
      const modifiers = document.createElement("p");
      modifiers.className = "item-modifiers";
      modifiers.textContent = projected.food ?? projected.dungeonTool ?? projected.restorative ?? (projected.modifiers.length === 0
        ? "No stat modifiers"
        : projected.modifiers.map((modifier) => modifierLabel(modifier.name, modifier.value)).join(" · "));
      const mastery = document.createElement("p");
      mastery.className = "item-mastery";
      mastery.hidden = projected.useMastery === null;
      if (projected.useMastery?.latestSource !== null && projected.useMastery !== null) {
        mastery.dataset.sourceCombat = projected.useMastery.latestSource.combatId;
        mastery.title = `Latest effective use: ${projected.useMastery.latestSource.combatId} · T${projected.useMastery.latestSource.resolvedTick} · ${projected.useMastery.latestSource.outcome}`;
      }
      const familiarFormCopy = familiarForm === null
        ? " · no combat bonus"
        : ` · Familiar Form · ${familiarForm.formName} · unlocked at Use L4 · visual handling only · no combat bonus`;
      mastery.textContent = projected.useMastery === null
        ? ""
        : projected.useMastery.nextExperience === null
          ? `Use Mastery L10 / 10 · ${projected.useMastery.experience} / 45 XP · mastery cap${familiarFormCopy}`
          : `Use Mastery L${projected.useMastery.level} / 10 · ${projected.useMastery.experience} / ${projected.useMastery.nextExperience} toward L${projected.useMastery.level + 1}${familiarFormCopy}${projected.useMastery.latestSource === null ? " · no effective use recorded" : ` · latest use T${projected.useMastery.latestSource.resolvedTick} · ${projected.useMastery.latestSource.outcome}`}`;
      item.append(header, kind, equipped, modifiers, mastery);
      return item;
    }),
  );

  narrativeJournalView.render();
  statusHistoryView.render(state);
  chroniclePlateView.render();
  const journal = projectJournalView(state);
  const heroGrowth = projectHeroGrowth(state.depth.heroGrowth, state.depth.hero);
  const party = projectParty(state.depth);
  const activeCompanion = party.active;
  elements.companionCard.hidden = activeCompanion === null;
  delete elements.companionCard.dataset.companionId;
  delete elements.companionCard.dataset.status;
  delete elements.companionCard.dataset.injured;
  delete elements.companionCard.dataset.destination;
  delete elements.companionCard.dataset.health;
  delete elements.companionCard.dataset.combatKit;
  delete elements.companionCard.dataset.roadcraft;
  delete elements.companionCard.dataset.roadcraftRetainedCombats;
  delete elements.companionCard.dataset.roadcraftFlourUses;
  delete elements.companionCard.dataset.roadcraftScreenedHits;
  delete elements.companionCard.dataset.roadcraftDamagePrevented;
  delete elements.companionCard.dataset.roadcraftMillstoneUses;
  delete elements.companionCard.dataset.roadcraftAffectedAttacks;
  delete elements.companionCard.dataset.roadcraftUnmeasured;
  if (activeCompanion !== null) {
    elements.companionCard.dataset.companionId = activeCompanion.id;
    elements.companionCard.dataset.status = activeCompanion.status;
    elements.companionCard.dataset.injured = String(isInjuredPartyStatus(activeCompanion.status));
    elements.companionCard.dataset.destination = activeCompanion.destination.locationId;
    elements.companionCard.dataset.health = `${activeCompanion.health}/${activeCompanion.maxHealth}`;
    elements.companionCard.dataset.combatKit = activeCompanion.combatKitId;
    elements.companionName.textContent = activeCompanion.name;
    elements.companionStatus.textContent = activeCompanion.status;
    elements.companionRole.textContent = activeCompanion.combatKitId === "miller-roadcraft"
      ? "miller · Roadcraft V1"
      : activeCompanion.role;
    elements.companionRole.title = `${activeCompanion.combatKitText} · ${activeCompanion.combatActionTexts.join(" · ")}`;
    elements.companionHealthText.textContent = `${activeCompanion.health}/${activeCompanion.maxHealth}`;
    elements.companionHealthBar.max = activeCompanion.maxHealth;
    elements.companionHealthBar.value = activeCompanion.health;
    elements.companionHealthBar.setAttribute("aria-label", `${activeCompanion.name} health ${activeCompanion.health} of ${activeCompanion.maxHealth}`);
    const roadcraftRuntime = state.depth.combat?.companionActionRuntime?.actorId === activeCompanion.id
      ? state.depth.combat.companionActionRuntime
      : undefined;
    const roadcraftReadiness = activeCompanion.combatKitId !== "miller-roadcraft"
      ? ""
      : isInjuredPartyStatus(activeCompanion.status)
        ? "Flour Veil UNAVAILABLE · Millstone Drag UNAVAILABLE"
      : (["flour-veil", "millstone-drag"] as const).map((actionId) => {
          const name = actionId === "flour-veil" ? "Flour Veil" : "Millstone Drag";
          const readyRound = roadcraftRuntime?.readyRounds[actionId] ?? 1;
          const currentRound = state.depth.combat?.round ?? 1;
          return `${name} ${readyRound <= currentRound ? "READY" : `CD ${companionActionDefinition(actionId).cooldownRounds}R`}`;
        }).join(" · ");
    if (roadcraftReadiness !== "") elements.companionCard.dataset.roadcraft = roadcraftReadiness;
    elements.companionPurpose.textContent = `${activeCompanion.statusText}${roadcraftReadiness === "" ? "" : ` · ${roadcraftReadiness}`}`;
    elements.companionOrigin.textContent = activeCompanion.origin.name;
    elements.companionDestination.textContent = activeCompanion.destination.name;
    elements.companionVictories.textContent = String(activeCompanion.victories);
    elements.companionBond.textContent = String(activeCompanion.bond);
    const effectiveness = activeCompanion.roadcraftEffectiveness;
    if (effectiveness !== null) {
      elements.companionCard.dataset.roadcraftRetainedCombats = String(effectiveness.retainedCombatCount);
      elements.companionCard.dataset.roadcraftFlourUses = String(effectiveness.flourVeilUses);
      elements.companionCard.dataset.roadcraftScreenedHits = String(effectiveness.flourScreenedHits);
      elements.companionCard.dataset.roadcraftDamagePrevented = String(effectiveness.damagePrevented);
      elements.companionCard.dataset.roadcraftMillstoneUses = String(effectiveness.millstoneDragUses);
      elements.companionCard.dataset.roadcraftAffectedAttacks = String(effectiveness.millstoneAffectedAttacks);
      elements.companionCard.dataset.roadcraftUnmeasured = String(effectiveness.unmeasuredImpactCount);
    }
  }
  elements.journalCompanionSummary.textContent = activeCompanion === null
    ? party.former.length === 0
      ? "No road oath has been sworn."
      : `${party.former.length} completed ${party.former.length === 1 ? "oath" : "oaths"} retained.`
    : `${activeCompanion.name} · ${activeCompanion.statusText}`;
  elements.journalCompanionActive.hidden = activeCompanion === null;
  const previousCredit = elements.journalCompanionActive.querySelector<HTMLDetailsElement>("details[data-companion-credit]")
    ?? elements.journalCompanionFormer.querySelector<HTMLDetailsElement>("details[data-companion-credit]");
  const creditRecordFocused = previousCredit?.querySelector("summary") === document.activeElement;
  const appendCreditRecord = (host: HTMLElement, residentId: string, joinedTick: number): void => {
    const creditRecord = createCompanionCreditRecord(document, state, residentId, joinedTick);
    if (creditRecord === null) return;
    creditRecord.open = previousCredit?.dataset.campaign === state.campaignId
      && previousCredit.dataset.companionCredit === creditRecord.dataset.companionCredit && previousCredit.open;
    host.append(creditRecord);
  };
  const previousCompanyRecord = elements.journalCompanionActive.firstElementChild as HTMLElement | null;
  const companyRecordOpen = previousCompanyRecord?.dataset.companionId === activeCompanion?.id
    && previousCompanyRecord?.querySelector("details")?.open === true;
  const companyRecordFocused = previousCompanyRecord?.dataset.companionId === activeCompanion?.id
    && previousCompanyRecord?.querySelector("details > summary") === document.activeElement;
  elements.journalCompanionActive.replaceChildren(...(activeCompanion === null ? [] : [(() => {
    const record = document.createElement("article");
    record.className = "journal-companion-record";
    record.dataset.companionId = activeCompanion.id;
    record.dataset.status = activeCompanion.status;
    record.dataset.injured = String(isInjuredPartyStatus(activeCompanion.status));
    const identity = document.createElement("header");
    identity.className = "company-identity";
    const portrait = document.createElement("span");
    portrait.className = "watch-portrait company-portrait";
    portrait.setAttribute("aria-hidden", "true");
    const character = projectWatchParty(state.depth.hero, activeCompanion).companion!;
    portrait.dataset.characterId = character.id;
    for (const [part, color] of Object.entries(character.appearance)) portrait.style.setProperty(`--portrait-${part}`, cssColor(color));
    for (const part of ["cloak", "face", "hair"]) {
      const layer = document.createElement("i");
      layer.className = `watch-portrait-${part}`;
      portrait.append(layer);
    }
    const name = document.createElement("strong");
    name.textContent = activeCompanion.name;
    const role = document.createElement("span");
    role.textContent = activeCompanion.role;
    identity.append(portrait, name, role);
    const route = document.createElement("span");
    route.textContent = activeCompanion.purposeText;
    const meters = document.createElement("div");
    meters.className = "company-meters";
    for (const [id, label, kind, value, maximum] of [
      ["company-health", "HP", "health", activeCompanion.health, activeCompanion.maxHealth],
      ["company-bond", "Bond", "bond", activeCompanion.bond, 100],
    ] as const) {
      const row = document.createElement("label");
      row.htmlFor = id;
      row.textContent = `${label} ${value}/${maximum}`;
      const bar = document.createElement("progress");
      bar.id = id;
      bar.max = Math.max(1, maximum);
      bar.value = value;
      bar.setAttribute("aria-label", `${activeCompanion.name} ${kind} ${value} of ${maximum}`);
      row.append(bar);
      meters.append(row);
    }
    const victories = document.createElement("span");
    victories.textContent = `${activeCompanion.victories} victories together · ${activeCompanion.statusText}`;
    const details = document.createElement("details");
    details.className = "company-history";
    details.open = companyRecordOpen;
    const summary = document.createElement("summary");
    summary.textContent = "Roadcraft and journey record";
    const kit = document.createElement("span");
    kit.textContent = activeCompanion.combatKitText;
    const facts = document.createElement("small");
    facts.textContent = `${activeCompanion.statusText} · HP ${activeCompanion.health}/${activeCompanion.maxHealth} · ${activeCompanion.combatActionTexts.join(" · ")} · ${activeCompanion.victories} victories together · bond ${activeCompanion.bond} · joined T${activeCompanion.joinedTick}`;
    const roadcraft = document.createElement("small");
    roadcraft.className = "journal-roadcraft-record";
    roadcraft.textContent = activeCompanion.roadcraftEffectiveness === null
      ? ""
      : describeRoadcraftEffectiveness(activeCompanion.roadcraftEffectiveness);
    const bondNote = document.createElement("small");
    bondNote.textContent = "Bond records shared travel and victories, not a private feeling.";
    details.append(summary, kit, facts, bondNote, ...(activeCompanion.roadcraftEffectiveness === null ? [] : [roadcraft]));
    record.append(identity, route, meters, victories, details);
    appendCreditRecord(record, activeCompanion.id, activeCompanion.joinedTick);
    return record;
  })()]));
  if (companyRecordFocused) {
    elements.journalCompanionActive.querySelector<HTMLElement>("details > summary")?.focus({ preventScroll: true });
  }
  const previousReunion = elements.journalCompanionFormer.querySelector<HTMLDetailsElement>("details[data-companion-reunion]");
  const previousLoaf = elements.journalCompanionFormer.querySelector<HTMLDetailsElement>("details[data-elsewhere-loaf]");
  const loafRecordFocused = previousLoaf?.querySelector("summary") === document.activeElement;
  const reunionRecordFocused = previousReunion?.querySelector("summary") === document.activeElement;
  elements.journalCompanionFormer.replaceChildren(...party.former.map((companion) => {
    const item = document.createElement("li");
    item.className = "journal-companion-record";
    item.dataset.companionId = companion.id;
    item.dataset.outcome = companion.departureOutcome;
    item.dataset.injured = String(companion.departureOutcome === "injured");
    const name = document.createElement("strong");
    name.textContent = `${companion.name} · ${companion.combatKitText}`;
    const route = document.createElement("span");
    route.textContent = companion.purposeText;
    const facts = document.createElement("small");
    facts.textContent = `${companion.departureText} · ${companion.combatActionTexts.join(" · ")} · ${companion.victories} victories together · bond ${companion.bond} · T${companion.joinedTick}–T${companion.departureTick}`;
    const roadcraft = document.createElement("small");
    roadcraft.className = "journal-roadcraft-record";
    roadcraft.textContent = companion.roadcraftEffectiveness === null
      ? ""
      : describeRoadcraftEffectiveness(companion.roadcraftEffectiveness);
    item.append(name, route, facts, ...(companion.roadcraftEffectiveness === null ? [] : [roadcraft]));
    appendCreditRecord(item, companion.id, companion.joinedTick);
    const reunionRecord = createCompanionReunionRecord(document, state, companion.id, companion.joinedTick);
    if (reunionRecord !== null) {
      reunionRecord.open = previousReunion?.dataset.campaign === state.campaignId
        && previousReunion.dataset.companionReunion === reunionRecord.dataset.companionReunion
        && previousReunion.open;
      item.append(reunionRecord);
    }
    const loafRecord = createElsewhereLoafRecord(document, state, companion.id, companion.joinedTick);
    if (loafRecord !== null) {
      loafRecord.open = previousLoaf?.dataset.campaign === state.campaignId
        && previousLoaf.dataset.elsewhereLoaf === loafRecord.dataset.elsewhereLoaf && previousLoaf.open;
      item.append(loafRecord);
    }
    return item;
  }));
  const archivedCredit = state.depth.companionCredit;
  if (archivedCredit?.exchange != null
    && ![party.active, ...party.former].some((companion) => companion?.id === archivedCredit.residentId
      && companion.joinedTick === archivedCredit.joinedTick)) {
    const archived = document.createElement("li");
    archived.className = "journal-companion-record";
    archived.dataset.companionId = archivedCredit.residentId;
    archived.dataset.archivedCredit = "true";
    appendCreditRecord(archived, archivedCredit.residentId, archivedCredit.joinedTick);
    if (archived.childElementCount > 0) {
      const name = document.createElement("strong");
      name.textContent = `${archivedCredit.companionName} · retained contribution · oath joined T${archivedCredit.joinedTick}`;
      archived.prepend(name);
      elements.journalCompanionFormer.append(archived);
    }
  }
  if (reunionRecordFocused) {
    elements.journalCompanionFormer.querySelector<HTMLElement>("details[data-companion-reunion] > summary")?.focus({ preventScroll: true });
  }
  if (loafRecordFocused) elements.journalCompanionFormer.querySelector<HTMLElement>("details[data-elsewhere-loaf] > summary")?.focus({ preventScroll: true });
  if (creditRecordFocused) {
    (elements.journalCompanionActive.querySelector<HTMLElement>("details[data-companion-credit] > summary")
      ?? elements.journalCompanionFormer.querySelector<HTMLElement>("details[data-companion-credit] > summary"))?.focus({ preventScroll: true });
  }
  const mentorArc = state.legacyManifestations.mentorArc;
  const mentorLegend = mentorArc === null
    ? undefined
    : state.legacy.cards.find((candidate) => candidate.id === mentorArc.legendId);
  const mentorPhase = mentorArc === null
    ? "none"
    : mentorArc.farewellFact !== null
      ? "farewell"
      : mentorArc.returnFact !== null
        ? "return"
        : mentorArc.promiseFact !== null
          ? "promise"
          : "meeting";
  elements.journalMentorSummary.textContent = mentorArc === null || mentorLegend === undefined
    ? "No recurring mentor road has begun."
    : mentorPhase === "farewell"
      ? `${mentorLegend.heroName}'s road is complete. The kept promise remains in memory.`
      : mentorPhase === "return"
        ? `${mentorLegend.heroName} kept the promised return. One final parting remains.`
        : mentorPhase === "promise"
          ? `${mentorLegend.heroName} promised to return after the next completed chapter.`
          : `${mentorLegend.heroName}'s first meeting is remembered; no promise has yet been spoken.`;
  elements.journalMentorList.replaceChildren(...(mentorArc === null || mentorLegend === undefined ? [] : [(() => {
    const item = document.createElement("li");
    item.className = "journal-mentor-record";
    item.dataset.legendId = mentorLegend.id;
    item.dataset.phase = mentorPhase;
    item.dataset.importedPower = "false";
    item.dataset.mechanicalEffect = "none";
    item.dataset.memory = mentorArc.memoryFact?.memory ?? "none";
    const name = document.createElement("strong");
    name.textContent = `${mentorLegend.heroName} · Mortal mentor`;
    const relationship = document.createElement("span");
    relationship.textContent = mentorPhase === "farewell"
      ? "Roads parted as friends · kept-road-promise"
      : mentorPhase === "return"
        ? "Promise kept · the final road remains"
        : mentorPhase === "promise"
          ? "Promised return · after the next completed chapter"
          : "First meeting · future promise not yet spoken";
    const facts = document.createElement("small");
    facts.textContent = mentorArc.farewellFact !== null
      ? `Promise T${mentorArc.promiseFact?.tick ?? "?"} · returned T${mentorArc.returnFact?.tick ?? "?"} · farewell T${mentorArc.farewellFact.tick} · memory recorded T${mentorArc.memoryFact?.recordedTick ?? "?"} · no reward or power`
      : mentorArc.returnFact !== null
        ? `Returned T${mentorArc.returnFact.tick} · completed chapters ${mentorArc.returnFact.completedQuestBaseline}→${mentorArc.returnFact.completedQuestCount} · no reward or power`
        : mentorArc.promiseFact !== null
          ? `Promised T${mentorArc.promiseFact.tick} · visit ${mentorArc.promiseFact.townVisitOrdinal}, return due no earlier than visit ${mentorArc.promiseFact.scheduledTownVisit} and one completed chapter · no reward or power`
          : `Meeting ${mentorArc.meetingId} · migrated truth does not invent a past promise · no reward or power`;
    item.append(name, relationship, facts);
    return item;
  })()]));
  elements.journalSummary.textContent = journal.questSummary;
  elements.journalQuestLead.hidden = journal.questLead === null;
  elements.journalQuestLead.textContent = journal.questLead === null
    ? ""
    : `Quest lead · ${journal.questLead.locationName} · ${questLeadPhaseLabel(journal.questLead.phase)}${journal.questLead.discovered ? "" : " · rumored site"}`;
  elements.journalQuestLead.title = journal.questLead === null ? "" : `Quest lead at ${journal.questLead.locationName}: ${questLeadPhaseLabel(journal.questLead.phase)}`;
  elements.journalQuestList.replaceChildren(
    ...journal.quests.map((projected) => {
      const quest = document.createElement("section");
      quest.className = "journal-quest";
      quest.dataset.questId = projected.id;
      quest.dataset.status = projected.status;
      const title = document.createElement("h3");
      title.textContent = `${projected.title} · ${projected.statusLabel}`;
      const objectives = document.createElement("ul");
      objectives.append(...projected.objectives.map((projectedObjective) => {
        const objective = document.createElement("li");
        objective.dataset.status = projectedObjective.status;
        objective.dataset.ruleLabel = projectedObjective.ruleLabel;
        objective.textContent = `${projectedObjective.description} · ${projectedObjective.progress}`;
        objective.setAttribute("aria-label", `${projectedObjective.ruleLabel}: ${projectedObjective.description} · ${projectedObjective.progress}`);
        return objective;
      }));
      quest.append(title, objectives);
      return quest;
    }),
  );
  elements.journalCompletedList.replaceChildren(...journal.completedChapters.map((chapter) => {
    const item = document.createElement("li");
    item.dataset.completionId = chapter.id;
    item.dataset.questOrdinal = String(chapter.ordinal);
    const title = document.createElement("strong");
    title.textContent = `Chapter ${chapter.ordinal + 1} · ${chapter.title}`;
    const facts = document.createElement("span");
    facts.textContent = `Fulfilled T${chapter.fulfilledTick} · ${chapter.objectiveCount} objectives`;
    const reward = document.createElement("small");
    reward.textContent = chapter.rewardSummary;
    item.append(title, facts, reward);
    return item;
  }));
  elements.journalGrowthSummary.textContent = heroGrowth.summary;
  elements.journalGrowthCheckpoints.replaceChildren(...heroGrowth.checkpoints.map((checkpoint) => {
    const item = document.createElement("li");
    item.dataset.checkpoint = String(checkpoint.checkpointLevel);
    item.dataset.state = checkpoint.state;
    const level = document.createElement("strong");
    level.textContent = `LEVEL ${checkpoint.checkpointLevel}`;
    const status = document.createElement("span");
    status.textContent = `${checkpoint.mark} ${checkpoint.label}`;
    item.append(level, status);
    return item;
  }));
  elements.journalGrowthAttributes.replaceChildren(...heroGrowth.attributes.map((attribute) => {
    const fact = document.createElement("div");
    fact.dataset.attribute = attribute.key;
    const term = document.createElement("dt");
    term.textContent = attribute.label;
    const value = document.createElement("dd");
    value.textContent = `${attribute.baseline}→${attribute.current}${attribute.baseline === attribute.current ? " STAYS" : ""}`;
    fact.append(term, value);
    return fact;
  }));
  const growthRecords = heroGrowth.records.map((record) => {
    const item = document.createElement("li");
    item.dataset.recordId = record.id;
    item.dataset.checkpoint = String(record.checkpointLevel);
    item.dataset.packageId = record.packageId;
    const heading = document.createElement("header");
    const title = document.createElement("strong");
    title.textContent = `LEVEL ${record.checkpointLevel} · ${record.packageLabel} · CHOSEN`;
    const tick = document.createElement("small");
    tick.textContent = `SETTLED T${record.tick}`;
    heading.append(title, tick);
    const rationale = document.createElement("p");
    rationale.textContent = record.rationale;
    const attributes = document.createElement("span");
    attributes.textContent = record.attributeFacts.join(" · ");
    const derived = document.createElement("span");
    derived.textContent = record.derivedFacts.join(" · ");
    const health = document.createElement("span");
    health.textContent = record.healthFact;
    const mana = document.createElement("span");
    mana.textContent = record.manaFact;
    item.append(heading, rationale, attributes, derived, health, mana);
    return item;
  });
  if (growthRecords.length === 0) {
    const empty = document.createElement("li");
    empty.dataset.state = "empty";
    empty.textContent = "No recorded Turning Point choice yet. The first arrives at Level 10.";
    growthRecords.push(empty);
  }
  elements.journalGrowthRecords.replaceChildren(...growthRecords);

  const codex = projectCodexView(state);
  const sameResearchCampaign = elements.codexGrid.dataset.researchCampaign === state.campaignId;
  const previousResearch = new Map(sameResearchCampaign
    ? Array.from(elements.codexGrid.querySelectorAll<HTMLDetailsElement>(".codex-research-evidence"), (details) => [
      details.dataset.taskId,
      { open: details.open, focused: details.querySelector("summary") === document.activeElement },
    ] as const) : []);
  elements.codexRecorded.textContent = String(codex.recordedCount);
  elements.codexLearned.textContent = String(codex.learnedCount);
  elements.codexHeld.textContent = String(codex.heldCount);
  elements.codexRejected.textContent = String(codex.rejectedCount);
  elements.codexUnverified.textContent = String(codex.unverifiedCount);
  elements.codexSummary.textContent = codex.recordedCount === 0
    ? "No creature patterns recorded yet. Encountered species will appear here."
    : `${codex.recordedCount} encountered species · ${codex.learnedCount} learned · ${codex.heldCount} held · ${codex.rejectedCount} rejected.`;
  const codexCards = codex.monsters.map((projected) => {
    const card = document.createElement("li");
    card.className = "codex-monster";
    card.dataset.monsterId = projected.monsterId;
    card.dataset.techniqueStatus = projected.techniqueStatus;
    if (projected.observedOnly) card.dataset.observation = "direct";
    const portrait = document.createElement("div");
    portrait.className = "codex-portrait";
    portrait.dataset.visualKey = projected.visualKey;
    portrait.setAttribute("aria-hidden", "true");
    const body = document.createElement("span");
    body.className = "codex-creature-body";
    const feature = document.createElement("span");
    feature.className = "codex-creature-feature";
    const eyes = document.createElement("span");
    eyes.className = "codex-creature-eyes";
    portrait.append(body, feature, eyes);

    const dossier = document.createElement("article");
    const heading = document.createElement("header");
    const name = document.createElement("h3");
    name.textContent = projected.monsterName;
    const status = document.createElement("span");
    status.className = "codex-technique-status";
    status.textContent = projected.techniqueStatus === "learned"
      ? "Technique learned"
      : projected.techniqueStatus === "held"
        ? "Pattern held"
        : projected.techniqueStatus === "rejected"
          ? "Pattern rejected"
          : projected.techniqueStatus === "unverified"
            ? "Pattern unverified"
            : "Studying pattern";
    heading.append(name, status);

    const facts = document.createElement("dl");
    for (const [label, value] of [
      [projected.observedOnly ? "Encounter records" : "Battles encountered", projected.encounters],
      ["Victories studied", projected.victories],
    ] as const) {
      const fact = document.createElement("div");
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = String(value);
      fact.append(term, description);
      facts.append(fact);
    }

    const habit = document.createElement("section");
    habit.className = "codex-habit";
    habit.dataset.status = projected.habit?.status ?? "unavailable";
    if (projected.habit?.status === "established") {
      habit.dataset.stance = projected.habit.preferredStance;
    }
    const habitMark = document.createElement("span");
    habitMark.className = "codex-habit-mark";
    habitMark.dataset.stance = projected.habit?.status === "established"
      ? projected.habit.preferredStance
      : "unknown";
    habitMark.setAttribute("aria-hidden", "true");
    const habitCopy = document.createElement("span");
    const habitName = document.createElement("strong");
    const habitDetail = document.createElement("small");
    if (projected.habit === null) {
      habitName.textContent = "Field note unavailable";
      habitDetail.textContent = "No Pattern Duel behavior is catalogued for this species.";
    } else if (projected.habit.status === "unconfirmed") {
      habitName.textContent = "Habit unconfirmed";
      habitDetail.textContent = `${projected.habit.encounters}/${projected.habit.requiredEncounters} encounters recorded; no stance inferred.`;
    } else {
      habitName.textContent = "Field note established";
      habitDetail.textContent = `${projected.habit.label}; habit, not intent.`;
    }
    habitCopy.append(habitName, habitDetail);
    habit.append(habitMark, habitCopy);

    const insightLabel = document.createElement("div");
    insightLabel.className = "codex-meter-label";
    const insightName = document.createElement("span");
    insightName.textContent = "Technique insight";
    const insightValue = document.createElement("strong");
    insightValue.textContent = `${projected.insight}/${projected.requiredInsight}`;
    insightLabel.append(insightName, insightValue);
    const insight = document.createElement("progress");
    insight.max = projected.requiredInsight;
    insight.value = projected.insight;
    insight.setAttribute(
      "aria-label",
      `${projected.monsterName} technique insight ${projected.insight} of ${projected.requiredInsight}`,
    );

    const technique = document.createElement("section");
    technique.className = "codex-technique";
    const techniqueName = document.createElement("h4");
    const techniqueDetail = document.createElement("p");
    if (projected.technique === null) {
      const outcome = projected.discoveryOutcome;
      if (projected.techniqueStatus === "held" && outcome?.disposition === "deferred-capacity") {
        technique.dataset.outcome = "held";
        if (outcome.effect !== null) technique.dataset.effect = outcome.effect;
        techniqueName.textContent = outcome.abilityName;
        const mechanics = outcome.effect === null
          ? "Candidate mechanics unavailable"
          : `${outcome.effect} · ${outcome.manaCost} MP · ${outcome.potency} potency`;
        techniqueDetail.textContent = `${mechanics} · Pattern understood · repertoire full ${outcome.repertoireCount}/${outcome.repertoireLimit} · held`;
      } else if (projected.techniqueStatus === "rejected" && outcome?.disposition === "rejected") {
        technique.dataset.outcome = "rejected";
        techniqueName.textContent = outcome.abilityName;
        techniqueDetail.textContent = outcome.reason === "ability-id-conflict"
          ? "Rejected · an existing ability uses this identity. No technique was added."
          : "Historical threshold record unresolved. No technique admission is claimed.";
      } else if (projected.techniqueStatus === "unverified") {
        techniqueName.textContent = "Repertoire record unverified";
        techniqueDetail.textContent = "The pattern is complete, but no exact outcome, admission, discovery and owned ability chain can verify it.";
      } else {
        techniqueName.textContent = "Secret technique unknown";
        techniqueDetail.textContent = `${projected.remainingVictories} more ${projected.remainingVictories === 1 ? "victory" : "victories"} to complete the pattern.`;
      }
      technique.append(techniqueName, techniqueDetail);
    } else {
      technique.dataset.effect = projected.technique.effect;
      techniqueName.textContent = projected.technique.name;
      techniqueDetail.textContent = `${projected.technique.effect} · ${projected.technique.manaCost} MP · ${projected.technique.potency} potency · ${projected.technique.uses} uses`;
      const masteryLabel = document.createElement("div");
      masteryLabel.className = "codex-meter-label";
      const masteryName = document.createElement("span");
      masteryName.textContent = `Mastery · Level ${projected.technique.level}`;
      const masteryValue = document.createElement("strong");
      masteryValue.textContent = `${projected.technique.experience}/${projected.technique.experienceCeiling}`;
      masteryLabel.append(masteryName, masteryValue);
      const mastery = document.createElement("progress");
      mastery.className = "codex-mastery-meter";
      mastery.max = Math.max(1, projected.technique.experienceCeiling - projected.technique.experienceFloor);
      mastery.value = Math.max(0, projected.technique.experience - projected.technique.experienceFloor);
      mastery.setAttribute(
        "aria-label",
        `${projected.technique.name} mastery ${projected.technique.experience} of ${projected.technique.experienceCeiling}`,
      );
      const provenance = document.createElement("small");
      provenance.textContent = `Learned from ${projected.monsterName} at T${projected.technique.discoveryTick}`;
      technique.append(techniqueName, techniqueDetail, masteryLabel, mastery, provenance);
    }
    dossier.append(heading, facts, habit, insightLabel, insight, technique);
    if (projected.observedOnly) {
      const observation = document.createElement("p");
      observation.className = "codex-research-boundary";
      observation.textContent = "Directly observed; no encounter count recorded.";
      dossier.insertBefore(observation, habit);
    }
    if (projected.fieldResearch !== undefined) {
      const research = projected.fieldResearch;
      const study = document.createElement("section");
      study.className = "codex-field-research";
      study.dataset.taskId = research.taskId;
      study.dataset.progress = `${research.progress}/2`;
      const label = document.createElement("div");
      label.className = "codex-research-heading";
      const title = document.createElement("h4");
      title.textContent = research.title;
      const count = document.createElement("strong");
      count.textContent = `${research.progress}/2`;
      label.append(title, count);
      const progress = document.createElement("progress");
      progress.className = "codex-research-progress";
      progress.max = 2;
      progress.value = research.progress;
      progress.setAttribute("aria-label", `${projected.monsterName} field research ${research.progress} of 2`);
      const result = document.createElement("p");
      result.className = research.clue === null ? "codex-research-pending" : "codex-research-clue";
      result.textContent = research.clue ?? (research.progress === 0
        ? "Observe its technique and a later effect. Two different observations, not victories."
        : "Technique application recorded. A different observation is still needed.");
      const evidence = document.createElement("details");
      evidence.className = "codex-research-evidence";
      evidence.dataset.taskId = research.taskId;
      evidence.open = previousResearch.get(research.taskId)?.open === true;
      const toggle = document.createElement("summary");
      toggle.textContent = "Observed evidence";
      const records = document.createElement("ol");
      for (const [kind, fact, text] of [
        ["application", research.application, research.applicationText],
        ...(research.taskId === "copperhorn:final-ember@1"
          ? [["first-tick", research.firstTick, research.firstTickText] as const] : []),
        ["aftereffect", research.aftereffect, research.aftereffectText],
      ] as const) {
        if (fact === null || text === null) continue;
        const record = document.createElement("li");
        record.className = `codex-research-${kind}`;
        record.dataset.sourceEvent = fact.sourceEventId;
        record.dataset.sourceTick = String(fact.sourceTick);
        record.dataset.sourceTurn = String(fact.sourceTurn);
        record.dataset.combatId = fact.combatId;
        record.dataset.targetId = fact.targetId;
        record.dataset.potency = String(fact.potency);
        record.textContent = text;
        if ("applicationEventId" in fact) {
          record.dataset.applicationEvent = fact.applicationEventId;
          record.dataset.healthBefore = String(fact.healthBefore);
          record.dataset.amount = String(fact.amount);
          record.dataset.healthAfter = String(fact.healthAfter);
          record.dataset.durationBefore = String(fact.durationBefore);
          record.dataset.durationAfter = String(fact.durationAfter);
          if ("intentEventId" in fact) record.dataset.intentEvent = fact.intentEventId;
          if ("firstTickEventId" in fact) record.dataset.firstTickEvent = fact.firstTickEventId;
          if ("damageEventId" in fact) {
            record.dataset.damageEvent = fact.damageEventId;
            record.dataset.strikeTargetId = fact.strikeTargetId;
            record.dataset.strikeAction = fact.action;
            record.dataset.strikeAbilityId = fact.abilityId ?? "";
            record.dataset.targetHealthBefore = String(fact.targetHealthBefore);
            record.dataset.damage = String(fact.damage);
            record.dataset.targetHealthAfter = String(fact.targetHealthAfter);
          }
        } else {
          record.dataset.abilityId = fact.abilityId;
          record.dataset.duration = String(fact.duration);
          record.dataset.targetHealthAfter = String(fact.targetHealthAfter);
        }
        records.append(record);
      }
      if (records.childElementCount === 0) {
        const empty = document.createElement("li");
        empty.textContent = "No qualifying observations recorded yet.";
        records.append(empty);
      }
      const boundary = document.createElement("p");
      boundary.className = "codex-research-boundary";
      boundary.textContent = "Field research only. This study grants no ability or combat bonus.";
      evidence.append(toggle, records, boundary);
      study.append(label, progress, result, evidence);
      dossier.insertBefore(study, insightLabel);
    }
    card.append(portrait, dossier);
    return card;
  });
  if (codexCards.length === 0) {
    const empty = document.createElement("li");
    empty.className = "codex-empty";
    empty.textContent = "No creatures encountered. The Codex will fill itself as the adventure reaches real battles.";
    codexCards.push(empty);
  }
  elements.codexGrid.replaceChildren(...codexCards);
  elements.codexGrid.dataset.researchCampaign = state.campaignId;
  const focusedResearch = Array.from(elements.codexGrid.querySelectorAll<HTMLDetailsElement>(".codex-research-evidence"))
    .find((details) => previousResearch.get(details.dataset.taskId)?.focused === true);
  focusedResearch?.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
  elements.codexOverflow.hidden = codex.hiddenCount === 0;
  elements.codexOverflow.textContent = codex.hiddenCount === 0
    ? ""
    : `${codex.hiddenCount} more encountered ${codex.hiddenCount === 1 ? "species is" : "species are"} recorded outside this bounded view.`;

  const spellbook = projectSpellbookView(state);
  const previousTechniqueRecords = new Map(Array.from(elements.spellbookGrid
    .querySelectorAll<HTMLDetailsElement>(".spellbook-weapon-technique"))
    .map(record => [record.dataset.abilityId, { open: record.open, focused: record.contains(document.activeElement) }]));
  elements.spellbookOwned.textContent = String(spellbook.abilityCount);
  elements.spellbookSpells.textContent = String(spellbook.spellCount);
  elements.spellbookTechniques.textContent = String(spellbook.techniqueCount);
  elements.spellbookSecrets.textContent = String(spellbook.secretCount);
  elements.spellbookUses.textContent = String(spellbook.totalBattleUses);
  elements.spellbookSummary.textContent = spellbook.abilityCount === 0
    ? "No owned abilities recorded yet."
    : `${spellbook.abilityCount} owned ${spellbook.abilityCount === 1 ? "ability" : "abilities"} · ${spellbook.masteredCount} at the current mastery cap.`;
  const breakthrough = spellbook.closestBreakthrough;
  elements.spellbookBreakthrough.hidden = breakthrough === null;
  elements.spellbookBreakthroughName.textContent = breakthrough?.abilityName ?? "";
  elements.spellbookBreakthroughDetail.textContent = breakthrough === null
    ? ""
    : `${breakthrough.experienceToNext} mastery XP to Level ${breakthrough.nextLevel}`;
  const kindLabel = { spell: "Spell", technique: "Technique", secret: "Monster secret" } as const;
  const spellbookCards = spellbook.abilities.map((projected) => {
    const card = document.createElement("li");
    card.className = "spellbook-ability";
    card.dataset.abilityId = projected.id;
    card.dataset.kind = projected.kind;
    card.dataset.effect = projected.effect;
    card.dataset.provenance = projected.provenanceStatus;

    const sigil = document.createElement("div");
    sigil.className = "spellbook-sigil";
    sigil.dataset.effect = projected.effect;
    sigil.setAttribute("aria-hidden", "true");
    for (const className of ["spellbook-sigil-ring", "spellbook-sigil-mark", "spellbook-sigil-core"]) {
      const part = document.createElement("span");
      part.className = className;
      sigil.append(part);
    }

    const detail = document.createElement("article");
    const heading = document.createElement("header");
    const identity = document.createElement("div");
    const kind = document.createElement("span");
    kind.className = "spellbook-kind";
    kind.textContent = kindLabel[projected.kind];
    const name = document.createElement("h3");
    name.textContent = projected.name;
    identity.append(kind, name);
    const level = document.createElement("span");
    level.className = "spellbook-level";
    const levelLabel = document.createElement("small");
    levelLabel.textContent = "Level";
    const levelValue = document.createElement("strong");
    levelValue.textContent = String(projected.level);
    level.append(levelLabel, levelValue);
    heading.append(identity, level);

    const effect = document.createElement("p");
    effect.className = "spellbook-effect";
    effect.textContent = projected.effect;

    const facts = document.createElement("dl");
    for (const [label, value] of [
      ["MP cost", projected.manaCost],
      ["Potency", projected.potency],
      ["Battle uses", projected.battleUses],
    ] as const) {
      const fact = document.createElement("div");
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = String(value);
      fact.append(term, description);
      facts.append(fact);
    }

    const mastery = document.createElement("section");
    mastery.className = "spellbook-mastery";
    if (projected.mastered) {
      const capped = document.createElement("strong");
      capped.className = "spellbook-mastery-cap";
      capped.textContent = "Current mastery cap";
      const total = document.createElement("small");
      total.textContent = `${projected.experience} total mastery XP`;
      mastery.append(capped, total);
    } else {
      const meterLabel = document.createElement("div");
      const title = document.createElement("span");
      title.textContent = `Mastery to Level ${projected.level + 1}`;
      const value = document.createElement("strong");
      value.textContent = `${projected.masteryCurrent}/${projected.masterySpan}`;
      meterLabel.append(title, value);
      const meter = document.createElement("progress");
      meter.max = projected.masterySpan;
      meter.value = projected.masteryCurrent;
      meter.setAttribute(
        "aria-label",
        `${projected.name} current-tier mastery ${projected.masteryCurrent} of ${projected.masterySpan}; ${projected.experienceToNext} experience to Level ${projected.level + 1}`,
      );
      const remaining = document.createElement("small");
      remaining.textContent = `${projected.experienceToNext} XP remaining · ${projected.experience} total XP`;
      mastery.append(meterLabel, meter, remaining);
    }

    detail.append(heading, effect, facts, mastery);
    const techniqueRecord = createWeaponTechniqueRecord(document, state, projected.id);
    if (techniqueRecord !== null) {
      effect.textContent = weaponTechniqueEffectLabel;
      techniqueRecord.open = previousTechniqueRecords.get(projected.id)?.open === true;
      detail.append(techniqueRecord);
    }
    if (projected.kind === "secret") {
      const provenance = document.createElement("p");
      provenance.className = "spellbook-provenance";
      provenance.textContent = projected.provenance === null
        ? "Monster-secret origin unconfirmed"
        : `Learned from ${projected.provenance.monsterName} · recorded T${projected.provenance.discoveryTick}`;
      detail.append(provenance);
    }
    card.append(sigil, detail);
    return card;
  });
  if (spellbookCards.length === 0) {
    const empty = document.createElement("li");
    empty.className = "spellbook-empty";
    empty.textContent = "No owned abilities. Spells, techniques, and learned monster secrets will appear here when canonical records exist.";
    spellbookCards.push(empty);
  }
  elements.spellbookGrid.replaceChildren(...spellbookCards);
  const focusedTechniqueRecord = Array.from(elements.spellbookGrid.querySelectorAll<HTMLDetailsElement>(".spellbook-weapon-technique"))
    .find(record => previousTechniqueRecords.get(record.dataset.abilityId)?.focused === true);
  focusedTechniqueRecord?.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
  elements.spellbookOverflow.hidden = spellbook.hiddenCount === 0;
  elements.spellbookOverflow.textContent = spellbook.hiddenCount === 0
    ? ""
    : `${spellbook.hiddenCount} more owned ${spellbook.hiddenCount === 1 ? "ability is" : "abilities are"} recorded outside this bounded view.`;
  presentHallOfChampions();
  elements.inspectionScreen.scrollTop = scrollTop;
}

function presentSpectatorInbox(): void {
  const watchButton = viewButtons.find((button) => button.dataset.view === "watch");
  const unread = spectatorInbox.unread;
  elements.watchBadge.hidden = unread === 0;
  elements.watchBadge.textContent = unread > 99 ? "99+" : String(unread);
  watchButton?.setAttribute(
    "aria-label",
    unread === 0
      ? "Watch"
      : `Watch, ${unread} unseen adventure ${unread === 1 ? "highlight" : "highlights"}`,
  );
  const visible = activeView === "watch" && spectatorRecapOpen && spectatorInbox.items.length > 0
    && stageChromeMode !== "focus";
  elements.spectatorInbox.hidden = !visible;
  elements.spectatorInbox.dataset.count = String(spectatorInbox.items.length);
  elements.spectatorInbox.dataset.unread = String(unread);
  elements.spectatorInboxSummary.textContent = `${spectatorInbox.items.length} significant ${spectatorInbox.items.length === 1 ? "moment" : "moments"}, oldest first.`;
  const boundednessNotes: string[] = [];
  if (spectatorInbox.dropped > 0) {
    boundednessNotes.push(`${spectatorInbox.dropped} earlier significant ${spectatorInbox.dropped === 1 ? "moment was" : "moments were"} evicted`);
  }
  if (spectatorInbox.unavailableTicks > 0) {
    boundednessNotes.push(`${spectatorInbox.unavailableTicks} catch-up ${spectatorInbox.unavailableTicks === 1 ? "tick was" : "ticks were"} outside retained Chronicle history`);
  }
  elements.spectatorInboxDropped.hidden = boundednessNotes.length === 0;
  elements.spectatorInboxDropped.textContent = boundednessNotes.length === 0
    ? ""
    : `${boundednessNotes.join("; ")}.`;
  elements.spectatorInboxList.replaceChildren(...spectatorInbox.items.map((moment) => {
    const item = document.createElement("li");
    item.className = "spectator-moment";
    item.dataset.momentId = moment.id;
    item.dataset.kind = moment.kind;
    item.dataset.status = moment.status;
    item.dataset.provenance = moment.provenance;
    if (moment.sourceId !== null) item.dataset.sourceId = moment.sourceId;
    if (moment.latestSourceId !== null) item.dataset.latestSourceId = moment.latestSourceId;
    const heading = document.createElement("div");
    const kind = document.createElement("span");
    kind.className = "spectator-moment-kind";
    kind.textContent = moment.kind;
    const time = document.createElement("time");
    time.textContent = moment.fromTick === moment.tick
      ? `T${moment.tick}`
      : `T${moment.fromTick}–${moment.tick}`;
    heading.append(kind, time);
    const title = document.createElement("h3");
    title.textContent = moment.title;
    const location = document.createElement("p");
    location.className = "spectator-moment-location";
    location.textContent = moment.location;
    const details = document.createElement("ul");
    details.append(...moment.details.map((detail) => {
      const detailItem = document.createElement("li");
      detailItem.textContent = detail;
      return detailItem;
    }));
    if (moment.omittedDetails > 0) {
      const omitted = document.createElement("li");
      omitted.className = "spectator-moment-omitted";
      omitted.textContent = `${moment.omittedDetails} earlier exact ${moment.omittedDetails === 1 ? "detail" : "details"} omitted`;
      details.append(omitted);
    }
    item.append(heading, title, location, details);
    return item;
  }));
}

function setActiveView(view: InspectionView, restoreWatchFocus = false, suppressAutomaticRecap = false): void {
  const previousView = activeView;
  // Inspecting another tab dismisses the stage, not the captured story being
  // written. Its completion can still enter Narratives; presentation remains
  // Watch-only. Off, campaign changes and lifecycle suspension still invalidate.
  if (view !== previousView) narrativeIntermission.close();
  if (isScreenInspectionView(previousView)) inspectionScrollByView[previousView] = elements.inspectionScreen.scrollTop;
  if (previousView === "watch" && view !== "watch") {
    settleActiveCutaway(false);
    hideCutawayAdapterRoots();
  }
  if (previousView === "watch" && view !== "watch") {
    spectatorInbox = beginSpectatorAbsence(spectatorInbox, state);
    spectatorRecapOpen = false;
  }
  activeView = view;
  elements.app.dataset.activeView = view;
  syncReparteePresentation();
  elements.counterDuelSummary.hidden = view !== "watch" || elements.counterDuelSummary.textContent === "";
  for (const button of viewButtons) {
    const selected = button.dataset.view === view;
    button.setAttribute("aria-pressed", String(selected));
    button.tabIndex = selected ? 0 : -1;
  }
  const inspecting = isScreenInspectionView(view);
  elements.mapInspector.hidden = view !== "map";
  elements.inspectionScreen.hidden = !inspecting;
  elements.adventureView.hidden = view !== "adventure";
  elements.inventoryView.hidden = view !== "inventory";
  elements.journalView.hidden = view !== "journal";
  elements.codexView.hidden = view !== "codex";
  elements.spellbookView.hidden = view !== "spellbook";
  elements.hallView.hidden = view !== "hall";
  if (inspecting) {
    elements.inspectionTitle.textContent = inspectionCopy[view].title;
    elements.inspectionSubtitle.textContent = inspectionCopy[view].subtitle;
  }
  renderer.setViewMode(view === "map" ? "map" : "live");
  if (previousView === "watch" && view !== "watch" && view !== "map") {
    renderer.render(state);
  }
  const returningWithMoments = previousView !== "watch" && view === "watch" && spectatorInbox.items.length > 0;
  if (returningWithMoments) {
    spectatorRecapOpen = !suppressAutomaticRecap && stageChromeMode !== "focus";
    const missed = spectatorInbox.unread;
    if (spectatorRecapOpen) spectatorInbox = markSpectatorInboxRead(spectatorInbox);
    elements.viewAnnouncement.textContent = spectatorRecapOpen
      ? `Watch view. ${missed} unseen adventure ${missed === 1 ? "highlight" : "highlights"} summarized. Live adventure presentation restored.`
      : `Watch view. ${missed} unread adventure ${missed === 1 ? "highlight" : "highlights"} available after restoring navigation. Live adventure presentation restored.`;
  } else {
    elements.viewAnnouncement.textContent = view === "watch"
      ? "Watch view. Live adventure presentation restored."
    : `${view[0]?.toUpperCase() ?? ""}${view.slice(1)} view. Return to Watch for the adventure.`;
  }
  presentSpectatorInbox();
  presentHeroInspectionActivity();
  syncStageChromePresentation(false);
  if (inspecting) elements.inspectionScreen.scrollTop = inspectionScrollByView[view] ?? 0;
  if (restoreWatchFocus) focusWatchControl();
  if (previousView !== view) presentNarratorScene();
}

function createNewWorld(): WorldState {
  const campaignId = randomId();
  const seedBytes = crypto.getRandomValues(new Uint32Array(4));
  const seed = Array.from(seedBytes, (value) => value.toString(16).padStart(8, "0")).join("");
  return createWorld(seed, campaignId, createCampaignLegacyState(seed, champions));
}

function checkpointKey(campaignId: string): string {
  return `${checkpointPrefix}${campaignId}`;
}

async function catchUp(world: WorldState): Promise<WorldState> {
  // Foreground conversations, jobs and board turns resume from saved facts, never hidden-time debt.
  if (projectSpareGearTradeScene(world) !== null || projectRoadSupperScene(world) !== null || projectDungeonGuardianScene(world) !== null
    || world.depth.repartee.active !== null || projectReparteeScene(world) !== null
    || world.depth.usefulReply !== null && world.depth.usefulReply.reply === null
    || world.depth.roomChallenge !== null && world.depth.roomChallenge.result === null
    || selectCompanionReunion(world.depth) !== null
    || selectCompanionCredit(world.depth) !== null
    || world.depth.smithyJob != null && world.depth.smithyJob.completion === null
    || projectSmithyJobScene(world) !== null
    || world.depth.innBluff != null && world.depth.innBluff.resolution === null
    || projectInnBluffScene(world) !== null
    || world.depth.bellExpedition !== null && world.depth.bellExpedition.completion === null
    || projectBorrowedBellScene(world) !== null) return world;
  const lastActive = Number(localStorage.getItem(checkpointKey(world.campaignId)));
  if (!Number.isFinite(lastActive) || lastActive <= 0) return world;
  const observedAtMs = Date.now();
  const elapsed = Math.max(0, observedAtMs - lastActive);
  const requestedTicks = Math.floor(elapsed / beatDurationMs);
  if (requestedTicks === 0) return world;
  return simulation.catchUp({
    id: `${world.campaignId}:${lastActive}:${observedAtMs}`,
    observedAtMs,
    elapsedMs: elapsed,
    requestedTicks,
  });
}

async function resumeDeferredCatchUp(): Promise<void> {
  if (!catchUpAfterPresentation || startupHold || presentationBusy || narrativeReading || paused || document.hidden) return;
  catchUpAfterPresentation = false;
  await runInteraction(async () => {
    const before = state;
    state = await catchUp(state);
    retainFactualStoryBeatTransition(before, state);
    present();
    await persist();
    presentNarratorScene();
  });
}

function combatStatusText(status: CombatRosterStatus): string {
  const label = status.kind === "guarding"
    ? "Guarding"
    : status.kind === "poisoned"
      ? "Poison"
      : status.kind === "weakened"
        ? "Weakened"
        : "Burning";
  return `${label} ${status.duration}t · potency ${status.potency}`;
}

function presentCombatRoster(projection: CombatRosterProjection | null, combat: CombatState | null): void {
  elements.battleOverview.hidden = projection === null;
  elements.battleThreat.hidden = projection === null || combat === null;
  const supper = combat === null ? null : projectRoadSupperCombat(combat);
  elements.battleThreat.textContent = combat === null ? "" : `${describeEncounterThreat(combat.threat)}${supper?.label ? ` · ${supper.label}` : ""}`;
  delete elements.battleThreat.dataset.rating;
  delete elements.battleThreat.dataset.score;
  delete elements.battleThreat.dataset.band;
  delete elements.battleThreat.dataset.pattern;
  if (combat !== null) {
    elements.battleThreat.dataset.rating = combat.threat.rating;
    if (combat.threat.rating === "place-bound" || combat.threat.rating === "dungeon-bound") {
      elements.battleThreat.dataset.score = String(combat.threat.encounterScore);
      elements.battleThreat.dataset.band = combat.threat.band;
      elements.battleThreat.dataset.pattern = combat.threat.band;
    }
  }
  elements.battleRoster.replaceChildren();
  elements.battleUpcoming.replaceChildren();
  delete elements.battleOverview.dataset.combatId;
  delete elements.battleOverview.dataset.activeUnit;
  delete elements.battleOverview.dataset.focusTarget;
  delete elements.battleOverview.dataset.focusKind;
  delete elements.battleOverview.dataset.upcoming;
  if (projection === null) return;

  elements.battleOverview.dataset.combatId = projection.combatId;
  elements.battleOverview.dataset.activeUnit = projection.activeUnitId ?? "none";
  elements.battleOverview.dataset.focusTarget = projection.focusTargetId ?? "none";
  elements.battleOverview.dataset.focusKind = projection.focusKind;
  elements.battleOverview.dataset.upcoming = projection.upcomingTurns.map((turn) => turn.unitId).join(",");
  elements.battleRoster.replaceChildren(...projection.units.map((unit) => {
    const item = document.createElement("li");
    item.className = "battle-unit";
    item.dataset.unitId = unit.id;
    item.dataset.side = unit.side;
    item.dataset.living = String(unit.alive);
    item.dataset.active = String(unit.isActive);
    item.dataset.actedLast = String(unit.actedLast);
    item.dataset.targeted = String(unit.isFocused);
    item.dataset.intentTarget = String(unit.wasIntentTarget);
    item.dataset.health = `${unit.health}/${unit.maxHealth}`;
    item.dataset.mana = `${unit.mana}/${unit.maxMana}`;
    item.dataset.statuses = unit.statuses.map((status) => `${status.kind}:${status.duration}:${status.potency}`).join(",");

    const name = document.createElement("strong");
    name.className = "battle-unit-name";
    name.textContent = unit.name;
    const resources = document.createElement("span");
    resources.className = "battle-unit-resources";
    resources.textContent = `HP ${unit.health}/${unit.maxHealth} · MP ${unit.mana}/${unit.maxMana}`;
    const badges = document.createElement("span");
    badges.className = "battle-unit-badges";
    const badgeLabels: string[] = [];
    if (unit.isActive) badgeLabels.push("Next");
    if (unit.actedLast) badgeLabels.push("Acted");
    if (unit.isFocused) badgeLabels.push(projection.focusKind === "self-effect" ? "Self effect" : "Target");
    if (unit.wasIntentTarget && projection.latestTurn?.intentInterrupted === true) badgeLabels.push("Intent interrupted");
    if (!unit.alive) badgeLabels.push(unit.defeatedLastTurn ? "Defeated this turn" : "Defeated");
    badges.append(...badgeLabels.map((label) => {
      const badge = document.createElement("span");
      badge.textContent = label;
      return badge;
    }));
    const statuses = document.createElement("span");
    statuses.className = "battle-unit-statuses";
    statuses.textContent = unit.statuses.length === 0 ? "No status" : unit.statuses.map(combatStatusText).join(" · ");
    const meters = document.createElement("span");
    meters.className = "battle-unit-meters";
    const health = document.createElement("progress");
    health.max = Math.max(1, unit.maxHealth);
    health.value = unit.health;
    health.setAttribute("aria-label", `${unit.name} health ${unit.health} of ${unit.maxHealth}`);
    const mana = document.createElement("progress");
    mana.className = "battle-unit-mana";
    mana.max = Math.max(1, unit.maxMana);
    mana.value = unit.mana;
    mana.setAttribute("aria-label", `${unit.name} mana ${unit.mana} of ${unit.maxMana}`);
    meters.append(health, mana);
    item.append(name, resources, badges, statuses, meters);
    return item;
  }));
  elements.battleUpcoming.replaceChildren(...projection.upcomingTurns.map((turn) => {
    const item = document.createElement("li");
    item.dataset.slot = String(turn.slot);
    item.dataset.unitId = turn.unitId;
    item.dataset.round = String(turn.round);
    item.textContent = `${turn.slot} · ${turn.unitName}`;
    return item;
  }));
}

function present(): void {
  lastPresentedStory.syncCampaign(state.campaignId);
  renderLastStoryControl();
  if (!presentationBusy && cutawayController.queue.active === null && elements.trapCutaway.dataset.active === "false") {
    elements.trapCutaway.hidden = true;
  }
  if (!presentationBusy && cutawayController.queue.active === null && elements.farewellCutaway.dataset.active === "false") {
    elements.farewellCutaway.hidden = true;
  }
  if (!presentationBusy && cutawayController.queue.active === null && elements.levelUpCutaway.dataset.active === "false") {
    elements.levelUpCutaway.hidden = true;
  }
  if (!presentationBusy && cutawayController.queue.active === null && elements.weaponMemoryCutaway.dataset.active === "false") {
    elements.weaponMemoryCutaway.hidden = true;
  }
  if (!presentationBusy && cutawayController.queue.active === null && elements.battleSpoilsCutaway.dataset.active === "false") {
    elements.battleSpoilsCutaway.hidden = true;
  }
  if (!presentationBusy && cutawayController.queue.active === null && elements.townItineraryCutaway.dataset.active === "false") {
    elements.townItineraryCutaway.hidden = true;
  }
  if (!presentationBusy && cutawayController.queue.active === null && elements.fieldNoteCutaway.dataset.active === "false") {
    elements.fieldNoteCutaway.hidden = true;
  }
  spectatorInbox = observeSpectatorInbox(
    spectatorInbox,
    observedPresentationState,
    state,
    activeView !== "watch",
  );
  observedPresentationState = state;
  const { depth } = state;
  const heroGrowth = projectHeroGrowth(depth.heroGrowth, depth.hero);
  elements.app.dataset.simulationTick = String(state.tick);
  elements.app.dataset.questReward = depth.pendingQuestReward === null ? "settled" : "pending";
  elements.app.dataset.questRewardId = depth.pendingQuestReward?.id ?? "none";
  elements.app.dataset.questInstanceId = depth.quest.instanceId;
  elements.app.dataset.questOrdinal = String(depth.quest.ordinal);
  elements.app.dataset.questAdmittedTick = String(depth.quest.admittedTick);
  const questLead = projectSuccessorQuestLead(state.seed, depth.atlas, depth.quest);
  if (questLead === null) {
    delete elements.app.dataset.questLeadId;
    delete elements.app.dataset.questLeadLocation;
    delete elements.app.dataset.questLeadPhase;
  } else {
    elements.app.dataset.questLeadId = questLead.id;
    elements.app.dataset.questLeadLocation = questLead.locationId;
    elements.app.dataset.questLeadPhase = questLead.phase;
  }
  const detail = depth.hero;
  const stats = derivedStats(detail);
  elements.heroName.textContent = detail.name;
  elements.heroLevel.textContent = `${detail.className} · Level ${detail.level} · ${detail.gold}g`;
  elements.healthText.textContent = `${detail.resources.health} / ${detail.resources.maxHealth}`;
  elements.healthBar.max = Math.max(1, detail.resources.maxHealth);
  elements.healthBar.value = detail.resources.health;
  elements.manaText.textContent = `${detail.resources.mana} / ${detail.resources.maxMana}`;
  elements.manaBar.max = Math.max(1, detail.resources.maxMana);
  elements.manaBar.value = detail.resources.mana;
  const experience = projectHeroExperience(detail);
  elements.experienceText.textContent = experience.text;
  elements.experienceText.dataset.levelState = experience.state;
  elements.experienceBar.max = experience.progressMaximum;
  elements.experienceBar.value = experience.progressValue;
  elements.experienceBar.dataset.levelState = experience.state;
  elements.experienceBar.setAttribute("aria-label", experience.accessibleLabel);
  elements.strength.textContent = String(detail.attributes.strength);
  elements.agility.textContent = String(detail.attributes.agility);
  elements.vitality.textContent = String(detail.attributes.vitality);
  elements.intellect.textContent = String(detail.attributes.intellect);
  elements.spirit.textContent = String(detail.attributes.spirit);
  elements.luck.textContent = String(detail.attributes.luck);
  elements.armor.textContent = String(stats.armor);
  elements.power.textContent = String(stats.power);
  elements.initiative.textContent = String(stats.initiative);
  elements.heroGrowthSummary.textContent = heroGrowth.hudSummary;
  const latestWeaponUse = detail.inventory.flatMap((item) => item.useMastery?.receipts
      .filter((receipt) => receipt.resolvedTick === state.depth.tick)
      .map((receipt) => ({ item, receipt })) ?? [])[0] ?? null;
  if (latestWeaponUse === null) {
    delete elements.stage.dataset.weaponUseItem;
    delete elements.stage.dataset.weaponUseCombat;
    delete elements.stage.dataset.weaponUseContribution;
    delete elements.stage.dataset.weaponUseExperience;
    delete elements.stage.dataset.weaponUseLevel;
    delete elements.stage.dataset.weaponUseStatBonus;
  } else {
    elements.stage.dataset.weaponUseItem = latestWeaponUse.item.id;
    elements.stage.dataset.weaponUseCombat = latestWeaponUse.receipt.combatId;
    elements.stage.dataset.weaponUseContribution = `${latestWeaponUse.receipt.basicStrikes}:${latestWeaponUse.receipt.damage}`;
    elements.stage.dataset.weaponUseExperience = `${latestWeaponUse.receipt.experienceBefore}:${latestWeaponUse.receipt.experienceAfter}`;
    elements.stage.dataset.weaponUseLevel = `${latestWeaponUse.receipt.levelBefore}:${latestWeaponUse.receipt.levelAfter}`;
    elements.stage.dataset.weaponUseStatBonus = "0";
  }
  const questStatusLabel = depth.quest.status === "ready-to-fulfill"
    ? "Ready to fulfill"
    : depth.quest.status === "fulfilled"
      ? "Fulfilled"
      : depth.quest.status === "failed"
        ? "Failed"
        : "Active";
  elements.questTitle.textContent = `${depth.quest.title} · ${questStatusLabel}`;
  elements.questTitle.dataset.status = depth.quest.status;
  const latestQuestCompletion = depth.completedQuests.at(-1);
  elements.questSummary.textContent = depth.quest.status === "fulfilled" && latestQuestCompletion?.questInstanceId === depth.quest.instanceId
    ? `Fulfilled at T${latestQuestCompletion.fulfilledTick} · ${latestQuestCompletion.objectiveIds.length} objectives complete · ${describeCompletedQuestReward(latestQuestCompletion)}`
    : depth.quest.summary;
  elements.questLead.hidden = questLead === null;
  elements.questLead.textContent = questLead === null
    ? ""
    : `Lead · ${questLead.locationName} · ${questLeadPhaseLabel(questLead.phase)}${questLead.discovered ? "" : " · rumored site"}`;
  elements.questLead.title = questLead === null ? "" : `Quest lead at ${questLead.locationName}: ${questLeadPhaseLabel(questLead.phase)}`;
  const objectives = [
    ...depth.quest.objectives.map((objective) => ({ objective, parent: "Main" })),
    ...depth.quest.subquests.flatMap((subquest) =>
      subquest.objectives.map((objective) => ({ objective, parent: subquest.title })),
    ),
  ];
  elements.questObjectives.replaceChildren(
    ...objectives.slice(0, 4).map(({ objective, parent }) => {
      const item = document.createElement("li");
      item.dataset.complete = String(objective.status === "complete");
      item.dataset.ruleLabel = questObjectiveRuleLabel(objective.rule);
      item.textContent = `${parent}: ${objective.description} ${objective.current}/${objective.target}`;
      item.setAttribute("aria-label", `${item.dataset.ruleLabel}: ${parent}: ${objective.description} ${objective.current}/${objective.target}`);
      return item;
    }),
  );

  const latestCommandType = state.chronicle.at(-1)?.commandType;
  const isCounterDuelBeat = latestCommandType === "start-counter-duel" || latestCommandType === "counter-duel-action";
  const counterDuel = depth.counterDuel ?? (isCounterDuelBeat ? depth.completedCounterDuels.at(-1) ?? null : null);
  const combat = depth.combat ?? (
    state.scene.mode === "battle" && !isCounterDuelBeat
      ? depth.completedCombats.at(-1) ?? null
      : null
  );
  const combatTurn = combat === null || counterDuel !== null ? null : projectLatestCombatTurn(combat);
  const combatParty = projectParty(depth);
  const roadcraftImpact = combatTurn === null || combat === null
    ? null
    : [combatParty.active, ...combatParty.former]
        .flatMap((companion) => companion?.roadcraftEffectiveness?.latestImpact ?? [])
        .find((impact) => impact.combatId === combat.id && impact.turn === combatTurn.turn) ?? null;
  const combatWeaponForm = combat === null || counterDuel !== null
    ? null
    : projectCombatFamiliarWeaponForm(depth.hero, combat, projectLatestCombatCue(combat));
  const combatRoster = combat === null || counterDuel !== null ? null : projectCombatRoster(combat);
  presentCombatRoster(combatRoster, combat);
  const dungeon = depth.dungeon;
  const dungeonTraversal = dungeon === null || dungeon.completed ? null : projectDungeonWayfinding(dungeon);
  const dungeonTraps = dungeon === null ? [] : projectDungeonTraps(dungeon);
  const dungeonKeyGate = dungeon === null ? null : projectDungeonKeyGate(dungeon);
  const dungeonLandmark = dungeon === null ? null : projectDungeonLandmark(dungeon);
  const dungeonShrineUse = dungeon === null ? null : projectLatestShrineUse(dungeon, depth.tick);
  const dungeonShrineSummary = dungeonShrineUse === null ? null : describeDungeonShrineUse(dungeonShrineUse);
  const dungeonSearch = projectDungeonSearchView(state);
  const dungeonMedicine = projectDungeonFieldMedicineScene(state);
  const dungeonPassage = projectDungeonSecretPassageScene(state);
  const dungeonGuardian = projectDungeonGuardianScene(state);
  const roadSupper = projectRoadSupperScene(state);
  const spareGearTrade = projectSpareGearTradeScene(state);
  const pennywiseGate = projectPennywiseGateScene(state);
  const smithyJob = projectSmithyJobScene(state);
  const innBluff = projectInnBluffScene(state);
  const sightedKeyMove = dungeon === null
    ? undefined
    : projectDungeonMoveKnowledge(dungeon).find((move) => move.sightedWayfinderKey);
  const route = depth.atlas.route;
  const latestLeg = state.forwardMotion.recentLegs.at(-1) ?? null;
  const arrival = state.scene.mode === "travel" && latestLeg?.arrivedTick === state.tick ? latestLeg : null;
  const corridor = projectTravelCorridor(depth.atlas, arrival);
  delete elements.traversalText.dataset.biome;
  delete elements.traversalText.dataset.terrain;
  delete elements.traversalText.dataset.slope;
  delete elements.traversalText.dataset.crossing;
  delete elements.traversalText.dataset.trapsArmed;
  delete elements.traversalText.dataset.trapsSpent;
  delete elements.traversalText.dataset.trapsDisarmed;
  delete elements.traversalText.dataset.trapsTriggered;
  delete elements.traversalText.dataset.dungeonKey;
  delete elements.traversalText.dataset.dungeonGate;
  delete elements.traversalText.dataset.dungeonLandmark;
  delete elements.traversalText.dataset.dungeonLandmarkStatus;
  delete elements.traversalText.dataset.dungeonLandmarkCell;
  delete elements.traversalText.dataset.shrineState;
  delete elements.traversalText.dataset.shrineCell;
  delete elements.traversalText.dataset.shrineHealth;
  delete elements.traversalText.dataset.shrineMana;
  delete elements.traversalText.dataset.encounterEngine;
  delete elements.traversalText.dataset.counterDuelHabit;
  delete elements.traversalText.dataset.counterDuelHabitProgress;
  delete elements.traversalText.dataset.counterDuelOpening;
  delete elements.traversalText.dataset.counterDuelOpeningStatus;
  delete elements.traversalText.dataset.counterDuelOpeningEvent;
  delete elements.traversalText.dataset.counterDuelSignatureVersion;
  delete elements.traversalText.dataset.counterDuelSignatureId;
  delete elements.traversalText.dataset.counterDuelSignatureSpecies;
  delete elements.traversalText.dataset.counterDuelSignatureMotif;
  delete elements.traversalText.dataset.counterDuelWitnessVersion;
  delete elements.traversalText.dataset.counterDuelWitnessId;
  delete elements.traversalText.dataset.counterDuelWitnessCompanion;
  delete elements.traversalText.dataset.counterDuelWitnessRole;
  delete elements.traversalText.dataset.counterDuelWitnessGesture;
  delete elements.traversalText.dataset.counterDuelWitnessMotion;
  delete elements.traversalText.dataset.counterDuelWitnessMechanicalEffect;
  delete elements.traversalDirective.dataset.directions;
  delete elements.traversalDirective.dataset.frontierCell;
  delete elements.traversalDirective.dataset.routeLength;
  delete elements.traversalDirective.dataset.visibleObjective;
  delete elements.traversalDirective.dataset.visibleObjectiveDirection;
  delete elements.traversalDirective.dataset.shrineState;
  delete elements.traversalDirective.dataset.shrineCell;
  delete elements.traversalDirective.dataset.shrineHealth;
  delete elements.traversalDirective.dataset.shrineMana;
  delete elements.battleTurnStrip.dataset.combatId;
  delete elements.battleTurnStrip.dataset.turn;
  delete elements.battleTurnStrip.dataset.actor;
  delete elements.battleTurnStrip.dataset.target;
  delete elements.battleTurnStrip.dataset.action;
  delete elements.battleTurnStrip.dataset.interrupted;
  delete elements.battleTurnStrip.dataset.ability;
  delete elements.battleTurnStrip.dataset.companionAction;
  delete elements.battleTurnStrip.dataset.companionActionReadyRound;
  delete elements.battleTurnStrip.dataset.sharedOpening;
  delete elements.battleTurnStrip.dataset.sharedOpeningSource;
  delete elements.battleTurnStrip.dataset.sharedOpeningAffectedAction;
  delete elements.battleTurnStrip.dataset.sharedOpeningCompanion;
  delete elements.battleTurnStrip.dataset.sharedOpeningHero;
  delete elements.battleTurnStrip.dataset.sharedOpeningTarget;
  delete elements.battleTurnStrip.dataset.armorReduction;
  delete elements.battleTurnStrip.dataset.roadcraftImpact;
  delete elements.battleTurnStrip.dataset.roadcraftSourceEvent;
  delete elements.battleTurnStrip.dataset.roadcraftPreventedDamage;
  delete elements.battleTurnStrip.dataset.manaBefore;
  delete elements.battleTurnStrip.dataset.manaSpent;
  delete elements.battleTurnStrip.dataset.manaAfter;
  delete elements.battleTurnStrip.dataset.item;
  delete elements.battleTurnStrip.dataset.quantityBefore;
  delete elements.battleTurnStrip.dataset.quantityAfter;
  delete elements.battleTurnStrip.dataset.restorativeHealthBefore;
  delete elements.battleTurnStrip.dataset.healthRestored;
  delete elements.battleTurnStrip.dataset.restorativeHealthAfter;
  delete elements.battleTurnStrip.dataset.healthBefore;
  delete elements.battleTurnStrip.dataset.damage;
  delete elements.battleTurnStrip.dataset.healthAfter;
  delete elements.battleTurnStrip.dataset.statuses;
  delete elements.battleTurnStrip.dataset.statusDurations;
  delete elements.battleTurnStrip.dataset.defeated;
  delete elements.battleTurnStrip.dataset.outcome;
  delete elements.battleTurnStrip.dataset.weaponFormId;
  delete elements.battleTurnStrip.dataset.weaponFormWeapon;
  delete elements.battleTurnStrip.dataset.weaponFormSilhouette;
  delete elements.battleTurnStrip.dataset.weaponFormLevel;
  delete elements.battleTurnStrip.dataset.weaponFormUnlockReceipt;
  delete elements.battleTurnStrip.dataset.weaponFormSourceCombat;
  delete elements.battleTurnStrip.dataset.weaponFormTerminal;
  delete elements.battleTurnStrip.dataset.weaponFormBonus;
  elements.battleTurnStrip.hidden = combatTurn === null;
  const combatWeaponFormCopy = combatWeaponForm === null
    ? ""
    : ` · ${combatWeaponForm.terminal ? `Resolved with ${combatWeaponForm.weaponName}` : combatWeaponForm.weaponName} · Use L${combatWeaponForm.displayedMasteryLevel} · Familiar Form: ${combatWeaponForm.formName} · no combat bonus`;
  const roadcraftImpactCopy = roadcraftImpact === null
    ? ""
    : roadcraftImpact.kind === "flour-veil"
      ? ` · FLOUR VEIL · ${roadcraftImpact.preventedDamage} HP PREVENTED`
      : " · MILLSTONE DRAG · ATTACK WEAKENED";
  elements.battleTurnStrip.textContent = combatTurn === null ? "" : `Turn ${combatTurn.turn} · ${combatTurn.text}${roadcraftImpactCopy}${combatWeaponFormCopy}`;
  elements.battleTurnStrip.removeAttribute("title");
  if (combatTurn !== null && combat !== null) {
    elements.battleTurnStrip.dataset.combatId = combat.id;
    elements.battleTurnStrip.dataset.turn = String(combatTurn.turn);
    elements.battleTurnStrip.dataset.actor = combatTurn.actorId;
    elements.battleTurnStrip.dataset.target = combatTurn.targetId ?? "none";
    elements.battleTurnStrip.dataset.action = combatTurn.action;
    elements.battleTurnStrip.dataset.interrupted = String(combatTurn.intentInterrupted);
    if (combatTurn.sharedOpening !== undefined) {
      const opening = combatTurn.sharedOpening;
      elements.battleTurnStrip.dataset.sharedOpening = `${opening.openingBefore}→${opening.openingAfter}`;
      elements.battleTurnStrip.dataset.sharedOpeningSource = opening.sourceEventId;
      elements.battleTurnStrip.dataset.sharedOpeningAffectedAction = opening.affectedActionEventId;
      elements.battleTurnStrip.dataset.sharedOpeningCompanion = opening.companionId;
      elements.battleTurnStrip.dataset.sharedOpeningHero = opening.heroId;
      elements.battleTurnStrip.dataset.sharedOpeningTarget = opening.targetId;
      if (opening.kind === "shared-opening-spent") elements.battleTurnStrip.dataset.armorReduction = String(opening.armorReduction);
    }
    if (combatTurn.abilityId !== null) elements.battleTurnStrip.dataset.ability = combatTurn.abilityId;
    if (combatTurn.companionAction !== null) {
      elements.battleTurnStrip.dataset.companionAction = combatTurn.companionAction.companionActionId;
      elements.battleTurnStrip.dataset.companionActionReadyRound = String(combatTurn.companionAction.readyRoundAfter);
    }
    if (roadcraftImpact !== null) {
      elements.battleTurnStrip.dataset.roadcraftImpact = roadcraftImpact.kind;
      elements.battleTurnStrip.dataset.roadcraftSourceEvent = roadcraftImpact.sourceEventId;
      elements.battleTurnStrip.dataset.roadcraftPreventedDamage = String(roadcraftImpact.preventedDamage);
    }
    if (combatTurn.mana !== null) {
      elements.battleTurnStrip.dataset.manaBefore = String(combatTurn.mana.manaBefore);
      elements.battleTurnStrip.dataset.manaSpent = String(combatTurn.mana.amount);
      elements.battleTurnStrip.dataset.manaAfter = String(combatTurn.mana.manaAfter);
    }
    if (combatTurn.restorative !== null) {
      elements.battleTurnStrip.dataset.item = combatTurn.restorative.itemId;
      elements.battleTurnStrip.dataset.quantityBefore = String(combatTurn.restorative.quantityBefore);
      elements.battleTurnStrip.dataset.quantityAfter = String(combatTurn.restorative.quantityAfter);
      elements.battleTurnStrip.dataset.restorativeHealthBefore = String(combatTurn.restorative.healthBefore);
      elements.battleTurnStrip.dataset.healthRestored = String(combatTurn.restorative.amount);
      elements.battleTurnStrip.dataset.restorativeHealthAfter = String(combatTurn.restorative.healthAfter);
    }
    if (combatTurn.damage !== null) {
      elements.battleTurnStrip.dataset.healthBefore = String(combatTurn.damage.healthBefore);
      elements.battleTurnStrip.dataset.damage = String(combatTurn.damage.amount);
      elements.battleTurnStrip.dataset.healthAfter = String(combatTurn.damage.healthAfter);
    }
    if (combatTurn.statusEvents.length > 0) {
      elements.battleTurnStrip.dataset.statuses = combatTurn.statusEvents.map((event) => `${event.kind}:${event.status}`).join(",");
      elements.battleTurnStrip.dataset.statusDurations = combatTurn.statusEvents.map((event) =>
        `${event.status}:${event.kind === "status-applied" ? event.durationBefore ?? 0 : event.durationBefore}->${event.durationAfter}`
      ).join(",");
    }
    if (combatTurn.defeatedIds.length > 0) elements.battleTurnStrip.dataset.defeated = combatTurn.defeatedIds.join(",");
    if (combatTurn.outcome !== null) elements.battleTurnStrip.dataset.outcome = combatTurn.outcome;
    if (combatWeaponForm !== null) {
      elements.battleTurnStrip.dataset.weaponFormId = combatWeaponForm.formId;
      elements.battleTurnStrip.dataset.weaponFormWeapon = combatWeaponForm.weaponId;
      elements.battleTurnStrip.dataset.weaponFormSilhouette = combatWeaponForm.silhouette;
      elements.battleTurnStrip.dataset.weaponFormLevel = String(combatWeaponForm.displayedMasteryLevel);
      elements.battleTurnStrip.dataset.weaponFormUnlockReceipt = combatWeaponForm.unlockReceiptId;
      elements.battleTurnStrip.dataset.weaponFormSourceCombat = combatWeaponForm.sourceCombatId;
      elements.battleTurnStrip.dataset.weaponFormTerminal = String(combatWeaponForm.terminal);
      elements.battleTurnStrip.dataset.weaponFormBonus = String(combatWeaponForm.mechanicalBonus);
    }
    elements.battleTurnStrip.title = "Canonical turn facts in resolution order; interrupted intent is never presented as an executed action.";
  }
  let presentsCorridor = false;
  elements.counterDuelSummary.hidden = counterDuel === null || activeView !== "watch";
  elements.counterDuelSummary.textContent = "";
  if (counterDuel !== null) {
    const active = counterDuel.outcome === "ongoing";
    const habit = projectCounterDuelHabit(counterDuel, depth.hero.monsterLore);
    const signature = projectCounterDuelPatternBreakSignature(counterDuel);
    const witness = projectPatternBreakObserverReaction(state);
    elements.counterDuelSummary.textContent = projectCounterDuelSummary(state.hero.name, counterDuel, habit, witness);
    elements.traversalLabel.textContent = `Pattern Duel · ${active ? `Round ${counterDuel.round}` : counterDuel.outcome}`;
    const opening = counterDuel.patternBreak === undefined
      ? "Pattern Break unavailable"
      : counterDuelPatternBreakText(counterDuel.patternBreak);
    elements.traversalText.textContent = `${state.hero.name} ${counterDuel.heroScore}–${counterDuel.opponentScore} ${counterDuel.opponentName} · ${active ? counterDuelTellText(counterDuel.tell) : `final after ${counterDuel.history.length} rounds`} · ${opening}${signature === null ? "" : ` · Signature ${signature.speciesName} · presentation only`}${witness === null ? "" : ` · Witness ${witness.companion.name} · ${witness.companion.role} · ${witness.gesture.label} · presentation only`} · ${counterDuelHabitText(habit)}`;
    elements.traversalText.dataset.encounterEngine = "counter-triangle";
    elements.traversalText.dataset.counterDuelHabit = habit.status === "established" ? habit.preferredStance : "unconfirmed";
    elements.traversalText.dataset.counterDuelHabitProgress = `${habit.encounters}/${habit.requiredEncounters}`;
    elements.traversalText.dataset.counterDuelOpening = counterDuel.patternBreak === undefined ? "legacy" : `${counterDuel.patternBreak.opening}/2`;
    elements.traversalText.dataset.counterDuelOpeningStatus = counterDuel.patternBreak?.status ?? "legacy-inert";
    elements.traversalText.dataset.counterDuelOpeningEvent = counterDuel.history.at(-1)?.patternBreak?.triggered === true
      ? "pattern-break"
      : counterDuel.history.at(-1)?.patternBreak?.openingGain === 1
        ? "confirmed-read"
        : counterDuel.history.at(-1)?.patternBreak?.reset === true
          ? "reset"
          : "none";
    if (signature !== null) {
      elements.traversalText.dataset.counterDuelSignatureVersion = signature.registryVersion;
      elements.traversalText.dataset.counterDuelSignatureId = signature.signatureId;
      elements.traversalText.dataset.counterDuelSignatureSpecies = signature.speciesId;
      elements.traversalText.dataset.counterDuelSignatureMotif = signature.motif;
    }
    if (witness !== null) {
      elements.traversalText.dataset.counterDuelWitnessVersion = witness.registryVersion;
      elements.traversalText.dataset.counterDuelWitnessId = witness.reactionId;
      elements.traversalText.dataset.counterDuelWitnessCompanion = witness.companion.id;
      elements.traversalText.dataset.counterDuelWitnessRole = witness.companion.role;
      elements.traversalText.dataset.counterDuelWitnessGesture = witness.gesture.id;
      elements.traversalText.dataset.counterDuelWitnessMotion = witness.motionMode;
      elements.traversalText.dataset.counterDuelWitnessMechanicalEffect = String(witness.mechanicalEffect);
    }
    elements.traversalProgress.max = 2;
    elements.traversalProgress.value = counterDuel.heroScore;
  } else if (combat !== null) {
    const enemies = combat.combatants.filter((combatant) => combatant.side === "enemies");
    const totalHealth = enemies.reduce((total, enemy) => total + enemy.maxHealth, 0);
    const remainingHealth = enemies.reduce((total, enemy) => total + enemy.health, 0);
    elements.traversalLabel.textContent = `Battle · Round ${combat.round}`;
    elements.traversalText.textContent = `${enemies.filter((enemy) => enemy.health > 0).length} foes · ${describeEncounterThreat(combat.threat)}`;
    elements.traversalProgress.max = Math.max(1, totalHealth);
    elements.traversalProgress.value = totalHealth - remainingHealth;
  } else if (dungeon !== null && (!dungeon.completed || state.scene.mode === "dungeon")) {
    const armedTraps = dungeonTraps.filter((trap) => trap.status === "armed").length;
    const disarmedTraps = dungeonTraps.filter((trap) => trap.status === "disarmed").length;
    const triggeredTraps = dungeonTraps.filter((trap) => trap.status === "triggered").length;
    elements.traversalLabel.textContent = dungeon.name;
    const hazardSummary = dungeonTraps.length === 0
      ? "No marked traps"
      : `${armedTraps} armed · ${disarmedTraps} disarmed · ${triggeredTraps} sprung`;
    const mechanismSummary = dungeonKeyGate?.key === null || dungeonKeyGate?.key === undefined
      ? ""
      : dungeonKeyGate.key.status === "sighted"
        ? " · Wayfinder Key sighted"
        : dungeonKeyGate.key.status === "carried"
          ? ` · Key carried${dungeonKeyGate.gate?.status === "locked" ? " · gate locked" : ""}`
          : " · Key used · gate open";
    const landmarkSummary = dungeonLandmark === null
      ? ""
      : dungeonLandmark.status === "promised"
        ? " · landmark promised: far-stair shrine"
        : dungeonLandmark.status === "mapped"
          ? " · far-stair shrine mapped"
          : " · far-stair shrine awakened";
    elements.traversalText.textContent = dungeonShrineUse === null
      ? `${dungeon.visitedCellIds.length}/${dungeon.cells.length} rooms · ${hazardSummary}${mechanismSummary}${landmarkSummary}`
      : `${dungeonShrineSummary === "RESOURCES FULL" ? "SHRINE FOUND" : "SHRINE AWAKENS"} · ${dungeonShrineSummary}`;
    elements.traversalText.dataset.trapsArmed = String(armedTraps);
    elements.traversalText.dataset.trapsSpent = String(disarmedTraps + triggeredTraps);
    elements.traversalText.dataset.trapsDisarmed = String(disarmedTraps);
    elements.traversalText.dataset.trapsTriggered = String(triggeredTraps);
    if (dungeonKeyGate?.key !== null && dungeonKeyGate?.key !== undefined) elements.traversalText.dataset.dungeonKey = dungeonKeyGate.key.status;
    if (dungeonKeyGate?.gate !== null && dungeonKeyGate?.gate !== undefined) elements.traversalText.dataset.dungeonGate = dungeonKeyGate.gate.status;
    if (dungeonLandmark !== null) {
      elements.traversalText.dataset.dungeonLandmark = dungeonLandmark.kind;
      elements.traversalText.dataset.dungeonLandmarkStatus = dungeonLandmark.status;
      if (dungeonLandmark.cellId !== null) elements.traversalText.dataset.dungeonLandmarkCell = dungeonLandmark.cellId;
    }
    if (dungeonShrineUse !== null) {
      const shrineState = dungeonShrineSummary === "RESOURCES FULL" ? "full" : "restored";
      elements.traversalText.dataset.shrineState = shrineState;
      elements.traversalText.dataset.shrineCell = dungeonShrineUse.cellId;
      elements.traversalText.dataset.shrineHealth = `${dungeonShrineUse.healthBefore}/${dungeonShrineUse.healthRestored}/${dungeonShrineUse.healthAfter}`;
      elements.traversalText.dataset.shrineMana = `${dungeonShrineUse.manaBefore}/${dungeonShrineUse.manaRestored}/${dungeonShrineUse.manaAfter}`;
    }
    elements.traversalProgress.max = dungeon.cells.length;
    elements.traversalProgress.value = dungeon.visitedCellIds.length;
  } else if (route !== null) {
    const destination = depth.atlas.locations.find(
      (location) => location.id === route.destinationId,
    );
    elements.traversalLabel.textContent = `Route · ${destination?.name ?? "Unknown"}`;
    const remaining = Math.max(0, route.totalDistance - route.distanceTravelled);
    const nextLocationId = route.path[route.legIndex + 1];
    const nextLocation = depth.atlas.locations.find((location) => location.id === nextLocationId);
    const knownDanger = nextLocation !== undefined && depth.atlas.discoveredLocationIds.includes(nextLocation.id)
      ? ` · known place danger ${nextLocation.danger}`
      : "";
    elements.traversalText.textContent = corridor === null
      ? `${route.distanceTravelled}/${route.totalDistance} mi · ${remaining} left${knownDanger}`
      : `${route.distanceTravelled}/${route.totalDistance} mi · ${remaining} left · ${describeTravelCorridor(corridor)}${knownDanger}`;
    elements.traversalProgress.max = Math.max(1, route.totalDistance);
    elements.traversalProgress.value = route.distanceTravelled;
    presentsCorridor = corridor !== null;
  } else if (corridor?.arriving === true) {
    elements.traversalLabel.textContent = `Arrived · ${corridor.toName}`;
    elements.traversalText.textContent = `${corridor.projection.legDistance}/${corridor.projection.legDistance} mi · ${describeTravelCorridor(corridor)}`;
    elements.traversalProgress.max = Math.max(1, corridor.projection.legDistance);
    elements.traversalProgress.value = corridor.projection.legDistance;
    presentsCorridor = true;
  } else {
    const town = depth.towns[depth.atlas.currentLocationId];
    elements.traversalLabel.textContent = town?.name ?? "Exploration";
    elements.traversalText.textContent = town === undefined ? "Planning" : `Visit ${town.visits}`;
    elements.traversalProgress.max = 100;
    elements.traversalProgress.value = town?.reputation ?? 0;
  }
  if (corridor !== null && presentsCorridor) {
    elements.traversalText.dataset.biome = corridor.biome;
    elements.traversalText.dataset.terrain = corridor.edgeTerrain;
    elements.traversalText.dataset.slope = corridor.slope;
    elements.traversalText.dataset.crossing = corridor.crossing?.phase ?? "none";
    elements.traversalText.title = `${corridor.fromName} → ${corridor.toName}; ${describeTravelCorridor(corridor)}`;
  } else {
    elements.traversalText.removeAttribute("title");
  }
  const directive = state.forwardMotion.activeDirective;
  const directiveDestination = directive === null
    ? undefined
    : depth.atlas.locations.find((location) => location.id === directive.destinationId);
  const currentArmedTrap = dungeonTraps.find((trap) => trap.current && trap.status === "armed");
  if (spareGearTrade !== null) {
    elements.traversalDirective.textContent = `${spareGearTrade.headline} · ${spareGearTrade.detail}`;
    elements.traversalDirective.title = `${spareGearTrade.marketName}, ${spareGearTrade.locationName} · ${spareGearTrade.itemName} 1→0 · Gold ${spareGearTrade.goldBefore}→${spareGearTrade.goldAfter}. ${state.scene.consequence}`;
    elements.traversalDirective.dataset.reason = "spare-gear-sold";
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = "";
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (roadSupper !== null) {
    elements.traversalDirective.textContent = `${roadSupper.headline} · ${roadSupper.detail}`;
    elements.traversalDirective.title = `${roadSupper.routeLabel ?? roadSupper.marketName ?? roadSupper.locationName}. ${state.scene.consequence}`;
    elements.traversalDirective.dataset.reason = `road-supper-${roadSupper.phase}`;
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = "";
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (innBluff !== null) {
    elements.traversalDirective.textContent = `${innBluff.headline} · ${innBluff.detail}`;
    elements.traversalDirective.title = `${innBluff.innName}, ${innBluff.locationName} · ${innBluff.residentName}. ${innBluff.claim} ${innBluff.tellText} ${state.scene.consequence}`;
    elements.traversalDirective.dataset.reason = `inn-bluff-${innBluff.phase}`;
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = "";
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (smithyJob !== null) {
    elements.traversalDirective.textContent = `${smithyJob.headline} · ${smithyJob.detail}`;
    elements.traversalDirective.title = `${smithyJob.smithName}, ${smithyJob.locationName} · ${smithyJob.residentName}. ${state.scene.consequence}`;
    elements.traversalDirective.dataset.reason = `smithy-job-${smithyJob.phase}`;
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = "";
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (pennywiseGate !== null) {
    elements.traversalDirective.textContent = `${pennywiseGate.headline} · ${pennywiseGate.detail}`;
    elements.traversalDirective.title = `${pennywiseGate.fromName} → ${pennywiseGate.toName}. Gold ${pennywiseGate.goldBefore}→${pennywiseGate.goldAfter}; route ${pennywiseGate.distanceBefore}→${pennywiseGate.distanceAfter} miles. ${state.scene.consequence}`;
    elements.traversalDirective.dataset.reason = `pennywise-gate-${pennywiseGate.phase}`;
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = "";
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (dungeonGuardian !== null) {
    elements.traversalDirective.textContent = `${dungeonGuardian.headline} · ${dungeonGuardian.detail}`;
    elements.traversalDirective.title = state.scene.consequence;
    elements.traversalDirective.dataset.reason = `dungeon-guardian-${dungeonGuardian.phase}`;
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = dungeonGuardian.cellId;
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (dungeonMedicine !== null) {
    elements.traversalDirective.textContent = `${dungeonMedicine.headline} · ${dungeonMedicine.detail}`;
    elements.traversalDirective.title = state.scene.consequence;
    elements.traversalDirective.dataset.reason = "dungeon-field-medicine";
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = dungeonMedicine.cellId;
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (dungeonPassage !== null) {
    elements.traversalDirective.textContent = `${dungeonPassage.headline} · ${dungeonPassage.detail}`;
    elements.traversalDirective.title = state.scene.consequence;
    elements.traversalDirective.dataset.reason = `dungeon-passage-${dungeonPassage.phase}`;
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = dungeonPassage.cellId;
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (counterDuel !== null) {
    if (counterDuel.outcome === "ongoing") {
      const habit = projectCounterDuelHabit(counterDuel, depth.hero.monsterLore);
      elements.traversalDirective.textContent = habit.status === "established"
        ? `Live tell · ${counterDuelStanceLabel(counterDuel.tell.suggestedStance)} · ${counterDuel.patternBreak === undefined ? "Break unavailable" : counterDuelPatternBreakText(counterDuel.patternBreak)} · Field note · favors ${counterDuelStanceLabel(habit.preferredStance)}`
        : `Live tell · ${counterDuelStanceLabel(counterDuel.tell.suggestedStance)} · ${counterDuel.patternBreak === undefined ? "Break unavailable" : counterDuelPatternBreakText(counterDuel.patternBreak)} · Habit unconfirmed ${habit.encounters}/${habit.requiredEncounters}`;
      elements.traversalDirective.title = `${counterDuelHabitText(habit)}. The autonomous hero weighs the live tell against earned history; neither reveals the rival's committed stance. Rush defeats Feint; Feint defeats Ward; Ward defeats Rush.`;
    } else {
      elements.traversalDirective.textContent = `Resolved · ${counterDuel.outcome} · ${counterDuel.heroScore}–${counterDuel.opponentScore} · ${counterDuel.patternBreak === undefined ? "Break unavailable" : counterDuelPatternBreakText(counterDuel.patternBreak)}`;
      elements.traversalDirective.title = depth.log.at(-1)?.message ?? "The Pattern Duel resolved once and the route remains open.";
    }
    elements.traversalDirective.dataset.reason = "counter-duel";
  } else if (dungeonShrineUse !== null && dungeonShrineSummary !== null) {
    const shrineState = dungeonShrineSummary === "RESOURCES FULL" ? "full" : "restored";
    elements.traversalDirective.textContent = `${dungeonShrineSummary === "RESOURCES FULL" ? "SHRINE FOUND" : "SHRINE AWAKENS"} · ${dungeonShrineSummary}`;
    elements.traversalDirective.title = "A first-visit shrine restores half of maximum HP and MP, clamped to each maximum; revisits cannot restore again.";
    elements.traversalDirective.dataset.reason = "dungeon-shrine";
    elements.traversalDirective.dataset.shrineState = shrineState;
    elements.traversalDirective.dataset.shrineCell = dungeonShrineUse.cellId;
    elements.traversalDirective.dataset.shrineHealth = `${dungeonShrineUse.healthBefore}/${dungeonShrineUse.healthRestored}/${dungeonShrineUse.healthAfter}`;
    elements.traversalDirective.dataset.shrineMana = `${dungeonShrineUse.manaBefore}/${dungeonShrineUse.manaRestored}/${dungeonShrineUse.manaAfter}`;
  } else if (dungeonSearch !== null) {
    elements.traversalDirective.textContent = `${dungeonSearch.headline} · ${dungeonSearch.detail}`;
    elements.traversalDirective.title = dungeonSearch.consequence;
    elements.traversalDirective.dataset.reason = "dungeon-search";
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = dungeonSearch.cellId;
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (dungeonTraversal !== null && currentArmedTrap !== undefined) {
    const attribute = dungeonTrapCheckAttribute(currentArmedTrap.kind, "disarm");
    elements.traversalDirective.textContent = `Disarming · ${dungeonTrapKindLabel(currentArmedTrap.kind)} · ${attribute} vs ${currentArmedTrap.disarmDifficulty}`;
    elements.traversalDirective.title = "A detected current-cell mechanism blocks movement until one canonical disarm attempt resolves it.";
    elements.traversalDirective.dataset.reason = "dungeon-disarm";
    elements.traversalDirective.dataset.directions = "";
    elements.traversalDirective.dataset.frontierCell = currentArmedTrap.cellId;
    elements.traversalDirective.dataset.routeLength = "0";
  } else if (dungeonTraversal !== null && sightedKeyMove !== undefined) {
    elements.traversalDirective.textContent = `Key sighted · entering ${sightedKeyMove.direction}`;
    elements.traversalDirective.title = `The visible Wayfinder Key is a concrete mapped objective in the ${sightedKeyMove.direction} chamber; no hidden route or hazard is assumed.`;
    elements.traversalDirective.dataset.reason = "dungeon-sighted-key";
    elements.traversalDirective.dataset.directions = sightedKeyMove.direction;
    elements.traversalDirective.dataset.frontierCell = sightedKeyMove.destinationCellId;
    elements.traversalDirective.dataset.routeLength = "1";
    elements.traversalDirective.dataset.visibleObjective = "wayfinder-key";
    elements.traversalDirective.dataset.visibleObjectiveDirection = sightedKeyMove.direction;
  } else if (dungeonTraversal !== null) {
    const directions = dungeonTraversal.nextPassageDirections;
    elements.traversalDirective.textContent = dungeonTraversal.mode === "return-to-gate"
      ? `Key carried · returning ${dungeonTraversal.nextDirection ?? "along the mapped route"} · ${dungeonTraversal.roomsToFrontier} ${dungeonTraversal.roomsToFrontier === 1 ? "room" : "rooms"} to gate`
      : dungeonTraversal.mode === "unlock-gate"
        ? "Unlocking · Wayfinder Gate · stationary key-turn"
        : dungeonTraversal.mode === "cross-gate"
          ? `Shortcut open · crossing ${dungeonTraversal.nextDirection ?? "the gate"}`
          : dungeonTraversal.mode === "retrace"
            ? `Retracing ${dungeonTraversal.nextDirection ?? "mapped passage"} · ${dungeonTraversal.roomsToFrontier} ${dungeonTraversal.roomsToFrontier === 1 ? "room" : "rooms"} to frontier`
            : `Exploring · ${directions.join(" or ")} ${directions.length === 1 ? "passage" : "passages"}`;
    elements.traversalDirective.title = dungeonTraversal.mode === "return-to-gate"
      ? "The Wayfinder Key redirects the autonomous hero along the exact explored route to the known sealed shortcut."
      : dungeonTraversal.mode === "unlock-gate"
        ? "Unlocking consumes one stationary canonical tick; the shortcut is crossed on the following tick."
        : dungeonTraversal.mode === "cross-gate"
          ? "The opened passage is now real movement and reveals its far side only as the hero crosses it."
          : dungeonTraversal.mode === "retrace"
            ? "The adventurer is following mapped rooms to the nearest junction with an unexplored exit."
            : "Every listed passage reaches an unvisited adjacent room; no direction is selected yet.";
    elements.traversalDirective.dataset.reason = `dungeon-${dungeonTraversal.mode}`;
    elements.traversalDirective.dataset.directions = directions.join(",");
    elements.traversalDirective.dataset.frontierCell = dungeonTraversal.frontierCellId ?? "";
    elements.traversalDirective.dataset.routeLength = String(dungeonTraversal.roomsToFrontier);
  } else if (dungeon !== null && dungeon.completed && state.scene.mode === "dungeon") {
    elements.traversalDirective.textContent = "Cleared · far stair reached";
    elements.traversalDirective.title = "The dungeon completed atomically with the final room's consequences.";
    elements.traversalDirective.dataset.reason = "dungeon-completed";
  } else {
    elements.traversalDirective.textContent = route === null
      ? "Momentum · choosing next purpose"
      : forwardMotionLabel(directive);
    elements.traversalDirective.title = directive === null
      ? "The Game Master is selecting the next canonical purpose."
      : describeForwardMotionReason(directive.reason, directiveDestination?.name ?? directive.destinationId);
    elements.traversalDirective.dataset.reason = directive?.reason ?? "planning";
  }

  elements.location.textContent = state.scene.location;
  elements.headline.textContent = state.scene.headline;
  const criticalRecovery = projectCriticalRoadsideRecovery(state);
  elements.action.textContent = criticalRecovery?.recoveryText ?? state.scene.action;
  elements.goal.textContent = state.scene.goal;
  elements.consequence.textContent = criticalRecovery?.readinessText ?? state.scene.consequence;
  const nextObjective = objectives.find(({ objective }) => objective.status !== "complete") ?? null;
  const party = projectWatchParty(detail, projectParty(state.depth).active);
  elements.stageFocusHero.textContent = party.hero.name;
  elements.stageFocusHero.title = `${detail.name} · ${detail.className} · Level ${detail.level}`;
  elements.watchHeroRole.textContent = `Hero · L${detail.level}`;
  const paintPortrait = (host: HTMLElement, character: typeof party.hero | NonNullable<typeof party.companion>) => {
    host.dataset.characterId = character.id;
    for (const [part, color] of Object.entries(character.appearance)) host.style.setProperty(`--portrait-${part}`, cssColor(color));
  };
  const showResource = (label: HTMLElement, bar: HTMLProgressElement, name: string, kind: "HP" | "MP", current: number, maximum: number) => {
    label.textContent = `${kind} ${current}/${maximum}`;
    bar.max = Math.max(1, maximum);
    bar.value = current;
    bar.setAttribute("aria-label", `${name} ${kind === "HP" ? "health" : "mana"} ${current} of ${maximum}`);
  };
  paintPortrait(elements.watchHeroPortrait, party.hero);
  showResource(elements.watchHeroHealthText, elements.watchHeroHealth, party.hero.name, "HP", party.hero.health, party.hero.maxHealth);
  showResource(elements.watchHeroManaText, elements.watchHeroMana, party.hero.name, "MP", party.hero.mana, party.hero.maxMana);
  elements.stageFocusResources.textContent = `HP ${detail.resources.health}/${detail.resources.maxHealth} · MP ${detail.resources.mana}/${detail.resources.maxMana}`;
  elements.watchCompanionCard.hidden = party.companion === null;
  elements.stageFocusRibbon.dataset.partySize = party.companion === null ? "1" : "2";
  elements.stageFocusCompanion.textContent = party.companion?.name ?? "";
  elements.stageFocusCompanion.title = party.companion?.name ?? "";
  elements.watchCompanionStatus.textContent = party.companion?.status ?? "";
  delete elements.watchCompanionCard.dataset.injured;
  if (party.companion !== null) {
    paintPortrait(elements.watchCompanionPortrait, party.companion);
    elements.watchCompanionCard.dataset.injured = String(party.companion.injured);
    showResource(elements.watchCompanionHealthText, elements.watchCompanionHealth, party.companion.name, "HP", party.companion.health, party.companion.maxHealth);
  } else {
    delete elements.watchCompanionPortrait.dataset.characterId;
    elements.watchCompanionPortrait.removeAttribute("style");
    elements.watchCompanionHealthText.textContent = "";
    elements.watchCompanionHealth.value = 0;
    elements.watchCompanionHealth.max = 1;
    elements.watchCompanionHealth.setAttribute("aria-label", "Companion health");
  }
  elements.stageFocusQuest.textContent = elements.questTitle.textContent;
  elements.stageFocusObjectiveProgress.textContent = nextObjective === null
    ? "DONE"
    : `${nextObjective.objective.current}/${nextObjective.objective.target}`;
  elements.stageFocusObjective.textContent = nextObjective === null
    ? "All current objectives complete"
    : `${nextObjective.parent}: ${nextObjective.objective.description}`;
  elements.stageFocusObjective.title = nextObjective === null
    ? elements.stageFocusObjective.textContent
    : `${elements.stageFocusObjectiveProgress.textContent} ${elements.stageFocusObjective.textContent}`;
  elements.stageFocusHeadline.textContent = `${state.scene.location} · ${state.scene.headline}`;
  elements.stageFocusHeadline.removeAttribute("title");
  delete elements.stageFocusHeadline.dataset.techniqueCommand;
  const techniqueLesson = projectWeaponTechniqueScene(state);
  if (techniqueLesson !== null) {
    elements.stageFocusHeadline.textContent = techniqueLesson.headline;
    elements.stageFocusHeadline.title = `${state.scene.action} ${state.scene.consequence}`;
    elements.stageFocusHeadline.dataset.techniqueCommand = techniqueLesson.commandId;
  }
  for (const key of ["aftermathCommand", "aftermathCombat", "aftermathTick"]) delete elements.stageFocusHeadline.dataset[key];
  for (const key of ["controlCommand", "controlCombat", "controlApplicationTurn", "controlRetaliationTurn"]) delete elements.stageFocusHeadline.dataset[key];
  const millrace = state.scene.mode === "battle" && combat !== null ? projectMillraceReversal(combat) : null;
  if (millrace !== null) {
    elements.stageFocusHeadline.textContent = millrace.headline;
    if (millrace.phase === "ready") {
      sharedOpeningPip.setAttribute("aria-label", `Shared Opening 1 of 1. ${millrace.headline}`);
      elements.stageFocusHeadline.prepend(sharedOpeningPip);
    }
  } else {
    const control = projectCombatControlScene(state);
    const aftermath = control === null ? projectCombatAftermathScene(state) : null;
    if (control !== null) {
      elements.stageFocusHeadline.textContent = control.compactDetail;
      elements.stageFocusHeadline.title = control.detail;
      elements.stageFocusHeadline.dataset.controlCommand = control.commandId;
      elements.stageFocusHeadline.dataset.controlCombat = control.combatId;
      elements.stageFocusHeadline.dataset.controlApplicationTurn = String(control.applicationTurn);
      elements.stageFocusHeadline.dataset.controlRetaliationTurn = String(control.retaliationTurn);
    } else if (aftermath !== null) {
      elements.stageFocusHeadline.textContent = aftermath.compactDetail;
      elements.stageFocusHeadline.title = `${aftermath.headline}: ${aftermath.detail}`;
      elements.stageFocusHeadline.dataset.aftermathCommand = aftermath.commandId;
      elements.stageFocusHeadline.dataset.aftermathCombat = aftermath.combatId;
      elements.stageFocusHeadline.dataset.aftermathTick = String(aftermath.tick);
    }
  }
  if (combatTurn === null) {
    elements.stageFocusAction.textContent = techniqueLesson?.detail ?? [
      elements.action.textContent,
      elements.traversalDirective.textContent,
      elements.consequence.textContent,
    ].filter((value) => value !== null && value.length > 0).join(" · ");
    elements.stageFocusAction.title = elements.stageFocusAction.textContent;
  } else {
    elements.stageFocusAction.textContent = `Turn ${combatTurn.turn} · ${formatCombatQuickReceipt(combatTurn, roadcraftImpact)}`;
    elements.stageFocusAction.title = elements.battleTurnStrip.textContent ?? "";
  }
  elements.stageFocusRibbon.dataset.sceneMode = state.scene.mode;
  syncWatchStageGeometry();
  const decision = state.chronicle.at(-1);
  const trace = decision?.decisionTrace;
  elements.decision.textContent = trace === undefined
    ? decision?.rationale ?? "The first instinct is still forming."
    : `${trace.actorName} → ${trace.selected.actionLabel}${trace.selected.targetLabel === null ? "" : ` → ${trace.selected.targetLabel}`} · ${trace.reasons[0]}`;
  elements.decision.title = decision?.rationale ?? "No canonical decision has resolved yet.";
  elements.decision.dataset.commandId = decision?.commandId ?? "pending";
  elements.decision.dataset.profileId = trace?.profileId ?? "pending";
  elements.decision.dataset.ruleId = trace?.matchedRuleId ?? "pending";
  elements.decision.dataset.reasonCode = trace?.reasonCode ?? "pending";
  presentViewScreens();
  presentHeroInspectionActivity();
  presentSpectatorInbox();
  reparteeScene = projectReparteeScene(state);
  reparteeView.render(state);
  bellScene = projectBorrowedBellScene(state);
  bellView.render(state);
  syncReparteePresentation();
  renderer.render(state);
  presentDungeonPerspectivePreference();
  syncStoryBeatPresentation();
}

async function persist(): Promise<void> {
  await repository.save(state);
  if (
    state.championInduction !== null &&
    !champions.some((champion) => champion.id === state.championInduction?.id)
  ) {
    champions = await repository.listChampions();
    presentHallOfChampions();
  }
  durableState = state;
  localStorage.setItem(checkpointKey(state.campaignId), String(Date.now()));
}

async function refreshCampaigns(): Promise<void> {
  const campaigns = await repository.list();
  elements.campaignSelect.replaceChildren(
    ...campaigns.map((campaign) => {
      const option = document.createElement("option");
      option.value = campaign.campaignId;
      option.textContent = `${campaign.hero.name} · Lv ${campaign.hero.level}`;
      option.selected = campaign.campaignId === state.campaignId;
      return option;
    }),
  );
}

async function runInteraction(action: () => Promise<void>): Promise<void> {
  pendingInteractions += 1;
  try {
    while (stepping) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 25));
    }
    stepping = true;
    await action();
  } finally {
    stepping = false;
    pendingInteractions -= 1;
    requestNarrativeCheck();
  }
}

async function step(): Promise<void> {
  syncReparteePresentation();
  if (startupHold || paused || narrativeReading || document.hidden || stepping || pendingInteractions > 0 || presentationBusy) return;
  if (reparteeDwellPending(reparteeDwell, reparteeVisible === true, fastMode)) return;
  stepping = true;
  try {
    const before = state;
    state = await simulation.advance();
    retainFactualStoryBeatTransition(before, state);
    const source = state.chronicle.at(-1);
    lastAdvanceAtMs = Date.now();
    elements.app.dataset.runtimeStatus = "running";
    await persist();
    // Capture only this durable transition, before ordinary presentation can move on.
    // Both companion milestones can be narrated with Quiet recovery. Only
    // authored fallback depends on the separate draft-recovery preference.
    if (storytellingPreferences.focus !== "scene"
      && !["off", "failed"].includes(creativeStoryController.snapshot.phase)) {
      const farewell = projectRecordedFarewell(before, state);
      const remembrance = storytellingPreferences.draftRecovery === "vignette"
        ? projectFarewellRemembrance(before, state) : null;
      const firstVictory = projectFirstSharedVictory(before, state);
      const job = farewell === null && firstVictory === null ? null
        : projectStoryBeatJobV1(state.campaignId, state.scene, source, source?.id);
      if (farewell !== null && job !== null) creativeStoryDirector.offerFarewell({
        job, mode: state.scene.mode,
        viewpoint: projectCreativeStoryViewpoint(state.hero, projectParty(state.depth)),
        farewell,
        ...(remembrance === null ? {} : { remembrance }),
      });
      if (firstVictory !== null && job !== null) creativeStoryDirector.offerFirstVictory({
        job, mode: state.scene.mode,
        viewpoint: projectCreativeStoryViewpoint(state.hero, projectParty(state.depth)),
        firstVictory,
      });
    }
    const cutawayCandidates = source === undefined
      ? Object.freeze([])
      : projectCutawayCandidates(before, state, source);
    const firstVictoryPlate = projectFirstSharedVictoryChroniclePlate(projectFirstSharedVictory(before, state),
      { campaignId: state.campaignId, currentTick: state.tick });
    if (firstVictoryPlate !== null) chroniclePlateArchive.record(firstVictoryPlate);
    // Capture event-time landmarks only after the successful save above.
    // Gallery retention is independent of cutaway selection and narrator mode.
    for (const candidate of cutawayCandidates) {
      if (candidate.recipeKey !== "town-itinerary@1") continue;
      const plate = projectTownChroniclePlate(candidate.packet,
        { campaignId: state.campaignId, currentTick: state.tick });
      if (plate !== null) chroniclePlateArchive.record(plate);
    }
    present();
    for (const candidate of cutawayCandidates) enqueueCutaway(candidate);
    await refreshCampaigns();
    presentNarratorScene();
  } catch {
    state = durableState;
    clearFactualStoryBeatOpportunity();
    elements.app.dataset.runtimeStatus = "recovering";
    try {
      await simulation.reset(durableState);
      lastAdvanceAtMs = Date.now();
    } catch {
      simulation.terminate();
    }
    present();
    elements.consequence.textContent = "The adventure engine recovered from its last safe moment · retrying";
    presentNarratorScene();
  } finally {
    stepping = false;
    requestNarrativeCheck();
  }
}

async function recoverRuntime(): Promise<void> {
  if (startupHold || runtimeRecovering || paused || narrativeReading || document.hidden || pendingInteractions > 0 || presentationBusy) return;
  if (stepping) {
    elements.app.dataset.runtimeStatus = "recovering";
    simulation.terminate();
    return;
  }
  runtimeRecovering = true;
  elements.app.dataset.runtimeStatus = "recovering";
  try {
    await runInteraction(async () => {
      simulation.terminate();
      state = durableState;
      clearFactualStoryBeatOpportunity();
      await simulation.reset(durableState);
      lastAdvanceAtMs = Date.now();
      present();
      elements.consequence.textContent = "The adventure engine resumed from its last safe moment";
      presentNarratorScene();
    });
  } catch {
    simulation.terminate();
    elements.app.dataset.runtimeStatus = "waiting-to-recover";
  } finally {
    runtimeRecovering = false;
  }
}

function startRuntimeWatchdog(): void {
  if (runtimeWatchdog !== undefined) window.clearInterval(runtimeWatchdog);
  runtimeWatchdog = window.setInterval(() => {
    if (startupHold || narrativeReading) return;
    syncReparteePresentation();
    if (reparteeDwellPending(reparteeDwell, reparteeVisible === true, fastMode)) return;
    if (presentationBusy) {
      const maximumMs = activeCutawayMaximumMs(cutawayRegistry, cutawayController);
      if (!paused && !document.hidden && maximumMs !== null && Date.now() - cutawayStartedAtMs > maximumMs) {
        settleActiveCutaway();
      }
      return;
    }
    if (!shouldRecoverRuntime({
      nowMs: Date.now(),
      lastAdvanceAtMs,
      beatDurationMs,
      paused,
      hidden: document.hidden,
      interacting: pendingInteractions > 0 || presentationBusy,
    })) return;
    void recoverRuntime();
  }, 5_000);
}

function presentDungeonPerspectivePreference(): void {
  elements.dungeonPerspectiveSelect.value = dungeonPerspective;
  elements.app.dataset.dungeonPerspective = dungeonPerspective;
  const status = elements.stage.dataset.dungeonPerspectiveStatus;
  const detail = dungeonPerspective === "map" ? "The classic 2D dungeon map is selected."
    : status === "failed" ? "Preview unavailable; using 2D. Reload to try again."
    : status === "loading" ? "Loading the optional view; 2D remains available."
    : "First-person preview is selected for dungeon scenes. Other scenes are unchanged.";
  elements.dungeonPerspectiveStatus.textContent = `${detail} Changes visuals only, never adventure rules or turns. `
    + (dungeonPerspectiveSaved ? "Your choice is remembered when browser storage is available." : "Storage unavailable; this choice applies to this page only.");
}

async function applyDungeonPerspective(): Promise<void> {
  const requested = dungeonPerspective;
  const ready = renderer.setDungeonPerspective(requested);
  presentDungeonPerspectivePreference();
  await ready;
  if (dungeonPerspective === requested) presentDungeonPerspectivePreference();
}

function startLoop(): void {
  if (loop !== undefined) window.clearInterval(loop);
  const intervalMs = adventureStepIntervalMs(adventureSpeed, fastMode);
  // Foreground cadence only: no step batching, missed-tick debt, or faster offline catch-up.
  loop = window.setInterval(() => void step(), intervalMs);
  elements.app.dataset.adventureSpeed = String(adventureSpeed);
  elements.app.dataset.adventureStepMs = String(intervalMs);
  elements.adventureSpeedSelect.value = String(adventureSpeed);
  elements.adventureSpeedStatus.textContent = "Speeds up adventure steps; story reading and cutscenes keep their own pace. "
    + "Maximum speed depends on this device. "
    + (adventureSpeedSaved ? "Your choice is saved when browser storage is available." : "Storage unavailable; speed applies to this page only.");
}

async function fetchDeployedVersion(): Promise<unknown> {
  const response = await fetch(
    `${import.meta.env.BASE_URL}version.json?check=${encodeURIComponent(randomId())}`,
    {
      cache: "no-store",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) throw new Error(`Version check failed with ${response.status}`);
  return response.json();
}

function readUpdateAttempt(): UpdateAttempt | null {
  const source = sessionStorage.getItem(updateAttemptKey);
  if (source === null) return null;
  try {
    const value = JSON.parse(source) as Partial<UpdateAttempt>;
    if (
      typeof value.fromVersion !== "string"
      || typeof value.targetVersion !== "string"
      || !Number.isFinite(value.attemptedAt)
    ) return null;
    return value as UpdateAttempt;
  } catch {
    return null;
  }
}

function clearCompletedUpdateAttempt(): void {
  const attempt = readUpdateAttempt();
  if (attempt !== null && !isNewerVersion(attempt.targetVersion, __APP_VERSION__)) {
    sessionStorage.removeItem(updateAttemptKey);
  }
}

async function applyAutomaticUpdate(nextVersion: string): Promise<void> {
  const previousAttempt = readUpdateAttempt();
  if (
    previousAttempt?.fromVersion === __APP_VERSION__
    && previousAttempt.targetVersion === nextVersion
    && Date.now() - previousAttempt.attemptedAt < updateIntervalMs
  ) {
    throw new Error("This update target was already attempted recently");
  }
  cancelNarrativeIntermission();
  elements.updateStatus.hidden = false;
  elements.updateStatus.textContent = `Saving progress · updating to v${nextVersion}…`;
  document.documentElement.dataset.updateStatus = "saving";
  catchUpAfterPresentation = false;
  settleActiveCutaway(false);
  await runInteraction(async () => persist());
  sessionStorage.setItem(updateAttemptKey, JSON.stringify({
    fromVersion: __APP_VERSION__,
    targetVersion: nextVersion,
    attemptedAt: Date.now(),
  } satisfies UpdateAttempt));
  document.documentElement.dataset.updateStatus = "reloading";
  window.location.reload();
}

function startAutomaticUpdates(): void {
  clearCompletedUpdateAttempt();
  automaticUpdateMonitor = new AutomaticUpdateMonitor({
    currentVersion: __APP_VERSION__,
    fetchVersion: fetchDeployedVersion,
    randomUnit: Math.random,
    schedule: (callback, delayMs) => window.setTimeout(callback, delayMs),
    cancel: (timer) => window.clearTimeout(timer),
    isVisible: () => !document.hidden,
    applyUpdate: applyAutomaticUpdate,
    report: (status, version) => {
      document.documentElement.dataset.updateStatus = status;
      if (status === "deferred") {
        elements.updateStatus.textContent = `Update v${version ?? "new"} ready · resumes when visible`;
        elements.updateStatus.hidden = false;
        return;
      }
      if (status === "available") {
        elements.updateStatus.textContent = `Update v${version ?? "new"} found…`;
        elements.updateStatus.hidden = false;
        return;
      }
      elements.updateStatus.hidden = true;
      elements.updateStatus.textContent = "";
    },
  });
  automaticUpdateMonitor.start();
}

async function registerServiceWorker(): Promise<void> {
  const registration = await navigator.serviceWorker.register(
    `${import.meta.env.BASE_URL}sw.js`,
    { updateViaCache: "none" },
  );
  await registration.update();
}

for (const button of viewButtons) {
  button.addEventListener("click", () => {
    if (!isInspectionView(button.dataset.view)) return;
    if (button.dataset.view === "watch" && activeView === "watch" && spectatorInbox.items.length > 0) {
      spectatorRecapOpen = !spectatorRecapOpen;
      if (spectatorRecapOpen) spectatorInbox = markSpectatorInboxRead(spectatorInbox);
      presentSpectatorInbox();
      return;
    }
    setActiveView(button.dataset.view);
  });
}

elements.stageFocusButton.addEventListener("click", () => {
  const focused = stageChromeMode === "focus" && activeView === "watch";
  setStageChromeMode(toggledStageChromeMode(focused ? "focus" : "panels"), true);
  elements.stageFocusButton.focus();
});

elements.watchCharacterDetails.addEventListener("click", () => {
  setActiveView("adventure");
  viewButtons.find((button) => button.dataset.view === "adventure")?.focus();
});
elements.journalStatusButton.addEventListener("click", () => statusHistoryView.refresh());
elements.openStatusLog.addEventListener("click", () => {
  setActiveView("journal");
  narrativeJournalView.selectSection("status");
  statusHistoryView.refresh();
  elements.inspectionScreen.scrollTop = 0;
  elements.journalStatusButton.focus();
});

elements.gameMenuButton.addEventListener("click", openGameMenu);
elements.stageMenuButton.addEventListener("click", openGameMenu);
elements.gameMenuClose.addEventListener("click", () => closeGameMenu());
elements.adventureSpeedSelect.addEventListener("change", () => {
  adventureSpeed = normalizeAdventureSpeed(Number(elements.adventureSpeedSelect.value));
  adventureSpeedSaved = writeAdventureSpeedPreference(adventureSpeed);
  startLoop();
});
elements.dungeonPerspectiveSelect.addEventListener("change", () => {
  dungeonPerspective = normalizeDungeonPerspective(elements.dungeonPerspectiveSelect.value);
  dungeonPerspectiveSaved = writeDungeonPerspectivePreference(dungeonPerspective);
  void applyDungeonPerspective();
});
elements.lastStoryButton.addEventListener("click", revisitLastStory);
elements.gameMenu.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeGameMenu();
});
elements.playStartLlm.addEventListener("click", () => playModeStartup.choose("llm"));
elements.playStartDeterministic.addEventListener("click", () => playModeStartup.choose("deterministic"));
elements.playStartDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  playModeStartup.choose("deterministic");
});
elements.playModeSelect.addEventListener("change", () => {
  if (elements.playModeSelect.disabled) {
    renderPlayModeStatus();
    return;
  }
  if (elements.playModeSelect.value === "llm" || elements.playModeSelect.value === "deterministic") {
    playModeStartup.choose(elements.playModeSelect.value);
  }
});
elements.playModeRetry.addEventListener("click", () => playModeStartup.choose("llm"));


elements.narratorButton.addEventListener("click", openNarratorDialog);
elements.narratorClose.addEventListener("click", () => closeNarratorDialog());
elements.narratorDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeNarratorDialog();
});
elements.narratorDownload.addEventListener("click", () => {
  playModeStartup.cancel();
  requestedPlayMode = "llm";
  writePlayModePreference("llm");
  cancelNarrativeIntermission();
  creativeStoryController.stop();
  void localNarratorController.install(state.campaignId).then(() => {
    presentNarratorScene();
  });
});
elements.narratorCancel.addEventListener("click", () => {
  playModeStartup.choose("deterministic");
});
elements.narratorDisable.addEventListener("click", () => {
  playModeStartup.choose("deterministic");
});
elements.narratorRemove.addEventListener("click", () => {
  playModeStartup.choose("deterministic");
  elements.narratorRemove.disabled = true;
  void localNarratorController.remove().finally(() => {
    elements.narratorRemove.disabled = false;
  });
});
elements.creativeLoad.addEventListener("click", () => {
  playModeStartup.choose("llm");
});
elements.creativeStop.addEventListener("click", () => {
  playModeStartup.choose("deterministic");
});
elements.creativeRemove.addEventListener("click", () => {
  playModeStartup.choose("deterministic");
  void creativeStoryController.remove();
});
elements.creativeFocus.addEventListener("change", () => {
  if (creativeStoryController.snapshot.busy) {
    elements.creativeFocus.value = storytellingPreferences.focus;
    return;
  }
  storytellingPreferences = normalizeStorytellingPreferences({ ...storytellingPreferences, focus: elements.creativeFocus.value });
  writeStorytellingPreferences(storytellingPreferences);
  creativeStoryDirector.invalidate();
  const source = state.chronicle.at(-1);
  creativeStoryController.sync({
    job: projectStoryBeatJobV1(state.campaignId, state.scene, source, source?.id),
    mode: state.scene.mode,
    viewpoint: projectCreativeStoryViewpoint(state.hero, projectParty(state.depth)),
    eligible: false,
  });
  creativeStoryController.setFocus(effectiveStoryFocus(storytellingPreferences.focus,
    projectCreativeStoryViewpoint(state.hero, projectParty(state.depth))?.companion != null));
  elements.creativeFocus.value = storytellingPreferences.focus;
  requestNarrativeCheck();
});
elements.creativeRhythm.addEventListener("change", () => {
  if (creativeStoryController.snapshot.busy) {
    elements.creativeRhythm.value = storytellingPreferences.rhythm;
    return;
  }
  storytellingPreferences = normalizeStorytellingPreferences({ ...storytellingPreferences, rhythm: elements.creativeRhythm.value });
  writeStorytellingPreferences(storytellingPreferences);
  // Rhythm changes spacing, not the captured story; cadence reads the preference live.
  requestNarrativeCheck();
});
elements.creativeDraftRecovery.addEventListener("change", () => {
  if (creativeStoryController.snapshot.busy) {
    elements.creativeDraftRecovery.value = storytellingPreferences.draftRecovery;
    return;
  }
  storytellingPreferences = normalizeStorytellingPreferences({
    ...storytellingPreferences, draftRecovery: elements.creativeDraftRecovery.value,
  });
  writeStorytellingPreferences(storytellingPreferences);
  // Recovery controls authored fallbacks, not successful model prose or queued moments.
  if (storytellingPreferences.draftRecovery === "quiet") {
    creativeStoryDirector.discardAuthoredReady();
  }
  elements.creativeDraftRecovery.value = storytellingPreferences.draftRecovery;
  requestNarrativeCheck();
});
async function requestStableStoryBeat(): Promise<void> {
  const leaseCampaignId = state.campaignId;
  const sceneLease = {
    isPaused: () => paused,
    pause: () => {
      if (!paused) togglePaused();
    },
    waitForStable: async () => {
      while (stepping && paused) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 25));
      }
    },
  };
  if (!elements.storyBeatKeepMoving.checked) {
    await writeStoryBeatAtStableScene(storyBeatController, sceneLease);
    return;
  }
  await writeStoryBeatAndContinueAtStableScene(storyBeatController, {
    ...sceneLease,
    pauseGeneration: () => pauseRequestGeneration,
    canResume: () => state.campaignId === leaseCampaignId
      && !document.hidden
      && !presentationSuspended
      && !presentationBusy
      && elements.stage.dataset.cutawayFallback === undefined
      && state.scene.mode !== "battle"
      && elements.stage.dataset.encounterEngine === undefined,
    resume: () => {
      if (paused) togglePaused();
    },
  });
}

async function requestStoryTrailCopy(): Promise<void> {
  const snapshot = storyBeatController.snapshot;
  if (
    storyTrailCopyInFlight
    || !snapshot.visible
    || snapshot.line !== null
    || snapshot.trail.length === 0
  ) return;

  storyTrailCopyInFlight = true;
  renderStoryBeatUi(snapshot);
  const outcome = await copyStoryBeatTrailPlainText(snapshot.trail, (text) => {
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== "function") {
      return Promise.reject(new Error("Clipboard unavailable"));
    }
    return clipboard.writeText(text);
  });
  storyTrailCopyInFlight = false;
  if (outcome === "empty" || !storyBeatController.reportTrailCopy(outcome)) {
    renderStoryBeatUi(storyBeatController.snapshot);
  }
}

elements.storyBeatWrite.addEventListener("click", () => {
  void requestStableStoryBeat();
});
elements.storyTrailCopy.addEventListener("click", () => {
  void requestStoryTrailCopy();
});


elements.miniMap.addEventListener("click", () => {
  setActiveView("map");
  viewButtons.find((button) => button.dataset.view === "map")?.focus();
});

elements.spectatorInboxClose.addEventListener("click", () => {
  spectatorRecapOpen = false;
  presentSpectatorInbox();
  focusWatchControl();
});

elements.elsewhereLoafOutcome.addEventListener("click", () => { renderer.showCutawayOutcome(); });

elements.trapCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.trapCutawayOutcome.disabled = true;
  elements.trapCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.farewellCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.farewellCutawayOutcome.disabled = true;
  elements.farewellCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.levelUpCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.levelUpCutawayOutcome.disabled = true;
  elements.levelUpCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.abilityResonanceCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.abilityResonanceCutawayOutcome.disabled = true;
  elements.abilityResonanceCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.weaponMemoryCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.weaponMemoryCutawayOutcome.disabled = true;
  elements.weaponMemoryCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.battleSpoilsCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.battleSpoilsCutawayOutcome.disabled = true;
  elements.battleSpoilsCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.townItineraryCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.townItineraryCutawayOutcome.disabled = true;
  elements.townItineraryCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.fieldNoteCutawayOutcome.addEventListener("click", () => {
  if (!renderer.showCutawayOutcome()) return;
  elements.fieldNoteCutawayOutcome.disabled = true;
  elements.fieldNoteCutawayOutcome.hidden = true;
  focusWatchControl();
});

elements.viewToolbar.addEventListener("keydown", (event) => {
  const currentIndex = viewButtons.findIndex((button) => button === document.activeElement);
  if (currentIndex < 0) return;
  let nextIndex: number | null = null;
  if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + viewButtons.length) % viewButtons.length;
  if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % viewButtons.length;
  if (event.key === "Home") nextIndex = 0;
  if (event.key === "End") nextIndex = viewButtons.length - 1;
  if (nextIndex === null) return;
  event.preventDefault();
  viewButtons[nextIndex]?.focus({ preventScroll: true });
  revealViewNavigation(true);
});

for (const button of document.querySelectorAll<HTMLButtonElement>("[data-close-view]")) {
  button.addEventListener("click", () => setActiveView("watch", true));
}

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || event.defaultPrevented) return;
  const target = event.target;
  if (target instanceof Element && target.closest("dialog, [role='dialog'], [role='menu']") !== null) return;
  if (stageChromeMode === "focus" && activeView === "watch") {
    setStageChromeMode("panels", true);
    focusWatchControl();
    return;
  }
  if (activeView === "watch") return;
  setActiveView("watch", true);
});

function setPauseButtonText(text: string): void {
  elements.pauseButton.textContent = text;
  elements.stagePauseButton.textContent = text;
}

function togglePaused(): void {
  const generation = ++pauseRequestGeneration;
  paused = !paused;
  syncReparteePresentation();
  if (paused && stepping) {
    setPauseButtonText("Pausing…");
    void (async () => {
      while (stepping) await new Promise<void>((resolve) => window.setTimeout(resolve, 25));
      if (!paused || generation !== pauseRequestGeneration) return;
      syncPresentationPaused();
      setPauseButtonText("Resume");
    })();
    return;
  }
  if (!paused) lastAdvanceAtMs = Date.now();
  syncPresentationPaused();
  setPauseButtonText(paused ? "Resume" : "Pause");
  requestNarrativeCheck();
  if (!paused && catchUpAfterPresentation && !presentationBusy) void resumeDeferredCatchUp();
}

elements.pauseButton.addEventListener("click", togglePaused);
elements.stagePauseButton.addEventListener("click", togglePaused);

elements.newButton.addEventListener("click", () => {
  closeGameMenu(false);
  playModeStartup.cancel();
  freshPlayChoice = true;
  startupHold = true;
  syncPresentationPaused();
  localNarratorController.disable();
  creativeStoryController.stop();
  cancelNarrativeIntermission();
  void runInteraction(async () => {
    cancelCutawayPresentation();
    state = createNewWorld();
    lastPresentedStory.syncCampaign(state.campaignId);
    clearFactualStoryBeatOpportunity();
    staticCutawayNarratorFingerprint = null;
    localNarratorController.setCampaign(state.campaignId);
    await simulation.reset(state);
    present();
    await persist();
    await refreshCampaigns();
    presentNarratorScene();
  }).catch(() => {
    elements.consequence.textContent = "The new adventure could not be fully saved · choose a play mode to continue";
  }).finally(() => {
    void playModeStartup.start(true);
  });
});

elements.campaignSelect.addEventListener("change", () => {
  closeGameMenu(false);
  cancelNarrativeIntermission();
  void runInteraction(async () => {
    cancelCutawayPresentation();
    const selected = await repository.load(elements.campaignSelect.value);
    if (selected === undefined) return;
    state = selected;
    lastPresentedStory.syncCampaign(state.campaignId);
    clearFactualStoryBeatOpportunity();
    staticCutawayNarratorFingerprint = null;
    localNarratorController.setCampaign(state.campaignId);
    await simulation.reset(state);
    const beforeCatchUp = state;
    state = await catchUp(state);
    retainFactualStoryBeatTransition(beforeCatchUp, state);
    lastAdvanceAtMs = Date.now();
    present();
    await persist();
    await refreshCampaigns();
    presentNarratorScene();
  });
});

document.addEventListener("visibilitychange", () => {
  presentationSuspended = document.hidden;
  if (document.hidden) cancelNarrativeIntermission();
  localNarratorController.setHidden(document.hidden);
  syncPresentationPaused();
  if (document.hidden) {
    localNarratorController.cancelInstall();
    void persist();
    return;
  }
  if (startupHold) {
    automaticUpdateMonitor?.notifyVisible();
    return;
  }
  if (presentationBusy) {
    catchUpAfterPresentation = true;
    automaticUpdateMonitor?.notifyVisible();
    return;
  }
  void runInteraction(async () => {
    const before = state;
    state = await catchUp(state);
    retainFactualStoryBeatTransition(before, state);
    present();
    await persist();
    presentNarratorScene();
  });
  automaticUpdateMonitor?.notifyVisible();
});

window.addEventListener("pagehide", () => {
  localStorage.setItem(checkpointKey(durableState.campaignId), String(Date.now()));
  presentationSuspended = true;
  playModeStartup.cancel();
  cancelNarrativeIntermission();
  creativeStoryController.stop();
  localNarratorController.cancelInstall();
  localNarratorController.setHidden(true);
  syncPresentationPaused();
});
window.addEventListener("unload", () => {
  inspectionChromeObserver.disconnect();
  cancelNarrativeIntermission();
  clearFactualStoryBeatOpportunity();
  storyBeatController.dispose();
  creativeStoryController.dispose();
  localNarratorController.dispose();
  renderer.dispose();
}, { once: true });
window.addEventListener("pageshow", (event) => {
  presentationSuspended = document.hidden;
  localNarratorController.setHidden(document.hidden);
  syncPresentationPaused();
  if (event.persisted) void playModeStartup.start(freshPlayChoice);
  startLoop();
  if (!document.hidden) presentNarratorScene();
});

await simulation.reset(state);
const beforeInitialCatchUp = state;
state = await catchUp(state);
retainFactualStoryBeatTransition(beforeInitialCatchUp, state);
localNarratorController.setCampaign(state.campaignId);
localNarratorController.disable();
setActiveView("watch");
syncPresentationPaused();
present();
await persist();
await refreshCampaigns();
presentNarratorScene();
startLoop();
startRuntimeWatchdog();
elements.app.dataset.presentationBusy = "false";
elements.app.dataset.runtimeStatus = "running";
document.documentElement.dataset.ready = "true";
void playModeStartup.start(freshPlayChoice);
startAutomaticUpdates();

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  const register = (): void => {
    void registerServiceWorker().catch(() => undefined);
  };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}
